import {
  useEffect,
  useMemo,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import { Link } from "react-router-dom";
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import {
  IconAlertTriangle,
  IconBillCheck,
  IconChart,
  IconClock,
  IconClose,
  IconEdit,
  IconInfoSolid,
  IconPrinter,
  IconReports,
  IconStore,
  IconTrash,
} from "../../components/layout/icons";
import { CkEditorField } from "../../components/ui/CkEditorField";
import {
  EmptyState,
  ErrorState,
  LoadingState,
} from "../../components/ui/Feedback";
import { ExcelExportButton } from "../../components/ui/ExcelExportButton";
import { FieldError } from "../../components/ui/FieldError";
import { Modal } from "../../components/ui/Modal";
import { ModuleHero } from "../../components/ui/ModuleHero";
import { PatientUhidAutocomplete } from "../../components/ui/PatientUhidAutocomplete";
import { SearchableSelect } from "../../components/ui/SearchableSelect";
import { SortableTh } from "../../components/ui/SortableTh";
import { TruncatedText } from "../../components/ui/TruncatedText";
import { ApiError } from "../../lib/api-client";
import { openAuthenticatedRoute } from "../../lib/auth";
import { confirmUpdate } from "../../lib/confirm-service";
import { formatDate } from "../../lib/datetime";
import { exportRowsToExcel, fetchAllPages } from "../../lib/excel-export";
import { usePatientGlobalSearch } from "../../lib/patient-uhid-search";
import {
  isPhoneFieldName,
  normalizePhoneInput,
  PHONE_INPUT_MAX_LENGTH,
  PHONE_INPUT_PATTERN,
  PHONE_INPUT_TITLE,
} from "../../lib/phone-input";
import {
  useTableSort,
  type SortAccessors,
  type TableSortControl,
} from "../../lib/table-sort";
import { useToast } from "../../lib/toast";
import { useSettingsLookup } from "../settings/settings.queries";
import { usePatientGlobalLookup } from "../patients/patient.queries";
import { fetchStoreRows, type StoreLookup } from "./store.api";
import {
  InventoryModuleProvider,
  useInventoryModule,
  type InventoryModule,
} from "./inventory-module";
import {
  useCreateStoreRecord,
  usePatchStoreRecord,
  useStoreDashboard,
  useStoreLookup,
  useStoreNextWorkOrderNumber,
  useStorePurchaseOrder,
  useStoreRequisition,
  useStoreRows,
} from "./store.queries";
import "./store.css";

const humanize = (key: string) =>
  key === "medicineName"
    ? "Medicine Name"
    : key === "medicineCategory"
      ? "Medicine Category"
      : key === "medicineComposition"
        ? "Medicine Composition"
        : key === "medicineUnit"
          ? "Unit"
          : key === "medicineSubUnit"
            ? "Sub Unit"
            : key === "medicinePrice"
              ? "Price / Unit"
              : key === "ledgerItemNumber"
                ? "#"
                : key === "ledgerItem"
                  ? "Ledger Item"
                  : key === "ledgerHead"
                    ? "Ledger Head"
                    : key === "tdsApplicable"
                      ? "TDS Applicable"
                      : key === "gstApplicable"
                        ? "GST Applicable"
                        : key === "sNo"
                          ? "S.No."
                          : key === "contactPerson"
                            ? "Contact Person"
                            : key === "contactMobile"
                              ? "Contact Mobile"
                              : key === "contactEmail"
                                ? "Contact Email"
                                : key === "gstin"
                                  ? "GSTIN"
                                  : key === "expense-ledger-head"
                                    ? "Expense Ledger Head"
                                    : key === "expense-ledger-item"
                                      ? "Expense Ledger Item"
                                      : key === "serialNumber"
                                        ? "Sl. No"
                                        : key === "workOrderNumber"
                                          ? "Workorder No"
                                          : key === "workOrderDate"
                                            ? "Start Date"
                                            : key === "expectedDate"
                                              ? "End Date"
                                              : key === "generatedBy"
                                                ? "Created By"
                                                : key === "billNumber"
                                                  ? "Bill No"
                                                  : key === "paymentDate"
                                                    ? "Payment Date"
                                                    : key === "receivedBy"
                                                      ? "Received By"
                                                      : key === "totalPayment"
                                                        ? "Paid"
                                                        : key === "dueAmount"
                                                          ? "Due"
                                                          : key ===
                                                              "approvalStatus"
                                                            ? "Approval"
                                                            : key ===
                                                                "paymentStatus"
                                                              ? "Payment"
                                                              : key ===
                                                                  "inspectionRequired"
                                                                ? "Inspection"
                                                                : key
                                                                    .replace(
                                                                      /([A-Z])/g,
                                                                      " $1",
                                                                    )
                                                                    .replace(
                                                                      /^./,
                                                                      (char) =>
                                                                        char.toUpperCase(),
                                                                    );

export type StoreSection =
  | "dashboard"
  | "masters"
  | "vendors"
  | "work-order-vendors"
  | "miscellaneous-vendors"
  | "items"
  | "requisitions"
  | "requisition-verify"
  | "requisition-approved"
  | "purchase-orders"
  | "purchase-order-verify"
  | "purchase-order-approved"
  | "purchases"
  | "issues"
  | "work-orders"
  | "work-order-verify"
  | "work-order-approved"
  | "work-order-bills"
  | "stock"
  | "returns";
export type StoreMasterKind =
  | "item-type"
  | "category"
  | "brand"
  | "unit"
  | "department"
  | "store"
  | "work-order-type"
  | "work-order-unit"
  | "expense-ledger-head"
  | "expense-ledger-item";

interface LineDraft {
  key: string;
  item: string;
  itemName: string;
  unit: string;
  unitName: string;
  batch: string;
  requestedQuantity: string;
  unitQuantity: string;
  subUnitQuantity: string;
  batchNumber: string;
  expiryDate: string;
  testQuantity: string;
  rate: string;
  mrp: string;
  discountType: "percentage" | "amount";
  discountPercent: string;
  discountAmount: string;
  taxPercent: string;
  cgstPercent: string;
  sgstPercent: string;
  igstPercent: string;
  remarks: string;
}

const labels: Record<
  StoreSection,
  { title: string; description: string; resource?: string }
> = {
  dashboard: {
    title: "Store Dashboard",
    description:
      "Stock, procurement, requisitions, issues, returns, and work-order activity.",
  },
  masters: {
    title: "Store Masters",
    description: "Maintain the supporting Store master records.",
    resource: "masters",
  },
  vendors: {
    title: "Vendors",
    description: "Supplier identity, tax, contact, and address details.",
    resource: "vendors",
  },
  "work-order-vendors": {
    title: "Work Order Vendors",
    description:
      "Maintain vendors used only for work orders and work-order billing.",
    resource: "vendors",
  },
  "miscellaneous-vendors": {
    title: "Miscellaneous Vendors",
    description:
      "Maintain service providers used only for miscellaneous bills.",
    resource: "vendors",
  },
  items: {
    title: "Items",
    description:
      "The complete item catalogue and unit relationship used by the Store.",
    resource: "items",
  },
  requisitions: {
    title: "Requisitions",
    description: "Department requests with one or more item quantities.",
    resource: "requisitions",
  },
  "requisition-verify": {
    title: "Verify Requisitions",
    description:
      "Review and verify submitted department requisitions before items can be issued.",
    resource: "requisitions",
  },
  "requisition-approved": {
    title: "Approved Requisitions",
    description:
      "Verified requisitions awaiting final approval and approved requisition history.",
    resource: "requisitions",
  },
  "purchase-orders": {
    title: "Purchase Orders",
    description: "Vendor purchase orders with unit and sub-unit quantities.",
    resource: "purchase-orders",
  },
  "purchase-order-verify": {
    title: "Verify Purchase Orders",
    description:
      "Review and verify pending purchase orders before they can be used for purchasing.",
    resource: "purchase-orders",
  },
  "purchase-order-approved": {
    title: "Approved Purchase Orders",
    description:
      "Verified purchase orders that are approved and available for purchasing.",
    resource: "purchase-orders",
  },
  purchases: {
    title: "Purchases",
    description:
      "GRN, invoice, lot, tax, discount, and received-stock details.",
    resource: "purchases",
  },
  issues: {
    title: "Item Issues",
    description: "Issue requisitioned items from specific stock batches.",
    resource: "issues",
  },
  "work-orders": {
    title: "Work Orders",
    description:
      "Department work requests with item quantities, priority, vendor, and completion status.",
    resource: "work-orders",
  },
  "work-order-verify": {
    title: "Verify Work Orders",
    description:
      "Review and verify submitted work orders before final approval.",
    resource: "work-orders",
  },
  "work-order-approved": {
    title: "Approved Work Orders",
    description:
      "Verified work orders awaiting approval and approved work-order history.",
    resource: "work-orders",
  },
  "work-order-bills": {
    title: "Work Order Bills",
    description:
      "Generate and track bills raised against approved work orders.",
    resource: "work-order-bills",
  },
  stock: {
    title: "Present Stock",
    description:
      "Live batch-level stock with supplier and expiry traceability.",
    resource: "stock",
  },
  returns: {
    title: "Store Returns",
    description: "Department and vendor return history.",
    resource: "returns",
  },
};

const masterTitles: Record<StoreMasterKind, string> = {
  "item-type": "Item Type",
  category: "Item Category",
  brand: "Item Company",
  unit: "Item Unit",
  department: "Store Department",
  store: "Store",
  "work-order-type": "Work Order Type",
  "work-order-unit": "Work Order Unit",
  "expense-ledger-head": "Expense Ledger Head",
  "expense-ledger-item": "Expense Ledger Item",
};
const storeItemMasterKinds: readonly StoreMasterKind[] = [
  "category",
  "unit",
  "item-type",
  "brand",
  "store",
  "department",
];
const isCompactWorkOrderMaster = (kind: StoreMasterKind) =>
  kind === "work-order-type" ||
  kind === "work-order-unit" ||
  kind === "expense-ledger-head";
const isPharmacyNameOnlyMaster = (kind: StoreMasterKind) =>
  kind === "unit" || kind === "category";
const today = () => new Date().toISOString().slice(0, 10);
let lineSequence = 0;
const blankLine = (): LineDraft => ({
  key: `store-line-${++lineSequence}`,
  item: "",
  itemName: "",
  unit: "",
  unitName: "",
  batch: "",
  requestedQuantity: "",
  unitQuantity: "0",
  subUnitQuantity: "0",
  batchNumber: "",
  expiryDate: "",
  testQuantity: "0",
  rate: "0",
  mrp: "0",
  discountType: "percentage",
  discountPercent: "0",
  discountAmount: "0",
  taxPercent: "0",
  cgstPercent: "0",
  sgstPercent: "0",
  igstPercent: "0",
  remarks: "",
});
const initialForm = (kind: StoreMasterKind = "category") => ({
  kind,
  name: "",
  code: "",
  parent: "",
  masterStatus: "active",
  tdsApplicable: "N",
  tdsNatureOfPayment: "",
  tdsSectionCode: "",
  tdsRate: "0",
  tdsThreshold: "0",
  gstApplicable: "N",
  gstChargeType: "",
  sacHsnCode: "",
  gstRate: "0",
  igstLedgerName: "",
  cgstLedgerName: "",
  sgstLedgerName: "",
  rcmOutputGstLedger: "",
  costCentreApplicable: "N",
  defaultCostCentre: "",
  openingBalance: "0",
  descriptionNotes: "",
  vendorName: "",
  gstin: "",
  phone: "",
  email: "",
  contactPersonName: "",
  address: "",
  pinCode: "",
  type: "",
  category: "",
  subCategory: "",
  brand: "",
  company: "",
  group: "",
  store: "",
  unit: "",
  subUnit: "",
  subUnitsPerUnit: "1",
  composition: "",
  unitRate: "0",
  taxPercent: "0",
  reorderLevel: "0",
  hsnSacNumber: "",
  department: "",
  requisitionFor: "department",
  patient: "",
  vendor: "",
  purchaseOrder: "",
  requisition: "",
  purchaseDate: today(),
  requisitionDate: today(),
  orderDate: today(),
  issueDate: today(),
  workOrderDate: today(),
  workOrderNumber: "",
  expectedDate: "",
  invoiceNumber: "",
  invoiceDateTime: "",
  purchaseType: "direct",
  grnStatus: "complete",
  inspectionRequired: "false",
  discountType: "amount",
  totalDiscountAmount: "0",
  paymentTerms: "",
  completionStatus: "complete",
  priority: "normal",
  subject: "",
  note: "",
});

const scalarText = (value: unknown): string =>
  typeof value === "string" ||
  typeof value === "number" ||
  typeof value === "boolean"
    ? String(value)
    : "-";
const requisitionDateFormatter = new Intl.DateTimeFormat("en-US", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  timeZone: "Asia/Kolkata",
});
const formatRequisitionDate = (value: unknown): string => {
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return "-";
  const parts = Object.fromEntries(
    requisitionDateFormatter
      .formatToParts(date)
      .map((part) => [part.type, part.value]),
  );
  return `${parts.day}-${parts.month}-${parts.year}`;
};
const requisitionTimeFormatter = new Intl.DateTimeFormat("en-US", {
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
  timeZone: "Asia/Kolkata",
});
const formatRequisitionDateTime = (value: unknown): string => {
  const date = formatRequisitionDate(value);
  return date === "-"
    ? date
    : `${date}, ${requisitionTimeFormatter.format(new Date(String(value)))}`;
};
const REQUISITION_STATUS_META: Record<string, { label: string; tone: string }> =
  {
    submitted: { label: "Pending", tone: "warning" },
    verified: { label: "Verified", tone: "verified" },
    approved: { label: "Approved", tone: "active" },
    "partially-issued": { label: "Partially Issued", tone: "info" },
    fulfilled: { label: "Fulfilled", tone: "active" },
    rejected: { label: "Rejected", tone: "critical" },
    cancelled: { label: "Cancelled", tone: "archived" },
  };
const RequisitionStatusBadge = ({ status }: { status: unknown }) => {
  const meta = REQUISITION_STATUS_META[String(status)] ?? {
    label: scalarText(status),
    tone: "",
  };
  return <span className={`status-badge ${meta.tone}`}>{meta.label}</span>;
};
const PURCHASE_ORDER_STATUS_META: Record<
  string,
  { label: string; tone: string }
> = {
  draft: { label: "Pending", tone: "warning" },
  verified: { label: "Verified", tone: "verified" },
  approved: { label: "Approved", tone: "active" },
  rejected: { label: "Rejected", tone: "critical" },
  cancelled: { label: "Cancelled", tone: "archived" },
  closed: { label: "Closed", tone: "info" },
};
const PurchaseOrderStatusBadge = ({ status }: { status: unknown }) => {
  const meta = PURCHASE_ORDER_STATUS_META[String(status)] ?? {
    label: scalarText(status),
    tone: "",
  };
  return <span className={`status-badge ${meta.tone}`}>{meta.label}</span>;
};
const WORK_ORDER_STATUS_META: Record<string, { label: string; tone: string }> =
  {
    submitted: { label: "Pending", tone: "warning" },
    verified: { label: "Verified", tone: "verified" },
    approved: { label: "Approved", tone: "active" },
    rejected: { label: "Rejected", tone: "critical" },
    open: { label: "Open", tone: "warning" },
    "in-progress": { label: "In Progress", tone: "info" },
    completed: { label: "Completed", tone: "active" },
    cancelled: { label: "Cancelled", tone: "archived" },
  };
const WorkOrderStatusBadge = ({ status }: { status: unknown }) => {
  const meta = WORK_ORDER_STATUS_META[String(status)] ?? {
    label: scalarText(status),
    tone: "",
  };
  return <span className={`status-badge ${meta.tone}`}>{meta.label}</span>;
};
type BadgeField = "purchaseType" | "grnStatus" | "completionStatus";
const PURCHASE_BADGE_META: Record<
  BadgeField,
  Record<string, { label: string; tone: string }>
> = {
  purchaseType: {
    direct: { label: "Direct", tone: "info" },
    indirect: { label: "Against PO", tone: "verified" },
  },
  grnStatus: {
    complete: { label: "Complete", tone: "active" },
    incomplete: { label: "Incomplete", tone: "warning" },
  },
  completionStatus: {
    complete: { label: "Complete", tone: "active" },
    incomplete: { label: "Incomplete", tone: "warning" },
  },
};
const PurchaseBadge = ({
  field,
  value,
}: {
  field: BadgeField;
  value: unknown;
}) => {
  if (value === null || value === undefined || value === "") return <>-</>;
  const meta = PURCHASE_BADGE_META[field][String(value)] ?? {
    label: scalarText(value),
    tone: "",
  };
  return (
    <span className={`status-badge store-badge-caps ${meta.tone}`}>
      {meta.label}
    </span>
  );
};
const text = (row: Record<string, unknown>, key: string): string => {
  if (key === "totalItems")
    return Array.isArray(row.lines) ? String(row.lines.length) : "-";
  const value = row[key];
  if (value === null || value === undefined) return "-";
  if (
    key === "requisitionDate" ||
    key === "orderDate" ||
    key === "purchaseDate" ||
    key === "issueDate" ||
    key === "workOrderDate" ||
    key === "expectedDate"
  )
    return formatRequisitionDate(value);
  if (key === "invoiceDateTime") return formatRequisitionDateTime(value);
  if (Array.isArray(value)) {
    const labels = value
      .map((entry) => text({ entry }, "entry"))
      .filter((label) => label !== "-");
    return labels.length ? labels.join(", ") : "-";
  }
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    const scalar = [
      record.name,
      record.departmentName,
      record.vendorName,
      record.purchaseOrderNumber,
      record.requisitionNumber,
      record.label,
      record.id,
    ].find((entry) => typeof entry === "string" || typeof entry === "number");
    return scalar !== undefined ? scalarText(scalar) : "-";
  }
  return scalarText(value);
};
const requisitionPatientName = (row: Record<string, unknown>): string => {
  if (typeof row.patientName === "string" && row.patientName.trim())
    return row.patientName.trim();
  if (!row.patient || typeof row.patient !== "object") return "-";
  const patient = row.patient as Record<string, unknown>;
  const name = [patient.firstName, patient.lastName]
    .filter(
      (part): part is string =>
        typeof part === "string" && Boolean(part.trim()),
    )
    .join(" ")
    .trim();
  return name || "-";
};
const requisitionDestination = (row: Record<string, unknown>): string =>
  row.requisitionFor === "patient"
    ? requisitionPatientName(row)
    : text(row, "department");
const number = (value: string) => Number(value || 0);
const storeErrorMessage = (reason: unknown, fallback: string): string => {
  if (!(reason instanceof ApiError)) return fallback;
  if (reason.code !== "VALIDATION_ERROR" || !reason.details)
    return reason.message;
  if (typeof reason.details !== "object") return reason.message;

  const details = reason.details as {
    fieldErrors?: Record<string, string[]>;
    formErrors?: string[];
  };
  const messages = [
    ...(details.formErrors ?? []),
    ...Object.entries(details.fieldErrors ?? {}).flatMap(([field, errors]) =>
      errors.map((message) => `${field}: ${message}`),
    ),
  ];
  return messages.length > 0
    ? `${reason.message} ${messages.join(" ")}`
    : reason.message;
};
const moneyFormatter = new Intl.NumberFormat("en-IN", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const formatMoney = (value: number) =>
  `₹${moneyFormatter.format(Number.isFinite(value) ? value : 0)}`;
const compactMoneyFormatter = new Intl.NumberFormat("en-IN", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});
const formatMoneyCompact = (value: unknown) =>
  value === null ||
  value === undefined ||
  value === "" ||
  !Number.isFinite(Number(value))
    ? "-"
    : `₹${compactMoneyFormatter.format(Number(value))}`;
const boolText = (value: unknown) =>
  value === true || String(value).toLowerCase() === "true" ? "Yes" : "No";
const moneyNumber = (value: unknown) =>
  Number.isFinite(Number(value)) ? Number(value) : 0;
const MONEY_KEYS = new Set(["total", "subTotal"]);
const cellText = (row: Record<string, unknown>, key: string): string => {
  const value = row[key];
  if (
    MONEY_KEYS.has(key) &&
    value !== null &&
    value !== undefined &&
    value !== "" &&
    Number.isFinite(Number(value))
  )
    return formatMoney(Number(value));
  if (key === "vendor") {
    const name = text(row, key);
    return name === "-" ? name : name.toUpperCase();
  }
  return text(row, key);
};
const purchaseOrderRequisitionText = (row: Record<string, unknown>): string => {
  if (Array.isArray(row.requisitions) && row.requisitions.length > 0)
    return text(row, "requisitions");
  if (Array.isArray(row.requisitionNumbers) && row.requisitionNumbers.length > 0)
    return text({ requisitions: row.requisitionNumbers }, "requisitions");
  return row.requisition
    ? text({ requisitions: [row.requisition] }, "requisitions")
    : "-";
};
const idOf = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    return idOf(record.id ?? record._id ?? record.value);
  }
  return String(value);
};
const lookupLabel = (value: unknown): string => {
  if (typeof value === "object" && value !== null) {
    const record = value as Record<string, unknown>;
    return String(record.name ?? record.label ?? record.departmentName ?? "");
  }
  return typeof value === "string" || typeof value === "number" ? String(value) : "";
};
const itemFor = (items: StoreLookup[] | undefined, id: string) =>
  items?.find((item) => item.id === id);
const usesSubUnitRemainder = (item: StoreLookup | undefined) => {
  const subUnitsPerUnit = Number(item?.subUnitsPerUnit ?? 1);
  return (
    Boolean(item) &&
    Number.isFinite(subUnitsPerUnit) &&
    subUnitsPerUnit > 1 &&
    item?.unit !== item?.subUnit &&
    item?.unitName !== item?.subUnitName
  );
};
const subUnitQuantityFor = (line: LineDraft, item: StoreLookup | undefined) =>
  usesSubUnitRemainder(item) ? number(line.subUnitQuantity) : 0;
const quantityFor = (line: LineDraft, item: StoreLookup | undefined) =>
  number(line.unitQuantity) * Number(item?.subUnitsPerUnit ?? 1) +
  subUnitQuantityFor(line, item);
const purchaseAmounts = (line: LineDraft, item: StoreLookup | undefined) => {
  const gross =
    (quantityFor(line, item) / Number(item?.subUnitsPerUnit ?? 1)) *
    number(line.rate);
  const discountAmount =
    number(line.discountPercent) > 0
      ? (gross * number(line.discountPercent)) / 100
      : number(line.discountAmount);
  const netAmount = gross - discountAmount;
  const cgstAmount = (netAmount * number(line.cgstPercent)) / 100;
  const sgstAmount = (netAmount * number(line.sgstPercent)) / 100;
  const igstAmount = (netAmount * number(line.igstPercent)) / 100;
  return {
    discountAmount,
    netAmount,
    cgstAmount,
    sgstAmount,
    igstAmount,
    total: netAmount + cgstAmount + sgstAmount + igstAmount,
  };
};
const requisitionLineAmount = (
  line: LineDraft,
  item: StoreLookup | undefined,
) =>
  (quantityFor(line, item) / Number(item?.subUnitsPerUnit ?? 1)) *
  number(line.rate);
const orderAmounts = (line: LineDraft, item: StoreLookup | undefined) => {
  const gross =
    (quantityFor(line, item) / Number(item?.subUnitsPerUnit ?? 1)) *
    number(line.rate);
  const discountAmount =
    line.discountType === "amount"
      ? Math.min(gross, number(line.discountAmount))
      : (gross * number(line.discountAmount)) / 100;
  const taxableAmount = gross - discountAmount;
  const taxAmount = (taxableAmount * number(line.taxPercent)) / 100;
  return {
    gross,
    discountAmount,
    taxableAmount,
    taxAmount,
    total: taxableAmount + taxAmount,
  };
};
const issueLineAmount = (line: LineDraft, item: StoreLookup | undefined) =>
  (quantityFor(line, item) / Number(item?.subUnitsPerUnit ?? 1)) *
  number(line.rate);
const quantityParts = (quantity: number, item: StoreLookup | undefined) => {
  const subUnitsPerUnit = Math.max(1, Number(item?.subUnitsPerUnit ?? 1));
  if (!Number.isFinite(quantity) || quantity <= 0)
    return { unitQuantity: "0", subUnitQuantity: "0" };
  return {
    unitQuantity: String(Math.floor(quantity / subUnitsPerUnit)),
    subUnitQuantity: String(
      usesSubUnitRemainder(item) ? quantity % subUnitsPerUnit : 0,
    ),
  };
};

const STORE_LINE_SECTIONS = [
  "requisitions",
  "requisition-verify",
  "requisition-approved",
  "purchase-orders",
  "purchases",
  "issues",
  "work-orders",
];
const validateStoreForm = (
  section: StoreSection,
  form: Form,
  lines: LineDraft[],
  isPharmacy = false,
) => {
  const errors: Record<string, string> = {};
  const isRequisitionSection =
    section === "requisitions" ||
    section === "requisition-verify" ||
    section === "requisition-approved";
  if (section === "masters") {
    if (!form.name.trim()) errors.name = "Name is required.";
    if (form.kind === "expense-ledger-item" && !form.parent)
      errors.parent = "Expense Ledger Head is required.";
  } else if (section === "items") {
    if (!form.name.trim()) errors.name = "Item Name is required.";
    if (!isPharmacy && !form.type) errors.type = "Item Type is required.";
    if (!form.category) errors.category = "Item Category is required.";
    if (!form.unit) errors.unit = "Item Unit is required.";
    if (!form.subUnit) errors.subUnit = "Item Sub Unit is required.";
    if (isPharmacy && !form.reorderLevel.trim())
      errors.reorderLevel = "Minimum level is required.";
  } else if (isRequisitionSection) {
    if (form.requisitionFor === "patient") {
      if (!form.patient) errors.patient = "Patient is required.";
    } else if (!form.department) errors.department = "Department is required.";
  } else if (section === "purchase-orders" || section === "purchases") {
    if (!form.vendor) errors.vendor = "Vendor is required.";
    if (section === "purchase-orders" && !form.requisition)
      errors.requisition = "Requisition No is required.";
    if (section === "purchases") {
      if (!form.invoiceNumber.trim())
        errors.invoiceNumber = "Invoice number is required.";
      if (form.purchaseType === "indirect" && !form.purchaseOrder)
        errors.purchaseOrder = "Purchase order is required.";
    }
  } else if (section === "issues") {
    if (isPharmacy && form.requisitionFor === "patient") {
      if (!form.patient) errors.patient = "Patient is required.";
    } else if (!form.department) errors.department = "Department is required.";
    if (!form.requisition) errors.requisition = "Requisition No is required.";
  } else if (
    section === "work-orders" ||
    section === "work-order-verify" ||
    section === "work-order-approved"
  ) {
    if (!form.department) errors.department = "Department is required.";
    if (!form.vendor) errors.vendor = "Vendor is required.";
  }
  if (
    STORE_LINE_SECTIONS.includes(section) ||
    section === "work-order-verify" ||
    section === "work-order-approved"
  ) {
    if (section !== "purchase-orders" || form.requisition)
      lines.forEach((line) => {
        if (!line.item) errors[`item-${line.key}`] = "Item is required.";
        if (
          isRequisitionSection &&
          number(line.requestedQuantity) <= 0 &&
          number(line.unitQuantity) <= 0 &&
          number(line.subUnitQuantity) <= 0
        )
          errors[`quantity-${line.key}`] =
            "Quantity must be greater than zero.";
        if (
          (section === "purchase-orders" ||
            section === "purchases" ||
            section === "issues") &&
          number(line.unitQuantity) <= 0 &&
          number(line.subUnitQuantity) <= 0
        )
          errors[`quantity-${line.key}`] =
            "Quantity must be greater than zero.";
        if (
          section === "purchases" &&
          line.expiryDate &&
          line.expiryDate <= form.purchaseDate
        )
          errors[`expiryDate-${line.key}`] =
            "Expiry date must be after purchase date.";
        if (
          (section === "work-orders" ||
            section === "work-order-verify" ||
            section === "work-order-approved") &&
          !line.remarks.trim()
        )
          errors[`description-${line.key}`] = "Description is required.";
        if (
          (section === "work-orders" ||
            section === "work-order-verify" ||
            section === "work-order-approved") &&
          !line.unit
        )
          errors[`unit-${line.key}`] = "Unit is required.";
      });
  }
  return errors;
};

type RequisitionTab =
  "requisitions" | "requisition-verify" | "requisition-approved";
const requisitionTabs: { tab: RequisitionTab; label: string }[] = [
  { tab: "requisitions", label: "Requisition" },
  { tab: "requisition-verify", label: "Verify" },
  { tab: "requisition-approved", label: "Approved" },
];

type PurchaseOrderTab =
  "purchase-orders" | "purchase-order-verify" | "purchase-order-approved";
const purchaseOrderTabs: { tab: PurchaseOrderTab; label: string }[] = [
  { tab: "purchase-orders", label: "Purchase Orders" },
  { tab: "purchase-order-verify", label: "Verify" },
  { tab: "purchase-order-approved", label: "Approved" },
];

type WorkOrderTab = "work-orders" | "work-order-verify" | "work-order-approved";
const workOrderTabs: { tab: WorkOrderTab; label: string }[] = [
  { tab: "work-orders", label: "Work Order" },
  { tab: "work-order-verify", label: "Verify" },
  { tab: "work-order-approved", label: "Approved" },
];

/** Column keys the backend list endpoints accept as sortBy, per resource. */
const STORE_SORTABLE_COLUMNS: Record<string, readonly string[]> = {
  masters: ["kind", "name", "code", "parent", "status", "createdAt"],
  vendors: [
    "vendorName",
    "shortName",
    "email",
    "phone",
    "gstin",
    "panNumber",
    "licenseNumber",
    "contactPersonName",
    "address",
    "pinCode",
    "paymentTermsDays",
    "creditLimit",
    "status",
    "createdAt",
  ],
  items: [
    "itemCode",
    "name",
    "shortCode",
    "type",
    "category",
    "subCategory",
    "brand",
    "store",
    "unit",
    "subUnit",
    "unitRate",
    "unitMrp",
    "taxPercent",
    "reorderLevel",
    "hsnSacNumber",
    "active",
    "createdAt",
  ],
  requisitions: [
    "requisitionNumber",
    "requisitionDate",
    "department",
    "status",
    "approvedAt",
    "createdAt",
  ],
  "purchase-orders": [
    "purchaseOrderNumber",
    "requisitions",
    "orderDate",
    "vendor",
    "total",
    "status",
    "approvedAt",
    "createdAt",
  ],
  purchases: [
    "purchaseNumber",
    "purchaseDate",
    "vendor",
    "invoiceNumber",
    "invoiceDateTime",
    "purchaseType",
    "grnStatus",
    "inspectionRequired",
    "subTotal",
    "total",
    "status",
    "createdAt",
  ],
  issues: [
    "issueNumber",
    "issueDate",
    "department",
    "completionStatus",
    "total",
    "createdAt",
  ],
  "work-orders": [
    "workOrderNumber",
    "workOrderDate",
    "expectedDate",
    "department",
    "vendor",
    "priority",
    "status",
    "subject",
    "total",
    "completedAt",
    "createdAt",
  ],
  stock: [
    "item",
    "itemCode",
    "vendor",
    "store",
    "batchNumber",
    "expiryDate",
    "receivedQuantity",
    "availableQuantity",
    "rate",
    "mrp",
    "taxPercent",
    "createdAt",
  ],
};

const StorePageContent = ({
  section,
  masterKind,
}: {
  section: StoreSection;
  masterKind?: StoreMasterKind;
}) => {
  const inventoryModule = useInventoryModule();
  const isPharmacy = inventoryModule === "pharmacy";
  const moduleBasePath = `/${inventoryModule}`;
  const [requisitionTab, setRequisitionTab] =
    useState<RequisitionTab>("requisitions");
  const [poTab, setPoTab] = useState<PurchaseOrderTab>("purchase-orders");
  const [workOrderTab, setWorkOrderTab] = useState<WorkOrderTab>("work-orders");
  const effectiveSection: StoreSection =
    section === "requisitions"
      ? requisitionTab
      : section === "purchase-orders"
        ? poTab
        : section === "work-orders"
          ? workOrderTab
          : section;
  const config = labels[effectiveSection];
  const baseTitle =
    effectiveSection === "masters" && masterKind
      ? masterTitles[masterKind]
      : config.title;
  const title =
    isPharmacy && effectiveSection === "vendors"
      ? "Medicine Vendors"
      : isPharmacy
        ? baseTitle.replace(/Store/g, "Pharmacy").replace(/Item/g, "Medicine")
        : baseTitle;
  const isPharmacyNameOnlyMasterPage = Boolean(
    isPharmacy && masterKind && isPharmacyNameOnlyMaster(masterKind),
  );
  const [form, setForm] = useState(() => initialForm(masterKind));
  const [patientSearch, setPatientSearch] = useState("");
  const [listSearch, setListSearch] = useState("");
  const [listPage, setListPage] = useState(1);
  const [listLimit, setListLimit] = useState(20);
  const [lines, setLines] = useState<LineDraft[]>([blankLine()]);
  const [workOrderAttachments, setWorkOrderAttachments] = useState<File[]>([]);
  const [isAttachmentDragging, setIsAttachmentDragging] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingRequisitionId, setEditingRequisitionId] = useState<
    string | null
  >(null);
  const [editingPurchaseOrderId, setEditingPurchaseOrderId] = useState<
    string | null
  >(null);
  const [editingPurchaseOrderRequisition, setEditingPurchaseOrderRequisition] =
    useState<StoreLookup | null>(null);
  const [editingWorkOrderId, setEditingWorkOrderId] = useState<string | null>(
    null,
  );
  const [editingMasterId, setEditingMasterId] = useState<string | null>(null);
  const [editingVendorId, setEditingVendorId] = useState<string | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [viewingRequisition, setViewingRequisition] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [viewingPurchaseOrder, setViewingPurchaseOrder] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [viewingWorkOrder, setViewingWorkOrder] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [viewingPurchase, setViewingPurchase] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [viewingIssue, setViewingIssue] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [viewingExpenseLedgerItem, setViewingExpenseLedgerItem] =
    useState<Record<string, unknown> | null>(null);
  const [payingPurchase, setPayingPurchase] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [rejectingRequisitionId, setRejectingRequisitionId] = useState<
    string | null
  >(null);
  const [rejectingPurchaseOrderId, setRejectingPurchaseOrderId] = useState<
    string | null
  >(null);
  const [rejectingWorkOrderId, setRejectingWorkOrderId] = useState<
    string | null
  >(null);
  const { showToast } = useToast();
  const [tableSortState, setTableSortState] = useState<{
    key: string | null;
    direction: "asc" | "desc";
  }>({ key: null, direction: "asc" });
  const tableSort = useTableSort(
    tableSortState.key,
    tableSortState.direction,
    (next) => {
      setTableSortState({ key: next.key, direction: next.direction });
      setListPage(1);
    },
  );
  const switchRequisitionTab = (tab: RequisitionTab) => {
    setRequisitionTab(tab);
    setShowForm(false);
    setTableSortState({ key: null, direction: "asc" });
  };
  const switchPoTab = (tab: PurchaseOrderTab) => {
    setPoTab(tab);
    setShowForm(false);
    setEditingPurchaseOrderId(null);
    setTableSortState({ key: null, direction: "asc" });
  };
  const switchWorkOrderTab = (tab: WorkOrderTab) => {
    setWorkOrderTab(tab);
    setShowForm(false);
    setEditingWorkOrderId(null);
    setWorkOrderAttachments([]);
    setIsAttachmentDragging(false);
    setTableSortState({ key: null, direction: "asc" });
  };
  const rowParams = masterKind
    ? { kind: masterKind }
    : effectiveSection === "vendors"
      ? { vendorScope: "store" }
      : effectiveSection === "work-order-vendors"
        ? { vendorScope: "work-order" }
        : effectiveSection === "miscellaneous-vendors"
          ? { vendorScope: "miscellaneous" }
          : effectiveSection === "requisition-verify"
            ? { requisitionStatus: "submitted,verified" }
            : effectiveSection === "requisition-approved"
              ? { requisitionStatus: "verified,approved" }
              : effectiveSection === "purchase-order-verify"
                ? { purchaseOrderStatus: "draft,verified" }
                : effectiveSection === "purchase-order-approved"
                  ? { purchaseOrderStatus: "verified,approved" }
                  : effectiveSection === "work-order-verify"
                    ? { workOrderStatus: "submitted,verified" }
                    : effectiveSection === "work-order-approved"
                      ? { workOrderStatus: "verified,approved" }
                      : {};
  const hasStoreItemListControls =
    !isPharmacy &&
    (section === "items" ||
      (section === "masters" &&
        Boolean(masterKind && storeItemMasterKinds.includes(masterKind))));
  const listParams: Record<string, unknown> = {
    ...rowParams,
    ...(tableSortState.key
      ? {
          sortBy: tableSortState.key,
          sortOrder: tableSortState.direction,
        }
      : section === "items" && !isPharmacy
        ? { sortBy: "createdAt", sortOrder: "desc" }
        : {}),
    ...(hasStoreItemListControls
      ? {
          page: listPage,
          limit: listLimit,
          ...(listSearch.trim() ? { search: listSearch.trim() } : {}),
        }
      : {}),
  };
  const rows = useStoreRows(
    config.resource ?? "",
    Boolean(config.resource),
    listParams,
  );
  const dashboard = useStoreDashboard(section === "dashboard");
  const items = useStoreLookup("items");
  const selectableRequisitionItems = useMemo(
    () =>
      items.data?.filter(
        (item) => item.active === true && item.status !== "inactive",
      ),
    [items.data],
  );
  const vendors = useStoreLookup("vendors", { vendorScope: "store" });
  const workOrderVendors = useStoreLookup("vendors", {
    vendorScope: "work-order",
  });
  const storeDepartments = useStoreLookup(
    "masters",
    { kind: "department" },
    !isPharmacy,
  );
  const settingsDepartments = useSettingsLookup("departments", isPharmacy);
  const debouncedPatientSearch = usePatientGlobalSearch(patientSearch);
  const patientLookup = usePatientGlobalLookup(debouncedPatientSearch);
  const departments: StoreLookup[] | undefined = isPharmacy
    ? settingsDepartments.data?.map((department) => ({
        id: department.id,
        label: String(
          department.label ?? department.departmentName ?? department.id,
        ),
      }))
    : storeDepartments.data;
  const stores = useStoreLookup("masters", { kind: "store" });
  const workOrderTypes = useStoreLookup("masters", { kind: "work-order-type" });
  const workOrderUnits = useStoreLookup("masters", { kind: "work-order-unit" });
  const expenseLedgerHeads = useStoreLookup("masters", {
    kind: "expense-ledger-head",
  });
  const nextWorkOrderNumber = useStoreNextWorkOrderNumber(
    effectiveSection === "work-orders" && showForm && !editingWorkOrderId,
  );
  const itemTypes = useStoreLookup("masters", { kind: "item-type" });
  const categories = useStoreLookup("masters", { kind: "category" });
  const brands = useStoreLookup("masters", { kind: "brand" });
  const units = useStoreLookup("masters", { kind: "unit" });
  const requisitions = useStoreLookup(
    "requisitions",
    {
      ...(section === "issues" && isPharmacy ? {} : { department: form.department }),
      ...(section === "purchase-orders"
        ? { availableForPurchaseOrder: true }
        : {}),
    },
    section === "issues" || section === "purchase-orders",
  );
  const purchaseOrders = useStoreLookup(
    "purchase-orders",
    form.vendor ? { vendor: form.vendor } : {},
    section === "purchases" && form.purchaseType === "indirect",
  );
  const selectedPurchaseOrder = useStorePurchaseOrder(
    section === "purchases" && form.purchaseType === "indirect"
      ? form.purchaseOrder
      : section === "purchase-orders" && showForm && editingPurchaseOrderId
        ? editingPurchaseOrderId
        : "",
  );
  const selectedPurchaseOrderRequisition = useStoreRequisition(
    section === "purchase-orders" && showForm ? form.requisition : "",
  );
  const selectedIssueRequisition = useStoreRequisition(
    section === "issues" && showForm ? form.requisition : "",
  );
  useEffect(() => {
    if (section !== "issues" || !isPharmacy || !form.requisition || !selectedIssueRequisition.data) return;
    const row = selectedIssueRequisition.data;
    const requisitionFor = row.requisitionFor === "patient" ? "patient" : "department";
    const patient = idOf(row.patient);
    const department = idOf(row.department);
    setForm((current) => {
      const next = {
        ...current,
        requisitionFor,
        patient: requisitionFor === "patient" ? patient || current.patient : "",
        department: requisitionFor === "department" ? department || current.department : "",
      };
      return next.requisitionFor === current.requisitionFor && next.patient === current.patient && next.department === current.department
        ? current
        : next;
    });
    if (requisitionFor === "patient" && (row.patientUhid || row.patientName)) {
      setPatientSearch(String(row.patientUhid ?? row.patientName));
    }
  }, [form.requisition, isPharmacy, section, selectedIssueRequisition.data]);
  const requisitionOptions = useMemo(() => {
    const options = [...(requisitions.data ?? [])];
    const selectedPurchaseOrderRequisitionRow = section === "purchase-orders"
      ? selectedPurchaseOrderRequisition.data ??
        (Array.isArray(selectedPurchaseOrder.data?.requisitions)
          ? (selectedPurchaseOrder.data.requisitions as Record<string, unknown>[])[0]
          : undefined)
      : undefined;
    const selectedPurchaseOrderRequisitionNumber =
      typeof selectedPurchaseOrderRequisitionRow?.requisitionNumber === "string"
        ? selectedPurchaseOrderRequisitionRow.requisitionNumber
        : undefined;
    if (editingPurchaseOrderRequisition) {
      const editingOption = selectedPurchaseOrderRequisitionNumber
        ? { ...editingPurchaseOrderRequisition, label: selectedPurchaseOrderRequisitionNumber }
        : editingPurchaseOrderRequisition;
      const existingIndex = options.findIndex((option) => option.id === editingOption.id);
      const existingOption = existingIndex >= 0 ? options[existingIndex] : undefined;
      if (existingOption) {
        options[existingIndex] = selectedPurchaseOrderRequisitionNumber
          ? { ...existingOption, label: selectedPurchaseOrderRequisitionNumber }
          : existingOption;
      } else options.unshift(editingOption);
    }
    if (
      !form.requisition ||
      options.some((option) => option.id === form.requisition)
    )
      return options;
    const row = section === "purchase-orders" ? selectedPurchaseOrderRequisitionRow : selectedIssueRequisition.data;
    const label =
      typeof row?.requisitionNumber === "string"
        ? row.requisitionNumber
        : form.requisition;
    return [{ id: form.requisition, label }, ...options];
  }, [
    editingPurchaseOrderRequisition,
    form.requisition,
    requisitions.data,
    section,
    selectedIssueRequisition.data,
    selectedPurchaseOrder.data,
    selectedPurchaseOrderRequisition.data,
  ]);
  const issueDestination = useMemo(() => {
    if (!form.requisition) return "";
    const row = selectedIssueRequisition.data;
    const option = requisitionOptions.find((entry) => entry.id === form.requisition);
    const requisitionFor = row?.requisitionFor ?? option?.requisitionFor ?? form.requisitionFor;
    if (requisitionFor === "patient") {
      return String(row?.patientName || option?.patientName || lookupLabel(row?.patient) || lookupLabel(option?.patient) || "Patient selected");
    }
    const departmentId = idOf(row?.department) || idOf(option?.department) || form.department;
    const department = departments?.find((entry) => entry.id === departmentId);
    return String(row?.departmentName || option?.departmentName || department?.label || lookupLabel(row?.department) || lookupLabel(option?.department) || "Department selected");
  }, [departments, form.department, form.requisition, form.requisitionFor, requisitionOptions, selectedIssueRequisition.data]);
  const mutation = useCreateStoreRecord();
  const statusMutation = usePatchStoreRecord();
  const [showErrors, setShowErrors] = useState(false);
  const errors = showErrors
    ? validateStoreForm(effectiveSection, form, lines, isPharmacy)
    : {};

  useEffect(() => {
    setForm(initialForm(masterKind));
    setPatientSearch("");
    setListSearch("");
    setListPage(1);
    setListLimit(20);
    setLines([blankLine()]);
    setWorkOrderAttachments([]);
    setIsAttachmentDragging(false);
    setShowForm(false);
    setEditingRequisitionId(null);
    setEditingPurchaseOrderId(null);
    setEditingPurchaseOrderRequisition(null);
    setEditingWorkOrderId(null);
    setEditingMasterId(null);
    setEditingVendorId(null);
    setEditingItemId(null);
    setViewingRequisition(null);
    setViewingPurchaseOrder(null);
    setViewingWorkOrder(null);
    setViewingPurchase(null);
    setViewingIssue(null);
    setViewingExpenseLedgerItem(null);
    setPayingPurchase(null);
    setRejectingRequisitionId(null);
    setRejectingPurchaseOrderId(null);
    setRejectingWorkOrderId(null);
    setTableSortState({ key: null, direction: "asc" });
  }, [section, masterKind]);

  useEffect(() => {
    if (!hasStoreItemListControls || !rows.data) return;
    const totalPages = Math.max(rows.data.pagination.totalPages, 1);
    if (listPage > totalPages) setListPage(totalPages);
  }, [hasStoreItemListControls, listPage, rows.data]);

  const firstStoreId = stores.data?.[0]?.id;
  useEffect(() => {
    if (section !== "purchases" || !showForm || !firstStoreId || isPharmacy)
      return;
    setForm((current) =>
      current.store ? current : { ...current, store: firstStoreId },
    );
  }, [section, showForm, firstStoreId, isPharmacy]);

  useEffect(() => {
    if (
      section !== "purchases" ||
      form.purchaseType !== "indirect" ||
      !selectedPurchaseOrder.data
    )
      return;
    const row = selectedPurchaseOrder.data;
    const rowLines = Array.isArray(row.lines)
      ? (row.lines as Record<string, unknown>[])
      : [];
    if (rowLines.length === 0) return;
    setForm((current) => ({
      ...current,
      store: idOf(row.store) || current.store,
    }));
    setLines(
      rowLines.map((line) => {
        const itemId = idOf(line.item);
        const item = itemFor(items.data, itemId);
        const taxPercent = Number(line.taxPercent ?? item?.taxPercent ?? 0);
        return {
          ...blankLine(),
          item: itemId,
          itemName: typeof line.itemName === "string" ? line.itemName : "",
          unitQuantity: String(line.unitQuantity ?? 0),
          subUnitQuantity: String(line.subUnitQuantity ?? 0),
          rate: String(line.rate ?? item?.unitRate ?? 0),
          mrp: String(item?.unitMrp ?? line.rate ?? 0),
          discountPercent: String(line.discountPercent ?? 0),
          discountAmount: "0",
          cgstPercent: String(taxPercent),
          sgstPercent: "0",
          igstPercent: "0",
        };
      }),
    );
  }, [form.purchaseType, items.data, section, selectedPurchaseOrder.data]);

  useEffect(() => {
    if (
      section !== "purchase-orders" ||
      !showForm ||
      !editingPurchaseOrderId ||
      !selectedPurchaseOrder.data
    )
      return;
    const requisitions = Array.isArray(selectedPurchaseOrder.data.requisitions)
      ? (selectedPurchaseOrder.data.requisitions as unknown[])
      : [];
    const requisitionId = idOf(requisitions[0]);
    if (!requisitionId) return;
    setForm((current) =>
      current.requisition === requisitionId
        ? current
        : { ...current, requisition: requisitionId },
    );
  }, [editingPurchaseOrderId, section, selectedPurchaseOrder.data, showForm]);

  useEffect(() => {
    if (
      section !== "purchase-orders" ||
      !showForm ||
      editingPurchaseOrderId ||
      !selectedPurchaseOrderRequisition.data
    )
      return;
    const rowLines = Array.isArray(selectedPurchaseOrderRequisition.data.lines)
      ? (selectedPurchaseOrderRequisition.data.lines as Record<
          string,
          unknown
        >[])
      : [];
    const orderLines = rowLines
      .map((line) => {
        const itemId = idOf(line.item);
        const item = itemFor(items.data, itemId);
        const quantity =
          Number(line.requestedQuantity ?? 0) -
          Number(line.issuedQuantity ?? 0) -
          Number(line.rejectedQuantity ?? 0);
        if (!itemId || !item || quantity <= 0) return null;
        const parts = quantityParts(quantity, item);
        return {
          ...blankLine(),
          item: itemId,
          itemName:
            typeof line.itemName === "string"
              ? line.itemName
              : (item?.label ?? ""),
          unitQuantity: parts.unitQuantity,
          subUnitQuantity: parts.subUnitQuantity,
          rate: String(line.rate ?? item?.unitRate ?? 0),
          taxPercent: String(item?.taxPercent ?? 0),
        };
      })
      .filter((line): line is NonNullable<typeof line> => line !== null);
    setLines(orderLines.length > 0 ? orderLines : [blankLine()]);
  }, [
    editingPurchaseOrderId,
    items.data,
    section,
    selectedPurchaseOrderRequisition.data,
    showForm,
  ]);

  useEffect(() => {
    if (section !== "issues" || !showForm || !selectedIssueRequisition.data)
      return;
    const rowLines = Array.isArray(selectedIssueRequisition.data.lines)
      ? (selectedIssueRequisition.data.lines as Record<string, unknown>[])
      : [];
    const issueLines = rowLines
      .map((line) => {
        const itemId = idOf(line.item);
        const item = itemFor(items.data, itemId);
        const pendingQuantity =
          Number(line.requestedQuantity ?? 0) -
          Number(line.issuedQuantity ?? 0) -
          Number(line.rejectedQuantity ?? 0);
        if (!itemId || pendingQuantity <= 0) return null;
        const parts = quantityParts(pendingQuantity, item);
        return {
          ...blankLine(),
          item: itemId,
          itemName:
            typeof line.itemName === "string"
              ? line.itemName
              : (item?.label ?? ""),
          unitQuantity: parts.unitQuantity,
          subUnitQuantity: parts.subUnitQuantity,
          rate: String(line.rate ?? item?.unitRate ?? 0),
        };
      })
      .filter((line): line is LineDraft => Boolean(line));
    setLines(issueLines.length > 0 ? issueLines : [blankLine()]);
  }, [items.data, section, selectedIssueRequisition.data, showForm]);

  useEffect(() => {
    if (
      section !== "work-orders" ||
      !showForm ||
      !editingWorkOrderId ||
      (!workOrderTypes.data && !workOrderUnits.data)
    )
      return;
    setLines((current) => {
      let changed = false;
      const next = current.map((line) => {
        const type =
          itemFor(workOrderTypes.data, line.item) ??
          workOrderTypes.data?.find(
            (entry) =>
              entry.label.trim().toLowerCase() ===
              line.itemName.trim().toLowerCase(),
          );
        const typeUnitId = idOf(type?.unit);
        const typeUnitName = type?.unitName ?? "";
        const unitName =
          line.unitName || lookupLabel(line.unit) || typeUnitName;
        const selected =
          workOrderUnits.data?.find((unit) => unit.id === line.unit) ??
          workOrderUnits.data?.find(
            (unit) =>
              unit.id === typeUnitId ||
              unit.label.trim().toLowerCase() === unitName.trim().toLowerCase(),
          );
        const unit = selected?.id || line.unit || typeUnitId || unitName;
        const selectedUnitName = selected?.label || unitName;
        if (line.unit === unit && line.unitName === selectedUnitName) return line;
        changed = true;
        return { ...line, unit, unitName: selectedUnitName };
      });
      return changed ? next : current;
    });
  }, [
    editingWorkOrderId,
    section,
    showForm,
    workOrderTypes.data,
    workOrderUnits.data,
  ]);

  const columnKeys = useMemo(() => {
    if (section === "masters" && masterKind === "expense-ledger-item")
      return [
        "ledgerItemNumber",
        "ledgerItem",
        "ledgerHead",
        "tdsApplicable",
        "gstApplicable",
        "openingBalance",
      ];
    if (section === "work-order-vendors" || section === "miscellaneous-vendors")
      return [
        "sNo",
        "vendorName",
        "contactPerson",
        "contactMobile",
        "contactEmail",
        "gstin",
        "status",
        ...(section === "work-order-vendors" ? ["address"] : []),
      ];
    if (
      section === "masters" &&
      masterKind &&
      (isCompactWorkOrderMaster(masterKind) || isPharmacyNameOnlyMasterPage)
    )
      return masterKind === "expense-ledger-head"
        ? ["sNo", "ledgerHead"]
        : ["sNo", "name"];
    if (section === "items" && isPharmacy)
      return [
        "serialNumber",
        "medicineName",
        "medicineCategory",
        "medicineComposition",
        "medicineUnit",
        "medicineSubUnit",
        "medicinePrice",
        "status",
      ];
    if (section === "work-orders")
      return [
        "serialNumber",
        "workOrderNumber",
        "department",
        "workOrderDate",
        "expectedDate",
        "generatedBy",
        "status",
      ];
    if (section === "requisitions" && isPharmacy)
      return [
        "requisitionNumber",
        "requisitionDate",
        "department",
        "totalItems",
        "status",
      ];
    if (section === "vendors")
      return [
        "vendorName",
        "email",
        "phone",
        "gstin",
        "panNumber",
        "licenseNumber",
        "contactPersonName",
        "status",
      ];
    const first = rows.data?.data[0];
    if (!first) return [];
    const excluded = [
      "_id",
      "createdAt",
      "updatedAt",
      "lines",
      "generatedBy",
      "createdBy",
      "updatedBy",
      "approvedBy",
      "normalizedName",
      "scope",
    ];
    if (
      section === "requisitions" ||
      section === "purchase-orders" ||
      section === "purchases"
    )
      excluded.push("store");
    if (section === "masters") excluded.push("id");
    if (section === "requisitions")
      excluded.push(
        "note",
        "approvedAt",
        "rejectionNote",
        "rejectedBy",
        "rejectedAt",
        "patient",
        "patientName",
        "patientUhid",
        "id",
      );
    if (section === "purchase-orders")
      excluded.push(
        "note",
        "id",
        "approvedAt",
        "rejectionNote",
        "rejectedBy",
        "rejectedAt",
        "requisition",
        "requisitionNumbers",
      );
    if (section === "issues") excluded.push("note", "id");
    if (section === "purchases")
      excluded.push("discountType", "note", "paymentTerms");
    let keys = Object.keys(first).filter((key) => !excluded.includes(key));
    if (section === "purchases") {
      keys = keys.filter((key) => key !== "purchaseOrder");
      const dateIndex = keys.indexOf("purchaseDate");
      keys.splice(dateIndex === -1 ? 0 : dateIndex + 1, 0, "purchaseOrder");
    }
    if (section === "purchase-orders") {
      keys = keys.filter((key) => key !== "requisitions");
      const numberIndex = keys.indexOf("purchaseOrderNumber");
      keys.splice(numberIndex === -1 ? 0 : numberIndex + 1, 0, "requisitions");
    }
    keys = keys.slice(0, 9);
    if (section === "requisitions") {
      const statusIndex = keys.indexOf("status");
      keys.splice(
        statusIndex === -1 ? keys.length : statusIndex,
        0,
        "totalItems",
      );
    }
    return keys;
  }, [
    isPharmacy,
    isPharmacyNameOnlyMasterPage,
    masterKind,
    rows.data,
    section,
  ]);
  const totals = useMemo(() => {
    const summary = lines.reduce(
      (current, line) => {
        const amount = purchaseAmounts(line, itemFor(items.data, line.item));
        current.subTotal += amount.netAmount;
        current.lineDiscount += amount.discountAmount;
        current.cgst += amount.cgstAmount;
        current.sgst += amount.sgstAmount;
        current.igst += amount.igstAmount;
        current.beforeOverallDiscount += amount.total;
        return current;
      },
      {
        subTotal: 0,
        lineDiscount: 0,
        cgst: 0,
        sgst: 0,
        igst: 0,
        beforeOverallDiscount: 0,
      },
    );
    const overallDiscount =
      form.discountType === "percentage"
        ? (summary.beforeOverallDiscount * number(form.totalDiscountAmount)) /
          100
        : number(form.totalDiscountAmount);
    return {
      ...summary,
      overallDiscount,
      total: Math.max(0, summary.beforeOverallDiscount - overallDiscount),
    };
  }, [form.discountType, form.totalDiscountAmount, items.data, lines]);
  const requisitionTotal = useMemo(
    () =>
      lines.reduce(
        (sum, line) =>
          sum + requisitionLineAmount(line, itemFor(items.data, line.item)),
        0,
      ),
    [lines, items.data],
  );
  const orderTotals = useMemo(
    () =>
      lines.reduce(
        (acc, line) => {
          const amount = orderAmounts(line, itemFor(items.data, line.item));
          acc.subTotal += amount.gross;
          acc.discount += amount.discountAmount;
          acc.tax += amount.taxAmount;
          acc.total += amount.total;
          return acc;
        },
        { subTotal: 0, discount: 0, tax: 0, total: 0 },
      ),
    [lines, items.data],
  );
  const issueTotal = useMemo(
    () =>
      lines.reduce(
        (sum, line) =>
          sum + issueLineAmount(line, itemFor(items.data, line.item)),
        0,
      ),
    [lines, items.data],
  );
  const workOrderTotal = useMemo(
    () =>
      lines.reduce(
        (sum, line) =>
          sum +
          requisitionLineAmount(line, itemFor(workOrderTypes.data, line.item)),
        0,
      ),
    [lines, workOrderTypes.data],
  );
  const inspectionRequired = form.inspectionRequired === "true";
  const update =
    (key: keyof ReturnType<typeof initialForm>) =>
    (
      event: React.ChangeEvent<
        HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
      >,
    ) =>
      setForm((current) => ({
        ...current,
        [key]: isPhoneFieldName(key)
          ? normalizePhoneInput(event.target.value)
          : event.target.value,
      }));
  const updateLine = (key: string, values: Partial<LineDraft>) =>
    setLines((current) =>
      current.map((line) =>
        line.key === key
          ? {
              ...line,
              ...values,
              ...(Object.hasOwn(values, "item") ||
              Object.hasOwn(values, "unitQuantity") ||
              Object.hasOwn(values, "subUnitQuantity")
                ? { requestedQuantity: "" }
                : {}),
            }
          : line,
      ),
    );
  const removeLine = (key: string) =>
    setLines((current) =>
      current.length === 1
        ? current
        : current.filter((line) => line.key !== key),
    );
  const selectIssueRequisition = (requisition: string) => {
    const selected = requisitionOptions.find(
      (option) => option.id === requisition,
    );
    setForm((current) => ({
      ...current,
      requisition,
      requisitionFor: selected?.requisitionFor ?? current.requisitionFor,
      department: selected?.department ?? current.department,
      patient: selected?.patient ?? current.patient,
    }));
    if (selected?.patientUhid || selected?.patientName) setPatientSearch(selected.patientUhid ?? selected.patientName ?? "");
  };
  const selectPurchaseOrderRequisition = (requisition: string) => {
    setForm((current) => ({ ...current, requisition }));
    setLines([blankLine()]);
  };
  const updateInspectionRequired = (value: string) => {
    setForm((current) => ({ ...current, inspectionRequired: value }));
    if (value !== "true")
      setLines((current) =>
        current.map((line) => ({ ...line, testQuantity: "0" })),
      );
  };
  const closeForm = () => {
    setForm(initialForm(masterKind));
    setPatientSearch("");
    setLines([blankLine()]);
    setWorkOrderAttachments([]);
    setIsAttachmentDragging(false);
    setShowForm(false);
    setShowErrors(false);
    setEditingRequisitionId(null);
    setEditingPurchaseOrderId(null);
    setEditingPurchaseOrderRequisition(null);
    setEditingWorkOrderId(null);
    setEditingMasterId(null);
    setEditingVendorId(null);
    setEditingItemId(null);
  };
  const addWorkOrderAttachments = (files: FileList | File[]) => {
    const incoming = Array.from(files);
    if (!incoming.length) return;
    setWorkOrderAttachments((current) => {
      const next = [...current];
      incoming.forEach((file) => {
        if (
          !next.some(
            (existing) =>
              existing.name === file.name &&
              existing.size === file.size &&
              existing.lastModified === file.lastModified,
          )
        )
          next.push(file);
      });
      return next;
    });
  };
  const openWorkOrderVendorEditor = (row: Record<string, unknown>) => {
    setForm((current) => ({
      ...current,
      vendorName:
        typeof row.vendorName === "string"
          ? isPharmacy && section === "vendors"
            ? row.vendorName.toUpperCase()
            : row.vendorName
          : "",
      contactPersonName:
        typeof row.contactPersonName === "string" ? row.contactPersonName : "",
      phone: typeof row.phone === "string" ? row.phone : "",
      email: typeof row.email === "string" ? row.email : "",
      gstin: typeof row.gstin === "string" ? row.gstin : "",
      address: typeof row.address === "string" ? row.address : "",
      pinCode: typeof row.pinCode === "string" ? row.pinCode : "",
      masterStatus: row.status === "inactive" ? "inactive" : "active",
    }));
    setEditingVendorId(String(row.id));
    setShowErrors(false);
    setShowForm(true);
  };
  const openCompactMasterEditor = (row: Record<string, unknown>) => {
    setForm((current) => ({
      ...current,
      name: typeof row.name === "string" ? row.name : "",
    }));
    setEditingMasterId(String(row.id));
    setShowErrors(false);
    setShowForm(true);
  };
  const openExpenseLedgerItemEditor = (row: Record<string, unknown>) => {
    const stringValue = (key: string, fallback: string) => {
      const value = row[key];
      return typeof value === "string" || typeof value === "number"
        ? String(value)
        : fallback;
    };
    setForm((current) => ({
      ...current,
      name: typeof row.name === "string" ? row.name : "",
      parent: idOf(row.parent),
      tdsApplicable: stringValue("tdsApplicable", "N"),
      tdsNatureOfPayment: stringValue("tdsNatureOfPayment", ""),
      tdsSectionCode: stringValue("tdsSectionCode", ""),
      tdsRate: stringValue("tdsRate", "0"),
      tdsThreshold: stringValue("tdsThreshold", "0"),
      gstApplicable: stringValue("gstApplicable", "N"),
      gstChargeType: stringValue("gstChargeType", ""),
      sacHsnCode: stringValue("sacHsnCode", ""),
      gstRate: stringValue("gstRate", "0"),
      igstLedgerName: stringValue("igstLedgerName", ""),
      cgstLedgerName: stringValue("cgstLedgerName", ""),
      sgstLedgerName: stringValue("sgstLedgerName", ""),
      rcmOutputGstLedger: stringValue("rcmOutputGstLedger", ""),
      costCentreApplicable: stringValue("costCentreApplicable", "N"),
      defaultCostCentre: stringValue("defaultCostCentre", ""),
      openingBalance: stringValue("openingBalance", "0"),
      descriptionNotes: stringValue("descriptionNotes", ""),
    }));
    setEditingMasterId(String(row.id));
    setShowErrors(false);
    setShowForm(true);
  };
  const toggleCompactMaster = async (row: Record<string, unknown>) => {
    const active = row.status === "active";
    const masterLabel = masterKind ? masterTitles[masterKind] : "Store master";
    try {
      await statusMutation.mutateAsync({
        resource: "masters",
        id: String(row.id),
        payload: { status: active ? "inactive" : "active" },
      });
      showToast(
        `${masterLabel} ${active ? "deactivated" : "activated"} successfully.`,
        "success",
      );
    } catch (reason: unknown) {
      showToast(
        reason instanceof ApiError
          ? reason.message
          : `The ${masterLabel} status could not be updated.`,
        "error",
      );
    }
  };
  const editItem = (row: Record<string, unknown>) => {
    setForm((current) => ({
      ...current,
      type: idOf(row.type),
      category: idOf(row.category),
      subCategory: idOf(row.subCategory),
      name: typeof row.name === "string" ? row.name : "",
      brand: idOf(row.brand),
      store: idOf(row.store),
      unit: idOf(row.unit),
      subUnit: idOf(row.subUnit),
      subUnitsPerUnit: String(row.subUnitsPerUnit ?? "1"),
      composition: typeof row.composition === "string" ? row.composition : "",
      company: typeof row.company === "string" ? row.company : "",
      group: typeof row.group === "string" ? row.group : "",
      unitRate: String(row.unitRate ?? "0"),
      taxPercent: String(row.taxPercent ?? "0"),
      reorderLevel: String(row.reorderLevel ?? "0"),
      hsnSacNumber:
        typeof row.hsnSacNumber === "string" ? row.hsnSacNumber : "",
    }));
    setEditingItemId(String(row.id));
    setShowErrors(false);
    setShowForm(true);
  };
  const toggleItemStatus = async (row: Record<string, unknown>) => {
    const active = row.active !== false && row.status !== "inactive";
    try {
      await statusMutation.mutateAsync({
        resource: "items",
        id: String(row.id),
        payload: { active: !active },
      });
      showToast(
        `${cellText(row, "name")} ${active ? "deactivated" : "activated"} successfully.`,
        "success",
      );
    } catch (reason: unknown) {
      showToast(
        reason instanceof ApiError
          ? reason.message
          : "The item status could not be updated.",
        "error",
      );
    }
  };
  const editRequisition = (row: Record<string, unknown>) => {
    const rowLines = Array.isArray(row.lines)
      ? (row.lines as Record<string, unknown>[])
      : [];
    const requisitionFor =
      row.requisitionFor === "patient" ? "patient" : "department";
    setPatientSearch(
      typeof row.patientUhid === "string"
        ? row.patientUhid
        : typeof row.patientName === "string"
          ? row.patientName
          : "",
    );
    setForm((current) => ({
      ...current,
      requisitionDate: String(row.requisitionDate ?? "").slice(0, 10),
      requisitionFor,
      department: idOf(row.department),
      patient: idOf(row.patient),
      note: typeof row.note === "string" ? row.note : "",
    }));
    setLines(
      rowLines.length > 0
        ? rowLines.map((line) => {
            const itemId = idOf(line.item);
            const item = itemFor(selectableRequisitionItems, itemId);
            const itemIsSelectable =
              !selectableRequisitionItems || Boolean(item);
            const unitQuantity = Number(line.unitQuantity ?? 0);
            const subUnitQuantity = Number(line.subUnitQuantity ?? 0);
            const parts =
              unitQuantity > 0 || subUnitQuantity > 0
                ? {
                    unitQuantity: String(unitQuantity),
                    subUnitQuantity: String(subUnitQuantity),
                  }
                : quantityParts(
                    Number(line.requestedQuantity ?? line.quantity ?? 0),
                    item,
                  );
            return {
              ...blankLine(),
              item: itemIsSelectable ? itemId : "",
              itemName: !itemIsSelectable
                ? ""
                : typeof line.itemName === "string" && line.itemName
                  ? line.itemName
                  : typeof line.item === "object" && line.item
                    ? String((line.item as Record<string, unknown>).name ?? "")
                    : (item?.label ?? ""),
              requestedQuantity: String(
                line.requestedQuantity ?? line.quantity ?? "",
              ),
              unitQuantity: parts.unitQuantity,
              subUnitQuantity: parts.subUnitQuantity,
              rate: String(line.rate ?? 0),
              remarks: typeof line.remarks === "string" ? line.remarks : "",
            };
          })
        : [blankLine()],
    );
    setEditingRequisitionId(String(row.id));
    setShowErrors(false);
    setShowForm(true);
  };
  const editPurchaseOrder = (row: Record<string, unknown>) => {
    const rowLines = Array.isArray(row.lines)
      ? (row.lines as Record<string, unknown>[])
      : [];
    const requisition = Array.isArray(row.requisitions)
      ? row.requisitions[0]
      : row.requisition ?? row.requisitionId ?? null;
    const requisitionId = idOf(requisition);
    setEditingPurchaseOrderRequisition(
      requisitionId
        ? {
            id: requisitionId,
            label:
              typeof requisition === "object" && requisition !== null &&
              typeof (requisition as Record<string, unknown>).requisitionNumber === "string"
                ? String((requisition as Record<string, unknown>).requisitionNumber)
                : requisitionId,
          }
        : null,
    );
    setForm((current) => ({
      ...current,
      orderDate: String(row.orderDate ?? "").slice(0, 10),
      vendor: idOf(row.vendor),
      store: idOf(row.store),
      requisition: requisitionId,
      note: typeof row.note === "string" ? row.note : "",
    }));
    setLines(
      rowLines.length > 0
        ? rowLines.map((line) => {
            const itemId = idOf(line.item);
            const item = itemFor(items.data, itemId);
            const itemIsSelectable = !items.data || Boolean(item);
            const isAmountDiscount =
              line.discountType === "amount" ||
              (!line.discountType && Number(line.discountAmount ?? 0) > 0);
            const subUnitsPerUnit = Number(item?.subUnitsPerUnit ?? 1) || 1;
            const quantity = Number(
              line.quantity ??
                Number(line.unitQuantity ?? 0) * subUnitsPerUnit +
                  Number(line.subUnitQuantity ?? 0),
            );
            const gross = (quantity / subUnitsPerUnit) * Number(line.rate ?? 0);
            const discountAmount = isAmountDiscount
              ? Number(line.discountAmount ?? 0)
              : (gross * Number(line.discountPercent ?? 0)) / 100;
            return {
              ...blankLine(),
              item: itemIsSelectable ? itemId : "",
              itemName:
                item?.label ??
                (typeof line.itemName === "string" ? line.itemName : ""),
              unitQuantity: String(line.unitQuantity ?? 0),
              subUnitQuantity: String(line.subUnitQuantity ?? 0),
              rate: String(line.rate ?? 0),
              taxPercent: String(line.taxPercent ?? 0),
              discountType: "amount",
              discountPercent: "0",
              discountAmount: String(discountAmount),
            };
          })
        : [blankLine()],
    );
    setEditingPurchaseOrderId(String(row.id));
    setShowErrors(false);
    setShowForm(true);
  };
  const workOrderPayload = () => {
    const firstLine = lines.find((line) => line.remarks.trim());
    const subject =
      form.subject.trim() ||
      firstLine?.remarks.trim().slice(0, 200) ||
      firstLine?.itemName ||
      "Work order";
    return {
      workOrderDate: form.workOrderDate,
      expectedDate: form.expectedDate || null,
      department: form.department,
      vendor: form.vendor || null,
      store: form.store || null,
      priority: form.priority,
      subject,
      lines: lines.map((line) => {
        const item = itemFor(workOrderTypes.data, line.item);
        return {
          item: line.item,
          unit: line.unit,
          quantity: quantityFor(line, item),
          unitQuantity: number(line.unitQuantity),
          subUnitQuantity: subUnitQuantityFor(line, item),
          rate: number(line.rate),
          remarks: line.remarks || null,
        };
      }),
      note: form.note || null,
    };
  };
  const editWorkOrder = (row: Record<string, unknown>) => {
    const rowLines = Array.isArray(row.lines)
      ? (row.lines as Record<string, unknown>[])
      : [];
    setForm((current) => ({
      ...current,
      workOrderNumber:
        typeof row.workOrderNumber === "string" ? row.workOrderNumber : "",
      workOrderDate: String(row.workOrderDate ?? "").slice(0, 10),
      expectedDate: String(row.expectedDate ?? "").slice(0, 10),
      department: idOf(row.department),
      vendor: idOf(row.vendor),
      store: idOf(row.store),
      priority: typeof row.priority === "string" ? row.priority : "normal",
      subject: typeof row.subject === "string" ? row.subject : "",
      note: typeof row.note === "string" ? row.note : "",
    }));
    setLines(
      rowLines.length > 0
        ? rowLines.map((line) => {
            const savedItem = line.item ?? line.type;
            const savedItemId = idOf(savedItem);
            const savedItemUnit =
              typeof savedItem === "object" && savedItem !== null
                ? (savedItem as Record<string, unknown>).unit
                : undefined;
            const savedItemName =
              lookupLabel(line.itemName) || lookupLabel(savedItem);
            const matchingItem = workOrderTypes.data?.find(
              (entry) =>
                entry.id === savedItemId ||
                entry.label.trim().toLowerCase() ===
                  savedItemName.trim().toLowerCase(),
            );
            const itemId =
              matchingItem?.id || savedItemId || savedItemName;
            const item = matchingItem || itemFor(workOrderTypes.data, itemId);
            const savedUnitId =
              idOf(line.unit) || idOf(savedItemUnit) || idOf(item?.unit);
            const savedUnitName =
              lookupLabel(line.unitName) ||
              lookupLabel(line.unit) ||
              lookupLabel(savedItemUnit) ||
              lookupLabel(item?.unitName) ||
              lookupLabel(item?.unit);
            const matchingUnit = workOrderUnits.data?.find((unit) => unit.id === savedUnitId || unit.label.trim().toLowerCase() === savedUnitName.trim().toLowerCase());
            const unitId =
              matchingUnit?.id ||
              savedUnitId ||
              idOf(item?.unit) ||
              savedUnitName;
            const unitName = matchingUnit?.label || savedUnitName;
            const parts =
              Number(line.unitQuantity ?? 0) > 0 ||
              Number(line.subUnitQuantity ?? 0) > 0
                ? {
                    unitQuantity: String(line.unitQuantity ?? 0),
                    subUnitQuantity: String(line.subUnitQuantity ?? 0),
                  }
                : quantityParts(Number(line.quantity ?? 0), item);
            return {
              ...blankLine(),
              item: itemId,
              unit: unitId,
              unitName,
              itemName: savedItemName || (item?.label ?? ""),
              unitQuantity: parts.unitQuantity,
              subUnitQuantity: parts.subUnitQuantity,
              rate: String(line.rate ?? item?.unitRate ?? 0),
              remarks: typeof line.remarks === "string" ? line.remarks : "",
            };
          })
        : [blankLine()],
    );
    setEditingWorkOrderId(String(row.id));
    setShowErrors(false);
    setShowForm(true);
  };
  const setRequisitionStatus = async (
    id: string,
    status: "verified" | "approved",
  ): Promise<boolean> => {
    if (!(await confirmUpdate("this requisition status"))) return false;
    try {
      await statusMutation.mutateAsync({
        resource: "requisitions",
        id: `${id}/status`,
        payload: { status, rejectionNote: null },
      });
      showToast(`Requisition ${status} successfully.`, "success");
      return true;
    } catch (reason: unknown) {
      showToast(
        storeErrorMessage(
          reason,
          "The requisition status could not be updated.",
        ),
        "error",
      );
      return false;
    }
  };
  const requisitionPayload = () => ({
    requisitionDate: form.requisitionDate,
    requisitionFor: form.requisitionFor,
    department: form.requisitionFor === "department" ? form.department : null,
    patient: form.requisitionFor === "patient" ? form.patient : null,
    lines: lines.map((line) => {
      const item = itemFor(items.data, line.item);
      return {
        item: line.item,
        requestedQuantity:
          number(line.requestedQuantity) || quantityFor(line, item),
        unitQuantity: number(line.unitQuantity),
        subUnitQuantity: subUnitQuantityFor(line, item),
        rate: number(line.rate),
        remarks: line.remarks || null,
      };
    }),
    note: form.note || null,
  });
  const saveRequisitionAndSetStatus = async (
    status: "verified" | "approved",
  ) => {
    setShowErrors(true);
    if (
      Object.keys(validateStoreForm(effectiveSection, form, lines, isPharmacy))
        .length > 0
    )
      return;
    if (!editingRequisitionId) return;
    try {
      await statusMutation.mutateAsync({
        resource: "requisitions",
        id: editingRequisitionId,
        payload: requisitionPayload(),
      });
    } catch (reason: unknown) {
      showToast(
        storeErrorMessage(reason, "The requisition could not be saved."),
        "error",
      );
      return;
    }
    if (await setRequisitionStatus(editingRequisitionId, status)) closeForm();
  };
  const rejectRequisitionFromForm = () => {
    if (editingRequisitionId) setRejectingRequisitionId(editingRequisitionId);
  };
  const confirmRejectRequisition = async (rejectionNote: string) => {
    if (!rejectingRequisitionId) return;
    try {
      await statusMutation.mutateAsync({
        resource: "requisitions",
        id: `${rejectingRequisitionId}/status`,
        payload: { status: "rejected", rejectionNote },
      });
      showToast("Requisition rejected.", "success");
      setRejectingRequisitionId(null);
      closeForm();
    } catch (reason: unknown) {
      showToast(
        reason instanceof ApiError
          ? reason.message
          : "The requisition could not be rejected.",
        "error",
      );
    }
  };
  const setWorkOrderStatus = async (
    id: string,
    status: "verified" | "approved",
  ): Promise<boolean> => {
    if (!(await confirmUpdate("this work order status"))) return false;
    try {
      await statusMutation.mutateAsync({
        resource: "work-orders",
        id: `${id}/status`,
        payload: { status, rejectionNote: null },
      });
      showToast(`Work order ${status} successfully.`, "success");
      return true;
    } catch (reason: unknown) {
      showToast(
        reason instanceof ApiError
          ? reason.message
          : "The work order status could not be updated.",
        "error",
      );
      return false;
    }
  };
  const saveWorkOrderAndSetStatus = async (status: "verified" | "approved") => {
    setShowErrors(true);
    if (
      Object.keys(validateStoreForm(effectiveSection, form, lines, isPharmacy))
        .length > 0
    )
      return;
    if (!editingWorkOrderId) return;
    try {
      await statusMutation.mutateAsync({
        resource: "work-orders",
        id: editingWorkOrderId,
        payload: workOrderPayload(),
      });
    } catch (reason: unknown) {
      showToast(
        reason instanceof ApiError
          ? reason.message
          : "The work order could not be saved.",
        "error",
      );
      return;
    }
    if (await setWorkOrderStatus(editingWorkOrderId, status)) closeForm();
  };
  const rejectWorkOrderFromForm = () => {
    if (editingWorkOrderId) setRejectingWorkOrderId(editingWorkOrderId);
  };
  const confirmRejectWorkOrder = async (rejectionNote: string) => {
    if (!rejectingWorkOrderId) return;
    try {
      await statusMutation.mutateAsync({
        resource: "work-orders",
        id: `${rejectingWorkOrderId}/status`,
        payload: { status: "rejected", rejectionNote },
      });
      showToast("Work order rejected.", "success");
      setRejectingWorkOrderId(null);
      closeForm();
    } catch (reason: unknown) {
      showToast(
        reason instanceof ApiError
          ? reason.message
          : "The work order could not be rejected.",
        "error",
      );
    }
  };
  const purchaseOrderPayload = () => ({
    orderDate: form.orderDate,
    vendor: form.vendor,
    requisitions: form.requisition ? [form.requisition] : [],
    store: form.store || null,
    lines: lines.map((line) => {
      const item = itemFor(items.data, line.item);
      return {
        item: line.item,
        quantity: quantityFor(line, item),
        unitQuantity: number(line.unitQuantity),
        subUnitQuantity: subUnitQuantityFor(line, item),
        rate: number(line.rate),
        taxPercent: number(line.taxPercent),
        discountType: "amount",
        discountPercent: 0,
        discountAmount: number(line.discountAmount),
      };
    }),
    note: form.note || null,
  });
  const setPurchaseOrderStatus = async (
    id: string,
    status: "verified" | "approved",
  ): Promise<boolean> => {
    if (!(await confirmUpdate("this purchase order status"))) return false;
    try {
      await statusMutation.mutateAsync({
        resource: "purchase-orders",
        id: `${id}/status`,
        payload: { status, rejectionNote: null },
      });
      showToast(`Purchase order ${status} successfully.`, "success");
      return true;
    } catch (reason: unknown) {
      showToast(
        reason instanceof ApiError
          ? reason.message
          : "The purchase order status could not be updated.",
        "error",
      );
      return false;
    }
  };
  const savePurchaseOrderAndSetStatus = async (
    status: "verified" | "approved",
  ) => {
    setShowErrors(true);
    if (
      Object.keys(validateStoreForm("purchase-orders", form, lines)).length > 0
    )
      return;
    if (!editingPurchaseOrderId) return;
    try {
      await statusMutation.mutateAsync({
        resource: "purchase-orders",
        id: editingPurchaseOrderId,
        payload: purchaseOrderPayload(),
      });
    } catch (reason: unknown) {
      showToast(
        reason instanceof ApiError
          ? reason.message
          : "The purchase order could not be saved.",
        "error",
      );
      return;
    }
    if (await setPurchaseOrderStatus(editingPurchaseOrderId, status))
      closeForm();
  };
  const rejectPurchaseOrderFromForm = () => {
    if (editingPurchaseOrderId)
      setRejectingPurchaseOrderId(editingPurchaseOrderId);
  };
  const confirmRejectPurchaseOrder = async (rejectionNote: string) => {
    if (!rejectingPurchaseOrderId) return;
    try {
      await statusMutation.mutateAsync({
        resource: "purchase-orders",
        id: `${rejectingPurchaseOrderId}/status`,
        payload: { status: "rejected", rejectionNote },
      });
      showToast("Purchase order rejected.", "success");
      setRejectingPurchaseOrderId(null);
      closeForm();
    } catch (reason: unknown) {
      showToast(
        reason instanceof ApiError
          ? reason.message
          : "The purchase order could not be rejected.",
        "error",
      );
    }
  };
  const updateWorkOrderStatus = async (
    id: string,
    status: "in-progress" | "completed" | "cancelled",
  ) => {
    if (!(await confirmUpdate("this work order status"))) return;
    try {
      await statusMutation.mutateAsync({
        resource: "work-orders",
        id: `${id}/status`,
        payload: { status },
      });
      showToast("Work order status updated successfully.", "success");
    } catch (reason: unknown) {
      showToast(
        reason instanceof ApiError
          ? reason.message
          : "The work order status could not be updated.",
        "error",
      );
    }
  };
  const deleteVendor = async (row: Record<string, unknown>) => {
    if (!(await confirmUpdate("this vendor"))) return;
    try {
      await statusMutation.mutateAsync({
        resource: "vendors",
        id: String(row.id),
        payload: { status: "archived" },
      });
      showToast("Vendor deleted successfully.", "success");
    } catch (reason: unknown) {
      showToast(
        reason instanceof ApiError
          ? reason.message
          : "The vendor could not be deleted.",
        "error",
      );
    }
  };
  const toggleVendorStatus = async (row: Record<string, unknown>) => {
    const active = row.status === "active";
    try {
      await statusMutation.mutateAsync({
        resource: "vendors",
        id: String(row.id),
        payload: { status: active ? "inactive" : "active" },
      });
      showToast(
        `Medicine Vendor ${active ? "deactivated" : "activated"} successfully.`,
        "success",
      );
    } catch (reason: unknown) {
      showToast(
        reason instanceof ApiError
          ? reason.message
          : "The Medicine Vendor status could not be updated.",
        "error",
      );
    }
  };
  const recordPurchasePayment = async (
    amount: number,
    mode: string,
    bankName: string,
  ) => {
    if (!payingPurchase) return;
    try {
      const updated = await mutation.mutateAsync({
        resource: `purchases/${String(payingPurchase.id)}/payments`,
        payload: {
          amount,
          mode,
          bankName: bankName.trim() || null,
          paymentDate: new Date().toISOString(),
        },
      });
      showToast("Payment recorded successfully.", "success");
      setPayingPurchase(null);
      setViewingPurchase(updated);
    } catch (reason: unknown) {
      showToast(
        reason instanceof ApiError
          ? reason.message
          : "The payment could not be recorded.",
        "error",
      );
    }
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setShowErrors(true);
    if (
      Object.keys(validateStoreForm(effectiveSection, form, lines, isPharmacy))
        .length > 0
    )
      return;
    const submitter = (event.nativeEvent as SubmitEvent)
      .submitter as HTMLButtonElement | null;
    const stockUpdate = submitter?.value === "stock-update";
    try {
      if (section === "masters") {
        const expenseLedgerFields =
          form.kind === "expense-ledger-item"
            ? {
                tdsApplicable: form.tdsApplicable,
                tdsNatureOfPayment: form.tdsNatureOfPayment || null,
                tdsSectionCode: form.tdsSectionCode || null,
                tdsRate: number(form.tdsRate),
                tdsThreshold: number(form.tdsThreshold),
                gstApplicable: form.gstApplicable,
                gstChargeType: form.gstChargeType || null,
                sacHsnCode: form.sacHsnCode || null,
                gstRate: number(form.gstRate),
                igstLedgerName: form.igstLedgerName || null,
                cgstLedgerName: form.cgstLedgerName || null,
                sgstLedgerName: form.sgstLedgerName || null,
                rcmOutputGstLedger: form.rcmOutputGstLedger || null,
                costCentreApplicable: form.costCentreApplicable,
                defaultCostCentre: form.defaultCostCentre || null,
                openingBalance: number(form.openingBalance),
                descriptionNotes: form.descriptionNotes || null,
              }
            : {};
        if (editingMasterId)
          await statusMutation.mutateAsync({
            resource: "masters",
            id: editingMasterId,
            payload: {
              name: form.name,
              ...(form.kind === "expense-ledger-item"
                ? { parent: form.parent || null, ...expenseLedgerFields }
                : {}),
            },
          });
        else
          await mutation.mutateAsync({
            resource: "masters",
            payload: {
              kind: form.kind,
              name: form.name,
              code: form.code || null,
              parent:
                form.kind === "category" || form.kind === "expense-ledger-item"
                  ? form.parent || null
                  : null,
              status: form.masterStatus,
              ...expenseLedgerFields,
            },
          });
      } else if (
        section === "vendors" ||
        section === "work-order-vendors" ||
        section === "miscellaneous-vendors"
      ) {
        const vendorPayload = {
          vendorName: form.vendorName,
          gstin: form.gstin || null,
          phone: form.phone || null,
          email: form.email || null,
          contactPersonName: form.contactPersonName || null,
          address: form.address || null,
          pinCode: form.pinCode || null,
          status: form.masterStatus,
        };
        if (
          (section === "work-order-vendors" ||
            section === "miscellaneous-vendors" ||
            (isPharmacy && section === "vendors")) &&
          editingVendorId
        )
          await statusMutation.mutateAsync({
            resource: "vendors",
            id: editingVendorId,
            payload: vendorPayload,
          });
        else
          await mutation.mutateAsync({
            resource: "vendors",
            payload: {
              scope:
                section === "work-order-vendors"
                  ? "work-order"
                  : section === "miscellaneous-vendors"
                    ? "miscellaneous"
                    : "store",
              ...vendorPayload,
            },
          });
      } else if (section === "items") {
        const itemPayload = {
          name: form.name,
          type: isPharmacy ? null : form.type,
          category: form.category,
          subCategory: form.subCategory || null,
          brand: isPharmacy ? null : form.brand || null,
          store: form.store || null,
          unit: form.unit,
          subUnit: form.subUnit,
          subUnitsPerUnit: number(form.subUnitsPerUnit),
          reorderLevel: number(form.reorderLevel),
          hsnSacNumber: form.hsnSacNumber || null,
          ...(isPharmacy
            ? {
                composition: form.composition || null,
                company: form.company || null,
                group: form.group || null,
                unitRate: number(form.unitRate),
                taxPercent: number(form.taxPercent),
              }
            : {}),
        };
        if (editingItemId)
          await statusMutation.mutateAsync({
            resource: "items",
            id: editingItemId,
            payload: itemPayload,
          });
        else {
          await mutation.mutateAsync({
            resource: "items",
            payload: {
              ...itemPayload,
              itemCode: `ITM-${Date.now().toString(36).toUpperCase()}`,
            },
          });
          if (!isPharmacy) {
            setListSearch("");
            setListPage(1);
            setTableSortState({ key: null, direction: "asc" });
          }
        }
      } else if (section === "requisitions") {
        const payload = requisitionPayload();
        if (editingRequisitionId)
          await statusMutation.mutateAsync({
            resource: "requisitions",
            id: editingRequisitionId,
            payload,
          });
        else await mutation.mutateAsync({ resource: "requisitions", payload });
      } else if (section === "purchase-orders") {
        const payload = purchaseOrderPayload();
        if (editingPurchaseOrderId)
          await statusMutation.mutateAsync({
            resource: "purchase-orders",
            id: editingPurchaseOrderId,
            payload,
          });
        else
          await mutation.mutateAsync({ resource: "purchase-orders", payload });
      } else if (section === "purchases") {
        await mutation.mutateAsync({
          resource: "purchases",
          payload: {
            purchaseDate: form.purchaseDate,
            vendor: form.vendor,
            invoiceNumber: form.invoiceNumber,
            invoiceDateTime: form.invoiceDateTime || null,
            purchaseOrder:
              form.purchaseType === "indirect" ? form.purchaseOrder : null,
            store: isPharmacy ? null : form.store || null,
            purchaseType: form.purchaseType,
            grnStatus: form.grnStatus,
            inspectionRequired,
            discountType: form.discountType,
            totalDiscountAmount: number(form.totalDiscountAmount),
            stockUpdate,
            lines: lines.map((line) => {
              const item = itemFor(items.data, line.item);
              return {
                item: line.item,
                batchNumber: null,
                expiryDate: line.expiryDate || null,
                quantity: quantityFor(line, item),
                unitQuantity: number(line.unitQuantity),
                subUnitQuantity: subUnitQuantityFor(line, item),
                testQuantity:
                  inspectionRequired && !isPharmacy
                    ? number(line.testQuantity)
                    : 0,
                rate: number(line.rate),
                mrp: number(line.mrp),
                taxPercent:
                  number(line.cgstPercent) +
                  number(line.sgstPercent) +
                  number(line.igstPercent),
                discountPercent: number(line.discountPercent),
                discountAmount: number(line.discountAmount),
                cgstPercent: number(line.cgstPercent),
                sgstPercent: number(line.sgstPercent),
                igstPercent: number(line.igstPercent),
              };
            }),
            note: form.note || null,
            paymentTerms: form.paymentTerms || null,
          },
        });
      } else if (section === "issues") {
        await mutation.mutateAsync({
          resource: "issues",
          payload: {
            issueDate: form.issueDate,
            requisitionFor: isPharmacy ? form.requisitionFor : "department",
            department: form.requisitionFor === "department" || !isPharmacy ? form.department : null,
            patient: isPharmacy && form.requisitionFor === "patient" ? form.patient : null,
            requisition: form.requisition,
            completionStatus: form.completionStatus,
            lines: lines.map((line) => {
              const item = itemFor(items.data, line.item);
              return {
                item: line.item,
                batch: line.batch || null,
                quantity: quantityFor(line, item),
                unitQuantity: number(line.unitQuantity),
                subUnitQuantity: subUnitQuantityFor(line, item),
              };
            }),
            note: form.note || null,
          },
        });
      } else if (section === "work-orders") {
        const payload = workOrderPayload();
        if (editingWorkOrderId)
          await statusMutation.mutateAsync({
            resource: "work-orders",
            id: editingWorkOrderId,
            payload,
          });
        else
          await mutation.mutateAsync({
            resource: "work-orders",
            payload,
          });
      } else return;
      showToast("Record saved successfully.", "success");
      closeForm();
    } catch (reason: unknown) {
      showToast(
        storeErrorMessage(reason, "The record could not be saved."),
        "error",
      );
    }
  };

  const purchaseOrderActions = (row: Record<string, unknown>) => {
    const canEdit =
      (effectiveSection === "purchase-order-verify" &&
        row.status === "draft") ||
      (effectiveSection === "purchase-order-approved" &&
        row.status === "verified") ||
      (effectiveSection === "purchase-orders" && row.status === "draft");
    return (
      <>
        {canEdit ? (
          <button
            className="opd-row-action-icon"
            type="button"
            title="Edit"
            aria-label="Edit purchase order"
            onClick={() => editPurchaseOrder(row)}
          >
            <IconEdit />
          </button>
        ) : null}
        <button
          className="opd-row-action-icon store-view-icon"
          type="button"
          title="View"
          aria-label="View purchase order"
          onClick={() => setViewingPurchaseOrder(row)}
        >
          <IconInfoSolid />
        </button>
        {row.status === "approved" ? (
          <button
            className="opd-row-action-icon"
            type="button"
            title="Print"
            aria-label="Print purchase order"
            onClick={() =>
              openAuthenticatedRoute(
                `${moduleBasePath}/purchase-orders/${String(row.id)}/print`,
              )
            }
          >
            <IconPrinter />
          </button>
        ) : null}
      </>
    );
  };

  const formVisible = [
    "masters",
    "vendors",
    "work-order-vendors",
    "miscellaneous-vendors",
    "items",
    "requisitions",
    "purchase-orders",
    "purchases",
    "issues",
    "work-orders",
  ].includes(effectiveSection);
  const formRenderable =
    formVisible ||
    effectiveSection === "requisition-verify" ||
    effectiveSection === "requisition-approved" ||
    effectiveSection === "purchase-order-verify" ||
    effectiveSection === "purchase-order-approved" ||
    effectiveSection === "work-order-verify" ||
    effectiveSection === "work-order-approved";
  const usesCompactMasterModal =
    section === "masters" &&
    Boolean(
      masterKind &&
      (isCompactWorkOrderMaster(masterKind) || isPharmacyNameOnlyMasterPage),
    );
  const usesPharmacyVendorModal = isPharmacy && section === "vendors";
  const usesScopedVendorModal =
    section === "work-order-vendors" || section === "miscellaneous-vendors";
  const scopedVendorTitle =
    section === "miscellaneous-vendors"
      ? "Miscellaneous Vendor"
      : "Work Order Vendor";
  const scopedVendorEyebrow =
    section === "miscellaneous-vendors" ? "Miscellaneous" : "Work Order Master";
  const compactMasterTitle = isPharmacyNameOnlyMasterPage
    ? title
    : masterKind
      ? masterTitles[masterKind]
      : "";
  const compactMasterShortTitle =
    masterKind === "work-order-unit" || masterKind === "unit"
      ? "Unit"
      : masterKind === "category"
        ? "Category"
        : masterKind === "expense-ledger-head"
          ? "Ledger Head"
          : "Type";
  if (section === "work-order-bills") return <WorkOrderBillPage />;
  return (
    <div className="page-stack store-page">
      <ModuleHero
        eyebrow={isPharmacy ? "Pharmacy" : "Store"}
        title={title}
        description={
          isPharmacy
            ? config.description
                .replace(/Store/g, "Pharmacy")
                .replace(/item/g, "medicine")
            : config.description
        }
        icon={IconStore}
        tone="store"
      />
      {section === "requisitions" ? (
        <div className="store-tabs" role="tablist">
          {requisitionTabs.map(({ tab, label }) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={requisitionTab === tab}
              className={`store-tab${requisitionTab === tab ? " active" : ""}`}
              onClick={() => switchRequisitionTab(tab)}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}
      {section === "purchase-orders" ? (
        <div className="store-tabs" role="tablist">
          {purchaseOrderTabs.map(({ tab, label }) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={poTab === tab}
              className={`store-tab${poTab === tab ? " active" : ""}`}
              onClick={() => switchPoTab(tab)}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}
      {section === "work-orders" ? (
        <div className="store-tabs" role="tablist">
          {workOrderTabs.map(({ tab, label }) => (
            <button
              key={tab}
              type="button"
              role="tab"
              aria-selected={workOrderTab === tab}
              className={`store-tab${workOrderTab === tab ? " active" : ""}`}
              onClick={() => switchWorkOrderTab(tab)}
            >
              {label}
            </button>
          ))}
        </div>
      ) : null}
      {section === "dashboard" ? (
        <StoreDashboard
          data={dashboard.data}
          loading={dashboard.isLoading}
          error={
            dashboard.isError
              ? `Unable to load ${isPharmacy ? "Pharmacy" : "Store"} dashboard.`
              : null
          }
        />
      ) : (
        <>
          {!showForm ||
          usesCompactMasterModal ||
          usesPharmacyVendorModal ||
          usesScopedVendorModal ? (
            <>
              {viewingPurchase ? (
                <PurchaseDetailPage
                  row={viewingPurchase}
                  onClose={() => {
                    setViewingPurchase(null);
                    setPayingPurchase(null);
                  }}
                  onMakePayment={() => setPayingPurchase(viewingPurchase)}
                />
              ) : (
                <RecordList
                  title={title}
                  resource={config.resource ?? ""}
                  params={listParams}
                  rows={rows}
                  columns={columnKeys}
                  columnLabels={
                    undefined
                  }
                  sortableKeys={
                    STORE_SORTABLE_COLUMNS[config.resource ?? ""] ?? []
                  }
                  tableSort={tableSort}
                  listControls={
                    hasStoreItemListControls
                      ? {
                          search: listSearch,
                          page: listPage,
                          limit: listLimit,
                          total: rows.data?.pagination.total ?? 0,
                          totalPages: Math.max(
                            rows.data?.pagination.totalPages ?? 1,
                            1,
                          ),
                          onSearchChange: (search) => {
                            setListSearch(search);
                            setListPage(1);
                          },
                          onPageChange: setListPage,
                          onLimitChange: (limit) => {
                            setListLimit(limit);
                            setListPage(1);
                          },
                        }
                      : undefined
                  }
                  onAdd={
                    formVisible && !(isPharmacy && section === "requisitions")
                      ? () => {
                          if (
                            usesCompactMasterModal ||
                            usesPharmacyVendorModal ||
                            usesScopedVendorModal
                          ) {
                            setForm(initialForm(masterKind));
                            setEditingMasterId(null);
                            setEditingVendorId(null);
                            setShowErrors(false);
                          }
                          setShowForm(true);
                        }
                      : undefined
                  }
                  addActions={
                    formVisible && isPharmacy && section === "requisitions"
                      ? [
                          {
                            label: "Add by Department",
                            onClick: () => {
                              setForm({
                                ...initialForm(masterKind),
                                requisitionFor: "department",
                              });
                              setPatientSearch("");
                              setShowErrors(false);
                              setShowForm(true);
                            },
                          },
                          {
                            label: "Add by Patient",
                            onClick: () => {
                              setForm({
                                ...initialForm(masterKind),
                                requisitionFor: "patient",
                              });
                              setPatientSearch("");
                              setShowErrors(false);
                              setShowForm(true);
                            },
                          },
                        ]
                      : undefined
                  }
                  addLabel={
                    usesCompactMasterModal
                      ? `Add ${compactMasterShortTitle}`
                      : usesPharmacyVendorModal
                        ? "Add Medicine Vendor"
                        : `Add ${title.replace(/s$/, "")}`
                  }
                  actionLabel={
                    isPharmacy && section === "items"
                      ? "Action"
                      : usesCompactMasterModal ||
                          (section === "masters" &&
                            masterKind === "expense-ledger-item")
                        ? "Action"
                        : undefined
                  }
                  actionClassName={
                    usesCompactMasterModal
                      ? "store-compact-master-actions"
                      : undefined
                  }
                  renderCell={
                    section === "masters" &&
                    masterKind === "expense-ledger-item"
                      ? (row, key, index) =>
                          key === "ledgerItemNumber" ? (
                            index + 1
                          ) : key === "ledgerItem" ? (
                            cellText(row, "name")
                          ) : key === "ledgerHead" ? (
                            cellText(row, "parent")
                          ) : key === "tdsApplicable" ||
                            key === "gstApplicable" ? (
                            <span
                              className={`status-badge store-ledger-applicable-badge ${row[key] === "Y" ? "active" : "critical"}`}
                            >
                              {scalarText(row[key])}
                            </span>
                          ) : key === "openingBalance" ? (
                            moneyNumber(row[key]).toFixed(2)
                          ) : undefined
                      : usesCompactMasterModal
                        ? (row, key, index) =>
                            key === "sNo"
                              ? index + 1
                              : key === "ledgerHead"
                                ? cellText(row, "name")
                                : undefined
                        : usesScopedVendorModal
                          ? (row, key, index) =>
                              key === "sNo" ? (
                                index + 1
                              ) : key === "contactPerson" ? (
                                cellText(row, "contactPersonName")
                              ) : key === "contactMobile" ? (
                                cellText(row, "phone")
                              ) : key === "contactEmail" ? (
                                cellText(row, "email")
                              ) : key === "status" ? (
                                <span
                                  className={`status-badge ${row.status === "active" ? "active" : "inactive"}`}
                                >
                                  {row.status === "active"
                                    ? "Active"
                                    : "Inactive"}
                                </span>
                              ) : undefined
                          : section === "requisitions"
                            ? (row, key) =>
                                key === "status" ? (
                                  <RequisitionStatusBadge status={row.status} />
                                ) : undefined
                            : section === "purchase-orders"
                              ? (row, key) =>
                                  key === "requisitions" ? (
                                    <TruncatedText
                                      className="store-cell-truncate"
                                      text={purchaseOrderRequisitionText(row)}
                                    />
                                  ) : key === "status" ? (
                                    <PurchaseOrderStatusBadge
                                      status={row.status}
                                    />
                                  ) : key === "total" ? (
                                    formatMoneyCompact(row.total)
                                  ) : undefined
                              : section === "work-orders"
                                ? (row, key, index) =>
                                    key === "serialNumber" ? (
                                      index + 1
                                    ) : key === "status" ? (
                                      <WorkOrderStatusBadge
                                        status={row.status}
                                      />
                                    ) : key === "generatedBy" ? (
                                      text(row, key)
                                    ) : undefined
                                : section === "purchases"
                                  ? (row, key) =>
                                      key === "purchaseType" ||
                                      key === "grnStatus" ? (
                                        <PurchaseBadge
                                          field={key}
                                          value={row[key]}
                                        />
                                      ) : key === "vendor" ? (
                                        <TruncatedText
                                          className="store-purchase-vendor-cell"
                                          text={cellText(row, key)}
                                        />
                                      ) : key === "invoiceNumber" ? (
                                        <TruncatedText
                                          className="store-purchase-invoice-cell"
                                          text={cellText(row, key)}
                                        />
                                      ) : key === "inspectionRequired" ? (
                                        boolText(row[key])
                                      ) : key === "total" ||
                                        key === "subTotal" ||
                                        key === "totalPayment" ||
                                        key === "dueAmount" ? (
                                        formatMoneyCompact(row[key])
                                      ) : undefined
                                  : section === "vendors"
                                    ? (row, key) =>
                                        key === "vendorName" ? (
                                          <TruncatedText
                                            className="store-vendor-name-cell"
                                            text={cellText(row, key)}
                                          />
                                        ) : undefined
                                    : section === "issues"
                                    ? (row, key) =>
                                        key === "completionStatus" ? (
                                          <PurchaseBadge
                                            field={key}
                                            value={row[key]}
                                          />
                                        ) : undefined
                                    : section === "items"
                                      ? (row, key, index) =>
                                          key === "serialNumber" ? (
                                            (listPage - 1) * listLimit + index + 1
                                          ) : key === "medicineName" ? (
                                            <TruncatedText
                                              className="store-cell-truncate"
                                              text={cellText(row, "name")}
                                            />
                                          ) : key === "medicineCategory" ? (
                                            cellText(row, "category")
                                          ) : key === "medicineComposition" ? (
                                            cellText(row, "composition")
                                          ) : key === "medicineUnit" ? (
                                            cellText(row, "unit")
                                          ) : key === "medicineSubUnit" ? (
                                            cellText(row, "subUnit")
                                          ) : key === "medicinePrice" ? (
                                            formatMoneyCompact(row.unitRate)
                                          ) : key === "status" ? (
                                            <button
                                              className={`status-badge store-medicine-status-button ${row.active !== false && row.status !== "inactive" ? "active" : "inactive"}`}
                                              type="button"
                                              aria-label={`${row.active !== false && row.status !== "inactive" ? "Deactivate" : "Activate"} ${cellText(row, "name")}`}
                                              disabled={
                                                statusMutation.isPending
                                              }
                                              onClick={() =>
                                                void toggleItemStatus(row)
                                              }
                                            >
                                              {row.active !== false &&
                                              row.status !== "inactive"
                                                ? "Active"
                                                : "Inactive"}
                                            </button>
                                          ) : key === "name" ? (
                                            <TruncatedText
                                              className="store-cell-truncate"
                                              text={cellText(row, key)}
                                            />
                                          ) : undefined
                                      : undefined
                  }
                  rowAction={
                    section === "masters" &&
                    masterKind === "expense-ledger-item"
                      ? (row) => (
                          <>
                            <button
                              className="opd-row-action-icon store-view-icon"
                              type="button"
                              title="View"
                              aria-label={`View ${cellText(row, "name")}`}
                              onClick={() => setViewingExpenseLedgerItem(row)}
                            >
                              <IconInfoSolid />
                            </button>
                            <button
                              className="opd-row-action-icon"
                              type="button"
                              title="Edit"
                              aria-label={`Edit ${cellText(row, "name")}`}
                              onClick={() => openExpenseLedgerItemEditor(row)}
                            >
                              <IconEdit />
                            </button>
                          </>
                        )
                      : usesCompactMasterModal
                        ? (row) => (
                            <>
                              {masterKind !== "expense-ledger-head" ? (
                                <button
                                  className="store-active-switch"
                                  type="button"
                                  role="switch"
                                  aria-checked={row.status === "active"}
                                  aria-label={`${row.status === "active" ? "Deactivate" : "Activate"} ${cellText(row, "name")}`}
                                  disabled={statusMutation.isPending}
                                  onClick={() => void toggleCompactMaster(row)}
                                >
                                  <span />
                                </button>
                              ) : null}
                              <button
                                className="opd-row-action-icon"
                                type="button"
                                title="Edit"
                                aria-label={`Edit ${cellText(row, "name")}`}
                                disabled={statusMutation.isPending}
                                onClick={() => openCompactMasterEditor(row)}
                              >
                                <IconEdit />
                              </button>
                            </>
                          )
                        : section === "masters" &&
                            masterKind === "expense-ledger-item"
                          ? (row) => (
                              <button
                                className="opd-row-action-icon"
                                type="button"
                                title="Edit"
                                aria-label={`Edit ${cellText(row, "name")}`}
                                onClick={() => openExpenseLedgerItemEditor(row)}
                              >
                                <IconEdit />
                              </button>
                            )
                          : effectiveSection === "requisition-verify"
                            ? (row) => (
                                <>
                                  {row.status === "submitted" ? (
                                    <button
                                      className="opd-row-action-icon"
                                      type="button"
                                      title="Edit"
                                      aria-label="Edit requisition"
                                      onClick={() => editRequisition(row)}
                                    >
                                      <IconEdit />
                                    </button>
                                  ) : null}
                                  <button
                                    className="opd-row-action-icon store-view-icon"
                                    type="button"
                                    title="View"
                                    aria-label="View requisition"
                                    onClick={() => setViewingRequisition(row)}
                                  >
                                    <IconInfoSolid />
                                  </button>
                                </>
                              )
                            : effectiveSection === "requisition-approved"
                              ? (row) => (
                                  <>
                                    {row.status === "verified" ? (
                                      <button
                                        className="opd-row-action-icon"
                                        type="button"
                                        title="Edit"
                                        aria-label="Edit requisition"
                                        onClick={() => editRequisition(row)}
                                      >
                                        <IconEdit />
                                      </button>
                                    ) : null}
                                    <button
                                      className="opd-row-action-icon store-view-icon"
                                      type="button"
                                      title="View"
                                      aria-label="View requisition"
                                      onClick={() => setViewingRequisition(row)}
                                    >
                                      <IconInfoSolid />
                                    </button>
                                  </>
                                )
                              : effectiveSection === "purchase-order-verify" ||
                                  effectiveSection ===
                                    "purchase-order-approved" ||
                                  effectiveSection === "purchase-orders"
                                ? purchaseOrderActions
                                : effectiveSection === "work-order-verify" ||
                                    effectiveSection ===
                                      "work-order-approved" ||
                                    effectiveSection === "work-orders"
                                  ? (row) => {
                                      const canEdit =
                                        (effectiveSection ===
                                          "work-order-verify" &&
                                          row.status === "submitted") ||
                                        (effectiveSection ===
                                          "work-order-approved" &&
                                          row.status === "verified") ||
                                        (effectiveSection === "work-orders" &&
                                          row.status === "submitted");
                                      return (
                                        <>
                                          {canEdit ? (
                                            <button
                                              className="opd-row-action-icon"
                                              type="button"
                                              title="Edit"
                                              aria-label="Edit work order"
                                              onClick={() => editWorkOrder(row)}
                                            >
                                              <IconEdit />
                                            </button>
                                          ) : null}
                                          <button
                                            className="opd-row-action-icon store-view-icon"
                                            type="button"
                                            title="View"
                                            aria-label="View work order"
                                            onClick={() => setViewingWorkOrder(row)}
                                          >
                                            <IconInfoSolid />
                                          </button>
                                        </>
                                      );
                                    }
                                  : effectiveSection === "requisitions"
                                    ? (row) => (
                                        <>
                                          {row.status === "submitted" ? (
                                            <button
                                              className="opd-row-action-icon"
                                              type="button"
                                              title="Edit"
                                              aria-label="Edit requisition"
                                              onClick={() =>
                                                editRequisition(row)
                                              }
                                            >
                                              <IconEdit />
                                            </button>
                                          ) : null}
                                          <button
                                            className="opd-row-action-icon store-view-icon"
                                            type="button"
                                            title="View"
                                            aria-label="View requisition"
                                            onClick={() =>
                                              setViewingRequisition(row)
                                            }
                                          >
                                            <IconInfoSolid />
                                          </button>
                                          {row.status === "approved" ? (
                                            <button
                                              className="opd-row-action-icon"
                                              type="button"
                                              title="Print"
                                              aria-label="Print requisition"
                                              onClick={() =>
                                                openAuthenticatedRoute(
                                                  `${moduleBasePath}/requisitions/${String(row.id)}/print`,
                                                )
                                              }
                                            >
                                              <IconPrinter />
                                            </button>
                                          ) : null}
                                        </>
                                      )
                                    : section === "issues"
                                      ? (row) => (
                                          <>
                                            <button
                                              className="opd-row-action-icon store-view-icon"
                                              type="button"
                                              title="View"
                                              aria-label="View issue"
                                              onClick={() =>
                                                setViewingIssue(row)
                                              }
                                            >
                                              <IconInfoSolid />
                                            </button>
                                            <button
                                              className="opd-row-action-icon"
                                              type="button"
                                              title="Print"
                                              aria-label="Print issue"
                                              onClick={() =>
                                                openAuthenticatedRoute(
                                                  `${moduleBasePath}/issues/${String(row.id)}/print`,
                                                )
                                              }
                                            >
                                              <IconPrinter />
                                            </button>
                                          </>
                                        )
                                      : section === "purchases"
                                        ? (row) => (
                                            <button
                                              className="opd-row-action-icon store-view-icon"
                                              type="button"
                                              title="View"
                                              aria-label="View purchase"
                                              onClick={() =>
                                                setViewingPurchase(row)
                                              }
                                            >
                                              <IconInfoSolid />
                                            </button>
                                          )
                                        : section === "work-order-vendors" ||
                                            section === "miscellaneous-vendors"
                                          ? (row) =>
                                              row.status !== "archived" ? (
                                                <>
                                                  <button
                                                    className="opd-row-action-icon"
                                                    type="button"
                                                    title="Edit"
                                                    aria-label={`Edit ${cellText(row, "vendorName")}`}
                                                    disabled={
                                                      statusMutation.isPending
                                                    }
                                                    onClick={() =>
                                                      openWorkOrderVendorEditor(
                                                        row,
                                                      )
                                                    }
                                                  >
                                                    <IconEdit />
                                                  </button>
                                                  <button
                                                    className="opd-row-action-icon is-delete"
                                                    type="button"
                                                    title="Delete"
                                                    aria-label="Delete vendor"
                                                    disabled={
                                                      statusMutation.isPending
                                                    }
                                                    onClick={() =>
                                                      void deleteVendor(row)
                                                    }
                                                  >
                                                    <IconTrash />
                                                  </button>
                                                </>
                                              ) : null
                                          : section === "vendors"
                                            ? (row) =>
                                                row.status !== "archived" ? (
                                                  <>
                                                    {usesPharmacyVendorModal ? (
                                                      <>
                                                        <button
                                                          className="store-active-switch"
                                                          type="button"
                                                          role="switch"
                                                          aria-checked={
                                                            row.status ===
                                                            "active"
                                                          }
                                                          aria-label={`${row.status === "active" ? "Deactivate" : "Activate"} ${cellText(row, "vendorName")}`}
                                                          disabled={
                                                            statusMutation.isPending
                                                          }
                                                          onClick={() =>
                                                            void toggleVendorStatus(
                                                              row,
                                                            )
                                                          }
                                                        >
                                                          <span />
                                                        </button>
                                                        <button
                                                          className="opd-row-action-icon"
                                                          type="button"
                                                          title="Edit"
                                                          aria-label={`Edit ${cellText(row, "vendorName")}`}
                                                          disabled={
                                                            statusMutation.isPending
                                                          }
                                                          onClick={() =>
                                                            openWorkOrderVendorEditor(
                                                              row,
                                                            )
                                                          }
                                                        >
                                                          <IconEdit />
                                                        </button>
                                                      </>
                                                    ) : null}
                                                    {!usesPharmacyVendorModal ? (
                                                      <button
                                                        className="opd-row-action-icon is-delete"
                                                        type="button"
                                                        title="Delete"
                                                        aria-label="Delete vendor"
                                                        disabled={
                                                          statusMutation.isPending
                                                        }
                                                        onClick={() =>
                                                          void deleteVendor(row)
                                                        }
                                                      >
                                                        <IconTrash />
                                                      </button>
                                                    ) : null}
                                                  </>
                                                ) : null
                                            : section === "items"
                                              ? (row) => (
                                                  <button
                                                    className="opd-row-action-icon"
                                                    type="button"
                                                    title="Edit"
                                                    aria-label="Edit item"
                                                    onClick={() =>
                                                      editItem(row)
                                                    }
                                                  >
                                                    <IconEdit />
                                                  </button>
                                                )
                                              : undefined
                  }
                />
              )}
            </>
          ) : null}
          {usesCompactMasterModal ? (
            <Modal
              open={showForm}
              onClose={() => {
                if (!mutation.isPending && !statusMutation.isPending)
                  closeForm();
              }}
              closeOnBackdrop={!mutation.isPending && !statusMutation.isPending}
              closeOnEscape={!mutation.isPending && !statusMutation.isPending}
              labelledBy="compact-work-order-master-modal-title"
              className="store-master-name-modal"
            >
              <header>
                <div>
                  <p>
                    {masterKind === "expense-ledger-head"
                      ? "Miscellaneous"
                      : isPharmacyNameOnlyMasterPage
                        ? "Pharmacy Master"
                        : "Store Master"}
                  </p>
                  <h2 id="compact-work-order-master-modal-title">
                    {editingMasterId
                      ? `Edit ${compactMasterTitle}`
                      : `Add ${compactMasterTitle}`}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={mutation.isPending || statusMutation.isPending}
                  aria-label="Close"
                >
                  ×
                </button>
              </header>
              <form onSubmit={(event) => void submit(event)}>
                <Field
                  label={
                    masterKind === "expense-ledger-head"
                      ? "Ledger Head"
                      : `${compactMasterTitle} Name`
                  }
                  value={form.name}
                  onChange={update("name")}
                  error={
                    errors.name
                      ? `${compactMasterTitle} is required.`
                      : undefined
                  }
                />
                <div className="bed-modal-actions">
                  <button
                    className="button button-secondary"
                    type="button"
                    disabled={mutation.isPending || statusMutation.isPending}
                    onClick={closeForm}
                  >
                    {masterKind === "expense-ledger-head" ? "Close" : "Cancel"}
                  </button>
                  <button
                    className="button button-primary"
                    type="submit"
                    disabled={mutation.isPending || statusMutation.isPending}
                  >
                    {mutation.isPending || statusMutation.isPending
                      ? "Saving…"
                      : editingMasterId || masterKind === "expense-ledger-head"
                        ? "Save"
                        : `Add ${compactMasterShortTitle}`}
                  </button>
                </div>
              </form>
            </Modal>
          ) : null}
          {usesPharmacyVendorModal ? (
            <Modal
              open={showForm}
              onClose={() => {
                if (!mutation.isPending && !statusMutation.isPending)
                  closeForm();
              }}
              closeOnBackdrop={!mutation.isPending && !statusMutation.isPending}
              closeOnEscape={!mutation.isPending && !statusMutation.isPending}
              labelledBy="medicine-vendor-modal-title"
              className="store-work-order-vendor-modal"
            >
              <header>
                <div>
                  <p>Pharmacy Master</p>
                  <h2 id="medicine-vendor-modal-title">
                    {editingVendorId
                      ? "Edit Medicine Vendor"
                      : "Add Medicine Vendor"}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={mutation.isPending || statusMutation.isPending}
                  aria-label="Close"
                >
                  ×
                </button>
              </header>
              <form
                className="store-work-order-vendor-form"
                onSubmit={(event) => void submit(event)}
              >
                <div className="form-grid store-work-order-vendor-grid">
                  <VendorFields form={form} update={update} uppercaseName />
                </div>
                <div className="bed-modal-actions">
                  <button
                    className="button button-secondary"
                    type="button"
                    disabled={mutation.isPending || statusMutation.isPending}
                    onClick={closeForm}
                  >
                    Cancel
                  </button>
                  <button
                    className="button button-primary"
                    type="submit"
                    disabled={mutation.isPending || statusMutation.isPending}
                  >
                    {mutation.isPending || statusMutation.isPending
                      ? "Saving…"
                      : "Save"}
                  </button>
                </div>
              </form>
            </Modal>
          ) : null}
          {usesScopedVendorModal ? (
            <Modal
              open={showForm}
              onClose={() => {
                if (!mutation.isPending && !statusMutation.isPending)
                  closeForm();
              }}
              closeOnBackdrop={!mutation.isPending && !statusMutation.isPending}
              closeOnEscape={!mutation.isPending && !statusMutation.isPending}
              labelledBy="work-order-vendor-modal-title"
              className="store-work-order-vendor-modal"
            >
              <header>
                <div>
                  <p>{scopedVendorEyebrow}</p>
                  <h2 id="work-order-vendor-modal-title">
                    {editingVendorId
                      ? `Edit ${scopedVendorTitle}`
                      : `Add ${scopedVendorTitle}`}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={closeForm}
                  disabled={mutation.isPending || statusMutation.isPending}
                  aria-label="Close"
                >
                  ×
                </button>
              </header>
              <form
                className="store-work-order-vendor-form"
                onSubmit={(event) => void submit(event)}
              >
                <div className="form-grid store-work-order-vendor-grid">
                  <WorkOrderVendorFields
                    form={form}
                    update={update}
                    setForm={setForm}
                  />
                </div>
                <div className="bed-modal-actions">
                  <button
                    className="button button-secondary"
                    type="button"
                    disabled={mutation.isPending || statusMutation.isPending}
                    onClick={closeForm}
                  >
                    Close
                  </button>
                  <button
                    className="button button-primary"
                    type="submit"
                    disabled={mutation.isPending || statusMutation.isPending}
                  >
                    {mutation.isPending || statusMutation.isPending
                      ? "Saving…"
                      : "Save"}
                  </button>
                </div>
              </form>
            </Modal>
          ) : null}
          {viewingRequisition ? (
            <RequisitionViewModal
              row={viewingRequisition}
              onClose={() => setViewingRequisition(null)}
            />
          ) : null}
          {viewingPurchaseOrder ? (
            <PurchaseOrderViewModal
              row={viewingPurchaseOrder}
              onClose={() => setViewingPurchaseOrder(null)}
            />
          ) : null}
          {viewingWorkOrder ? (
            <WorkOrderViewModal
              row={viewingWorkOrder}
              onClose={() => setViewingWorkOrder(null)}
            />
          ) : null}
          {viewingIssue ? (
            <IssueViewModal
              row={viewingIssue}
              hideBatch={isPharmacy}
              onClose={() => setViewingIssue(null)}
            />
          ) : null}
          {viewingExpenseLedgerItem ? (
            <ExpenseLedgerItemViewModal
              row={viewingExpenseLedgerItem}
              onClose={() => setViewingExpenseLedgerItem(null)}
            />
          ) : null}
          {payingPurchase ? (
            <PurchasePaymentModal
              row={payingPurchase}
              submitting={mutation.isPending}
              onClose={() => setPayingPurchase(null)}
              onConfirm={(amount, mode, bankName) =>
                void recordPurchasePayment(amount, mode, bankName)
              }
            />
          ) : null}
          {rejectingRequisitionId ? (
            <RejectModal
              title="Reject Requisition"
              submitting={statusMutation.isPending}
              onClose={() => setRejectingRequisitionId(null)}
              onConfirm={(reason) => void confirmRejectRequisition(reason)}
            />
          ) : null}
          {rejectingPurchaseOrderId ? (
            <RejectModal
              title="Reject Purchase Order"
              submitting={statusMutation.isPending}
              onClose={() => setRejectingPurchaseOrderId(null)}
              onConfirm={(reason) => void confirmRejectPurchaseOrder(reason)}
            />
          ) : null}
          {rejectingWorkOrderId ? (
            <RejectModal
              title="Reject Work Order"
              submitting={statusMutation.isPending}
              onClose={() => setRejectingWorkOrderId(null)}
              onConfirm={(reason) => void confirmRejectWorkOrder(reason)}
            />
          ) : null}
          {formRenderable &&
          showForm &&
          !usesCompactMasterModal &&
          !usesPharmacyVendorModal &&
          !usesScopedVendorModal ? (
            <form
              className={`form-card store-complete-form compact-form${isPharmacy && section === "items" ? " pharmacy-medicine-form" : ""}`}
              onSubmit={(event) => void submit(event)}
            >
              <div className="section-heading">
                <h2>
                  {editingRequisitionId ||
                  editingPurchaseOrderId ||
                  editingWorkOrderId ||
                  editingItemId ||
                  editingMasterId
                    ? "Edit"
                    : "Add"}{" "}
                  {!editingRequisitionId &&
                  section === "requisitions" &&
                  isPharmacy
                    ? `${form.requisitionFor === "patient" ? "Patient" : "Department"} Requisition`
                    : title.replace(/s$/, "")}
                </h2>
                <button
                  className="button button-secondary"
                  type="button"
                  onClick={closeForm}
                >
                  Cancel
                </button>
              </div>
              <div
                className={
                  section === "work-orders"
                    ? "form-grid store-work-order-header"
                    : section === "requisitions"
                      ? "form-grid store-requisition-header"
                      : "form-grid"
                }
              >
                {section === "masters" ? (
                  <MasterFields
                    form={form}
                    masterKind={masterKind}
                    categories={categories.data}
                    expenseLedgerHeads={expenseLedgerHeads.data}
                    update={update}
                    setForm={setForm}
                    errors={errors}
                  />
                ) : null}
                {section === "vendors" ? (
                  <VendorFields form={form} update={update} />
                ) : null}
                {section === "items" ? (
                  <ItemFields
                    isPharmacy={isPharmacy}
                    form={form}
                    itemTypes={itemTypes.data}
                    categories={categories.data}
                    brands={brands.data}
                    stores={stores.data}
                    units={units.data}
                    update={update}
                    setForm={setForm}
                    errors={errors}
                  />
                ) : null}
                {section === "requisitions" ? (
                  <>
                    <Field
                      label="Date"
                      type="date"
                      size="sm"
                      value={form.requisitionDate}
                      onChange={update("requisitionDate")}
                    />
                    {form.requisitionFor === "patient" && isPharmacy ? (
                      <label
                        className={`field field-half${errorClass(errors.patient)}`}
                      >
                        <span className="store-field-label">
                          Patient<span className="store-required">*</span>
                        </span>
                        <PatientUhidAutocomplete
                          id="pharmacy-requisition-patient"
                          value={patientSearch}
                          selectedId={form.patient}
                          options={(patientLookup.data?.data ?? []).filter(
                            (patient) => patient.matchType !== "bill",
                          )}
                          isFetching={patientLookup.isFetching}
                          isError={patientLookup.isError}
                          required
                          matchInputWidth
                          displaySelectedName
                          ariaInvalid={Boolean(errors.patient)}
                          mode="uhid-name-phone"
                          onInputChange={(value) => {
                            setPatientSearch(value);
                            setForm((current) => ({ ...current, patient: "" }));
                          }}
                          onChange={(patient) =>
                            setForm((current) => ({ ...current, patient }))
                          }
                        />
                        <FieldError message={errors.patient} />
                      </label>
                    ) : (
                      <Lookup
                        label="Department"
                        showRequiredMarker
                        matchDropdownWidth
                        size="half"
                        value={form.department}
                        options={departments}
                        error={errors.department}
                        onChange={(department) =>
                          setForm((current) => ({ ...current, department }))
                        }
                      />
                    )}
                  </>
                ) : null}
                {section === "purchase-orders" ? (
                  <>
                    <Field
                      label="Date"
                      type="date"
                      size="sm"
                      value={form.orderDate}
                      onChange={update("orderDate")}
                    />
                    <Lookup
                      label="Vendor"
                      size="lg"
                      value={form.vendor}
                      options={vendors.data}
                      error={errors.vendor}
                      onChange={(vendor) =>
                        setForm((current) => ({ ...current, vendor }))
                      }
                    />
                    <Lookup
                      label="Store"
                      value={form.store}
                      options={stores.data}
                      required={false}
                      onChange={(store) =>
                        setForm((current) => ({ ...current, store }))
                      }
                    />
                    <Lookup
                      label="Requisition No"
                      size="lg"
                      value={form.requisition}
                      options={requisitionOptions}
                      error={errors.requisition}
                      onChange={selectPurchaseOrderRequisition}
                    />
                  </>
                ) : null}
                {section === "purchases" ? (
                  <PurchaseHeaderFields
                    form={form}
                    vendors={vendors.data}
                    stores={stores.data}
                    showStore={!isPharmacy}
                    purchaseOrders={purchaseOrders.data}
                    update={update}
                    setForm={setForm}
                    updateInspectionRequired={updateInspectionRequired}
                    errors={errors}
                  />
                ) : null}
                {section === "issues" ? (
                  <>
                    <Field
                      label="Date"
                      type="date"
                      size="sm"
                      value={form.issueDate}
                      onChange={update("issueDate")}
                    />
                    <Lookup
                      label="Requisition No"
                      size="lg"
                      value={form.requisition}
                      options={requisitionOptions}
                      error={errors.requisition}
                      onChange={selectIssueRequisition}
                    />
                    {isPharmacy ? (
                      <Field
                        label="Issue For"
                        size="lg"
                        value={issueDestination}
                        required={false}
                        disabled
                      />
                    ) : (
                      <Lookup
                        label="Department"
                        value={form.department}
                        options={departments}
                        error={errors.department}
                        onChange={(department) => setForm((current) => ({ ...current, department, requisition: "" }))}
                      />
                    )}
                    <Select
                      label="Requisition Status"
                      size="sm"
                      value={form.completionStatus}
                      options={[
                        ["incomplete", "Issue Incomplete"],
                        ["complete", "Issue Complete"],
                      ]}
                      onChange={(completionStatus) =>
                        setForm((current) => ({ ...current, completionStatus }))
                      }
                    />
                  </>
                ) : null}
                {section === "work-orders" ? (
                  <WorkOrderHeaderFields
                    form={form}
                    workOrderNumber={
                      form.workOrderNumber ||
                      nextWorkOrderNumber.data ||
                      (nextWorkOrderNumber.isLoading ? "Loading..." : "")
                    }
                    departments={departments}
                    vendors={workOrderVendors.data}
                    update={update}
                    setForm={setForm}
                    errors={errors}
                  />
                ) : null}
              </div>
              {STORE_LINE_SECTIONS.includes(section) ? (
                <div className="store-line-editor">
                  <div className="section-heading">
                    <h2>Item details</h2>
                  </div>
                  {section === "requisitions" ? (
                    <RequisitionLineTable
                      lines={lines}
                      items={selectableRequisitionItems}
                      updateLine={updateLine}
                      removeLine={removeLine}
                      addLine={() =>
                        setLines((current) => [...current, blankLine()])
                      }
                      errors={errors}
                      allowAddLine={
                        effectiveSection !== "requisition-verify" &&
                        effectiveSection !== "requisition-approved"
                      }
                    />
                  ) : null}
                  {section === "purchase-orders" ? (
                    <PurchaseOrderLineTable
                      lines={lines}
                      items={items.data}
                      updateLine={updateLine}
                      removeLine={removeLine}
                      addLine={() =>
                        setLines((current) => [...current, blankLine()])
                      }
                      errors={errors}
                      itemSelectionDisabled={!form.requisition}
                      allowAddLine={Boolean(form.requisition)}
                    />
                  ) : null}
                  {section === "purchases" ? (
                    <PurchaseLineTable
                      lines={lines}
                      items={items.data}
                      updateLine={updateLine}
                      removeLine={removeLine}
                      addLine={() =>
                        setLines((current) => [...current, blankLine()])
                      }
                      errors={errors}
                      inspectionRequired={inspectionRequired}
                      hideTestQuantity={isPharmacy}
                      allowAddLine={
                        !(
                          form.purchaseType === "indirect" && form.purchaseOrder
                        )
                      }
                    />
                  ) : null}
                  {section === "issues" ? (
                    <IssueLineTable
                      lines={lines}
                      items={items.data}
                      updateLine={updateLine}
                      removeLine={removeLine}
                      addLine={() => undefined}
                      errors={errors}
                      allowAddLine={false}
                      hideBatch={isPharmacy}
                    />
                  ) : null}
                  {section === "work-orders" ? (
                    <WorkOrderLineTable
                      lines={lines}
                      items={workOrderTypes.data}
                      units={workOrderUnits.data}
                      updateLine={updateLine}
                      removeLine={removeLine}
                      addLine={() =>
                        setLines((current) => [...current, blankLine()])
                      }
                      errors={errors}
                    />
                  ) : null}
                </div>
              ) : null}
              {STORE_LINE_SECTIONS.includes(section) ? (
                <div
                  className={
                    section === "work-orders"
                      ? "store-summary-row store-work-order-footer"
                      : "store-summary-row"
                  }
                >
                  <div className="store-summary-notes">
                    <Field
                      label={
                        section === "work-orders"
                          ? "Terms & Conditions"
                          : "Note"
                      }
                      value={form.note}
                      onChange={update("note")}
                      textarea
                      required={false}
                    />
                    {section === "purchases" ? (
                      <Select
                        label="Payment Terms"
                        size="sm"
                        value={form.paymentTerms}
                        options={[
                          ["", "Select One"],
                          ["Advance", "Advance"],
                          ["After Billing", "After Billing"],
                          ["Payment Done", "Payment Done"],
                        ]}
                        onChange={(paymentTerms) =>
                          setForm((current) => ({ ...current, paymentTerms }))
                        }
                        required={false}
                      />
                    ) : null}
                  </div>
                  {section === "work-orders" ? (
                    <div className="store-work-order-note-attachment">
                      <Field
                        label="Note"
                        value={form.subject}
                        onChange={update("subject")}
                        required={false}
                      />
                      <div
                        className={`field store-work-order-attachment${isAttachmentDragging ? " is-dragging" : ""}`}
                        onDragEnter={(event) => {
                          event.preventDefault();
                          setIsAttachmentDragging(true);
                        }}
                        onDragOver={(event) => {
                          event.preventDefault();
                          event.dataTransfer.dropEffect = "copy";
                          setIsAttachmentDragging(true);
                        }}
                        onDragLeave={() => setIsAttachmentDragging(false)}
                        onDrop={(event) => {
                          event.preventDefault();
                          setIsAttachmentDragging(false);
                          addWorkOrderAttachments(event.dataTransfer.files);
                        }}
                      >
                        <span>Attachment</span>
                        <label className="store-work-order-dropzone">
                          <input
                            aria-label="Attachment"
                            type="file"
                            multiple
                            onChange={(event) => {
                              addWorkOrderAttachments(event.target.files ?? []);
                              event.target.value = "";
                            }}
                          />
                          <span
                            className="store-work-order-dropzone-icon"
                            aria-hidden="true"
                          >
                            <svg viewBox="0 0 24 24" fill="none">
                              <path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5" />
                              <path d="M5 14v4a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4" />
                            </svg>
                          </span>
                          <span className="store-work-order-dropzone-copy">
                            <strong>Drag &amp; drop files here</strong>
                            <span>
                              or <b>choose files</b> from your device
                            </span>
                          </span>
                        </label>
                        {workOrderAttachments.length ? (
                          <div className="store-work-order-attachment-files">
                            {workOrderAttachments.map((file) => (
                              <span
                                key={`${file.name}-${file.size}-${file.lastModified}`}
                                className="store-work-order-attachment-file"
                              >
                                <span title={file.name}>{file.name}</span>
                                <button
                                  type="button"
                                  aria-label={`Remove ${file.name}`}
                                  onClick={() =>
                                    setWorkOrderAttachments((current) =>
                                      current.filter(
                                        (selected) => selected !== file,
                                      ),
                                    )
                                  }
                                >
                                  <IconClose />
                                </button>
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  ) : null}
                  {section === "requisitions" ? (
                    <div className="store-totals-list">
                      <div>
                        <span>Estimated Value</span>
                        <strong>{formatMoney(requisitionTotal)}</strong>
                      </div>
                    </div>
                  ) : null}
                  {section === "purchase-orders" ? (
                    <div className="store-totals-list">
                      <div>
                        <span>Sub Total</span>
                        <strong>{formatMoney(orderTotals.subTotal)}</strong>
                      </div>
                      <div>
                        <span>Discount</span>
                        <strong>{formatMoney(orderTotals.discount)}</strong>
                      </div>
                      <div>
                        <span>Tax</span>
                        <strong>{formatMoney(orderTotals.tax)}</strong>
                      </div>
                      <div>
                        <span>Total</span>
                        <strong>{formatMoney(orderTotals.total)}</strong>
                      </div>
                    </div>
                  ) : null}
                  {section === "purchases" ? (
                    <PurchaseTotals
                      form={form}
                      totals={totals}
                      update={update}
                      setForm={setForm}
                    />
                  ) : null}
                  {section === "issues" ? (
                    <div className="store-totals-list">
                      <div>
                        <span>Total Value</span>
                        <strong>{formatMoney(issueTotal)}</strong>
                      </div>
                    </div>
                  ) : null}
                </div>
              ) : null}
              <div className="form-actions">
                {section === "requisitions" &&
                editingRequisitionId &&
                (effectiveSection === "requisition-verify" ||
                  effectiveSection === "requisition-approved") ? (
                  <>
                    <button
                      className="button button-secondary"
                      type="button"
                      disabled={statusMutation.isPending}
                      onClick={rejectRequisitionFromForm}
                    >
                      Reject
                    </button>
                    <button
                      className="button button-primary"
                      type="button"
                      disabled={statusMutation.isPending}
                      onClick={() =>
                        void saveRequisitionAndSetStatus(
                          effectiveSection === "requisition-approved"
                            ? "approved"
                            : "verified",
                        )
                      }
                    >
                      {statusMutation.isPending
                        ? "Saving…"
                        : effectiveSection === "requisition-approved"
                          ? "Approve"
                          : "Save & Verify"}
                    </button>
                  </>
                ) : section === "purchase-orders" &&
                  editingPurchaseOrderId &&
                  (effectiveSection === "purchase-order-verify" ||
                    effectiveSection === "purchase-order-approved") ? (
                  <>
                    <button
                      className="button button-secondary"
                      type="button"
                      disabled={statusMutation.isPending}
                      onClick={rejectPurchaseOrderFromForm}
                    >
                      Reject
                    </button>
                    <button
                      className="button button-primary"
                      type="button"
                      disabled={statusMutation.isPending}
                      onClick={() =>
                        void savePurchaseOrderAndSetStatus(
                          effectiveSection === "purchase-order-approved"
                            ? "approved"
                            : "verified",
                        )
                      }
                    >
                      {statusMutation.isPending
                        ? "Saving…"
                        : effectiveSection === "purchase-order-approved"
                          ? "Approve"
                          : "Save & Verify"}
                    </button>
                  </>
                ) : section === "work-orders" &&
                  editingWorkOrderId &&
                  (effectiveSection === "work-order-verify" ||
                    effectiveSection === "work-order-approved") ? (
                  <>
                    <button
                      className="button button-secondary"
                      type="button"
                      disabled={statusMutation.isPending}
                      onClick={rejectWorkOrderFromForm}
                    >
                      Reject
                    </button>
                    <button
                      className="button button-primary"
                      type="button"
                      disabled={statusMutation.isPending}
                      onClick={() =>
                        void saveWorkOrderAndSetStatus(
                          effectiveSection === "work-order-approved"
                            ? "approved"
                            : "verified",
                        )
                      }
                    >
                      {statusMutation.isPending
                        ? "Saving..."
                        : effectiveSection === "work-order-approved"
                          ? "Approve"
                          : "Save & Verify"}
                    </button>
                  </>
                ) : section === "purchases" ? (
                  <>
                    <button
                      className="button button-secondary"
                      disabled={mutation.isPending}
                      type="submit"
                      value="draft"
                    >
                      {mutation.isPending ? "Saving…" : "Save"}
                    </button>
                    <button
                      className="button button-primary"
                      disabled={mutation.isPending}
                      type="submit"
                      value="stock-update"
                    >
                      {mutation.isPending ? "Saving…" : "Save & Stock Update"}
                    </button>
                  </>
                ) : (
                  <button
                    className="button button-primary"
                    disabled={mutation.isPending}
                    type="submit"
                  >
                    {mutation.isPending ? "Saving…" : "Save"}
                  </button>
                )}
              </div>
            </form>
          ) : null}
        </>
      )}
    </div>
  );
};

export const StorePage = ({
  module = "store",
  ...props
}: {
  module?: InventoryModule;
  section: StoreSection;
  masterKind?: StoreMasterKind;
}) => (
  <InventoryModuleProvider module={module}>
    <StorePageContent {...props} />
  </InventoryModuleProvider>
);

type WorkOrderBillLine = {
  type: string;
  description: string;
  unit: string;
  amount: string;
  remarks: string;
};
type WorkOrderBillDeduction = {
  key: string;
  deduction: string;
  amount: string;
};
let workOrderBillDeductionSequence = 0;
const blankWorkOrderBillDeduction = (): WorkOrderBillDeduction => ({
  key: `work-order-bill-deduction-${++workOrderBillDeductionSequence}`,
  deduction: "",
  amount: "0",
});
const percentOrAmount = (value: string, mode: string, base: number) => {
  const amount = number(value);
  return mode === "percentage" ? (base * amount) / 100 : amount;
};

const WorkOrderBillPage = () => {
  const { showToast } = useToast();
  const [showForm, setShowForm] = useState(false);
  const [billTab, setBillTab] = useState<"bills" | "payments">("bills");
  const [payingWorkOrderBill, setPayingWorkOrderBill] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    search: "",
  });
  const [form, setForm] = useState({
    workOrder: "",
    vendor: "",
    billDate: today(),
    dueDate: "",
    remarks: "",
    miscellaneousChargeType: "amount",
    miscellaneousCharge: "0",
    discountType: "amount",
    discount: "0",
    tdsSectionCode: "",
    tdsPercent: "0",
    gstPercent: "0",
  });
  const [lines, setLines] = useState<WorkOrderBillLine[]>([]);
  const [deductions, setDeductions] = useState<WorkOrderBillDeduction[]>([
    blankWorkOrderBillDeduction(),
  ]);
  const [showErrors, setShowErrors] = useState(false);
  const rows = useStoreRows("work-order-bills", true, {
    ...(filters.fromDate ? { fromDate: filters.fromDate } : {}),
    ...(filters.toDate ? { toDate: filters.toDate } : {}),
    ...(filters.search ? { search: filters.search } : {}),
  });
  const workOrders = useStoreLookup("work-orders");
  const vendors = useStoreLookup("vendors", { vendorScope: "work-order" });
  const mutation = useCreateStoreRecord();
  const tableSort = useTableSort(null, "asc", () => undefined);
  const selectedWorkOrder = workOrders.data?.find(
    (entry) => entry.id === form.workOrder,
  );
  const paymentRows = useMemo(
    () =>
      (rows.data?.data ?? []).flatMap((bill) => {
        const payments = Array.isArray(bill.payments)
          ? (bill.payments as Record<string, unknown>[])
          : [];
        return payments.map((payment, index) => ({
          id: `${String(bill.id)}:${String(payment._id ?? index)}`,
          serialNumber: index + 1,
          billNumber: bill.billNumber,
          workOrderNumber: bill.workOrderNumber,
          vendor: bill.vendor,
          paymentDate: payment.date,
          mode: payment.mode,
          bankName: payment.bankName,
          amount: payment.amount,
          receivedBy: payment.receivedBy,
        }));
      }),
    [rows.data],
  );
  const grossAmount = lines.reduce((sum, line) => sum + number(line.amount), 0);
  const deductionRowsTotal = deductions.reduce(
    (sum, row) => sum + number(row.amount),
    0,
  );
  const miscAmount = percentOrAmount(
    form.miscellaneousCharge,
    form.miscellaneousChargeType,
    grossAmount,
  );
  const discountAmount = percentOrAmount(
    form.discount,
    form.discountType,
    grossAmount,
  );
  const tdsAmount = (grossAmount * number(form.tdsPercent)) / 100;
  const gstAmount =
    (Math.max(0, grossAmount + miscAmount - discountAmount) *
      number(form.gstPercent)) /
    100;
  const totalDeduction = deductionRowsTotal + discountAmount + tdsAmount;
  const netAmount = Math.max(
    0,
    grossAmount + miscAmount + gstAmount - totalDeduction,
  );
  const resetForm = () => {
    setForm({
      workOrder: "",
      vendor: "",
      billDate: today(),
      dueDate: "",
      remarks: "",
      miscellaneousChargeType: "amount",
      miscellaneousCharge: "0",
      discountType: "amount",
      discount: "0",
      tdsSectionCode: "",
      tdsPercent: "0",
      gstPercent: "0",
    });
    setLines([]);
    setDeductions([blankWorkOrderBillDeduction()]);
    setShowErrors(false);
    setShowForm(false);
  };
  const selectWorkOrder = (workOrder: string) => {
    const selected = workOrders.data?.find((entry) => entry.id === workOrder);
    setForm((current) => ({
      ...current,
      workOrder,
      vendor: selected?.vendor ?? current.vendor,
    }));
    setLines(
      (selected?.lines ?? []).map((line) => ({
        type: line.type,
        description: line.description ?? "",
        unit: line.unit ?? "",
        amount: String(line.amount ?? 0),
        remarks: line.remarks ?? "",
      })),
    );
  };
  const updateDeduction = (
    key: string,
    values: Partial<WorkOrderBillDeduction>,
  ) =>
    setDeductions((current) =>
      current.map((row) => (row.key === key ? { ...row, ...values } : row)),
    );
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setShowErrors(true);
    if (!form.workOrder || !form.vendor || !form.billDate) return;
    try {
      await mutation.mutateAsync({
        resource: "work-order-bills",
        payload: {
          workOrder: form.workOrder,
          vendor: form.vendor,
          billDate: form.billDate,
          dueDate: form.dueDate || null,
          remarks: form.remarks || null,
          lines: lines.map((line) => ({
            amount: number(line.amount),
            remarks: line.remarks || null,
          })),
          deductions: deductions.map((row) => ({
            deduction: row.deduction || null,
            amount: number(row.amount),
          })),
          miscellaneousChargeType: form.miscellaneousChargeType,
          miscellaneousCharge: number(form.miscellaneousCharge),
          discountType: form.discountType,
          discount: number(form.discount),
          tdsSectionCode: form.tdsSectionCode || null,
          tdsPercent: number(form.tdsPercent),
          gstPercent: number(form.gstPercent),
        },
      });
      showToast("Work order bill created successfully.", "success");
      resetForm();
    } catch (reason: unknown) {
      showToast(
        reason instanceof ApiError
          ? reason.message
          : "The work order bill could not be created.",
        "error",
      );
    }
  };
  const recordWorkOrderBillPayment = async (
    amount: number,
    mode: string,
    bankName: string,
  ) => {
    if (!payingWorkOrderBill) return;
    try {
      await mutation.mutateAsync({
        resource: `work-order-bills/${String(payingWorkOrderBill.id)}/payments`,
        payload: {
          amount,
          mode,
          bankName: bankName || null,
          paymentDate: today(),
        },
      });
      showToast("Payment recorded successfully.", "success");
      setPayingWorkOrderBill(null);
      setBillTab("payments");
    } catch (reason: unknown) {
      showToast(
        reason instanceof ApiError
          ? reason.message
          : "The payment could not be recorded.",
        "error",
      );
    }
  };
  const columns = [
    "serialNumber",
    "workOrderNumber",
    "vendor",
    "billDate",
    "netAmount",
    "totalPayment",
    "dueAmount",
    "generatedBy",
    "approvalStatus",
    "paymentStatus",
  ];
  const paymentColumns = [
    "serialNumber",
    "billNumber",
    "workOrderNumber",
    "vendor",
    "paymentDate",
    "mode",
    "bankName",
    "amount",
    "receivedBy",
  ];
  return (
    <div className="page-stack store-page">
      <ModuleHero
        eyebrow="Store"
        title={
          showForm
            ? "Create Work Order Bill"
            : billTab === "payments"
              ? "Work Order Bill Payment List"
              : "Work Order Bill List"
        }
        description="Generate and track bills raised against approved work orders."
        icon={IconStore}
        tone="store"
      />
      <div className="store-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={!showForm && billTab === "bills"}
          className={`store-tab${!showForm && billTab === "bills" ? " active" : ""}`}
          onClick={() => {
            setShowForm(false);
            setBillTab("bills");
          }}
        >
          Bill List
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={!showForm && billTab === "payments"}
          className={`store-tab${!showForm && billTab === "payments" ? " active" : ""}`}
          onClick={() => {
            setShowForm(false);
            setBillTab("payments");
          }}
        >
          Payment List
        </button>
      </div>
      {!showForm ? (
        <section className="content-card store-work-order-bill-list">
          <div className="section-heading">
            <h2>
              {billTab === "payments"
                ? "Work Order Bill Payment list"
                : "Work Order Bill list"}
            </h2>
            <form
              className="store-work-order-bill-filters"
              onSubmit={(event) => event.preventDefault()}
            >
              <div className="store-work-order-bill-date-range">
                <label>
                  <span
                    className={`store-work-order-bill-date-input${filters.fromDate ? " has-value" : ""}`}
                    data-placeholder="Start Date"
                  >
                    <input
                      type="date"
                      value={filters.fromDate}
                      aria-label="Start Date"
                      onChange={(event) =>
                        setFilters((current) => ({
                          ...current,
                          fromDate: event.target.value,
                        }))
                      }
                    />
                  </span>
                </label>
                <label>
                  <span
                    className={`store-work-order-bill-date-input${filters.toDate ? " has-value" : ""}`}
                    data-placeholder="End Date"
                  >
                    <input
                      type="date"
                      value={filters.toDate}
                      aria-label="End Date"
                      onChange={(event) =>
                        setFilters((current) => ({
                          ...current,
                          toDate: event.target.value,
                        }))
                      }
                    />
                  </span>
                </label>
              </div>
              <label>
                <input
                  value={filters.search}
                  placeholder="Search"
                  aria-label="Search"
                  onChange={(event) =>
                    setFilters((current) => ({
                      ...current,
                      search: event.target.value,
                    }))
                  }
                />
              </label>
              {billTab === "bills" ? (
                <button
                  className="button button-primary"
                  type="button"
                  onClick={() => setShowForm(true)}
                >
                  + Create Bill
                </button>
              ) : null}
            </form>
          </div>
          {rows.isLoading ? <LoadingState /> : null}
          {rows.isError ? (
            <ErrorState
              title="Unable to load work order bills"
              message="Please try again."
              action={{
                label: "Try again",
                onClick: () => void rows.refetch(),
              }}
            />
          ) : null}
          {billTab === "bills" && rows.data && rows.data.data.length === 0 ? (
            <EmptyState
              title="No work order bills found"
              message="Create a bill from an approved work order."
            />
          ) : null}
          {billTab === "payments" && paymentRows.length === 0 ? (
            <EmptyState
              title="No work order bill payments found"
              message="Recorded bill payments will appear here."
            />
          ) : null}
          {billTab === "bills" && rows.data && rows.data.data.length > 0 ? (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    {columns.map((key) => (
                      <SortableTh key={key} control={tableSort} column={key}>
                        {humanize(key)}
                      </SortableTh>
                    ))}
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.data.data.map((row, index) => (
                    <tr key={String(row.id)}>
                      {columns.map((key) => (
                        <td key={key}>
                          {key === "serialNumber"
                            ? index + 1
                            : key === "netAmount" ||
                                key === "totalPayment" ||
                                key === "dueAmount"
                              ? formatMoneyCompact(row[key])
                              : cellText(row, key)}
                        </td>
                      ))}
                      <td>
                        {moneyNumber(row.dueAmount) > 0 ? (
                          <button
                            className="button button-secondary store-payment-button"
                            type="button"
                            onClick={() => setPayingWorkOrderBill(row)}
                          >
                            Make Payment
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {billTab === "payments" && paymentRows.length > 0 ? (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    {paymentColumns.map((key) => (
                      <SortableTh key={key} control={tableSort} column={key}>
                        {humanize(key)}
                      </SortableTh>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paymentRows.map((row, index) => (
                    <tr key={String(row.id)}>
                      {paymentColumns.map((key) => (
                        <td key={key}>
                          {key === "serialNumber"
                            ? index + 1
                            : key === "amount"
                              ? formatMoneyCompact(row[key])
                              : key === "paymentDate"
                                ? formatRequisitionDate(row[key])
                                : cellText(row, key)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {payingWorkOrderBill ? (
            <PurchasePaymentModal
              row={payingWorkOrderBill}
              submitting={mutation.isPending}
              onClose={() => setPayingWorkOrderBill(null)}
              onConfirm={(amount, mode, bankName) =>
                void recordWorkOrderBillPayment(amount, mode, bankName)
              }
            />
          ) : null}
        </section>
      ) : (
        <form
          className="form-card store-work-order-bill-form"
          onSubmit={(event) => void submit(event)}
        >
          <div className="section-heading">
            <h2>Create Work Order Bill</h2>
            <button
              className="button button-secondary"
              type="button"
              onClick={resetForm}
            >
              Back
            </button>
          </div>
          <div className="form-grid store-work-order-bill-header">
            <Lookup
              label="Work Order"
              value={form.workOrder}
              options={workOrders.data}
              error={
                showErrors && !form.workOrder
                  ? "Work Order is required."
                  : undefined
              }
              onChange={selectWorkOrder}
            />
            <Lookup
              label="Vendor"
              value={form.vendor}
              options={vendors.data}
              error={
                showErrors && !form.vendor ? "Vendor is required." : undefined
              }
              onChange={(vendor) =>
                setForm((current) => ({ ...current, vendor }))
              }
            />
            <Field
              label="Bill Date"
              type="date"
              value={form.billDate}
              error={
                showErrors && !form.billDate
                  ? "Bill Date is required."
                  : undefined
              }
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  billDate: event.target.value,
                }))
              }
            />
            <Field
              label="Due Date"
              type="date"
              value={form.dueDate}
              required={false}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  dueDate: event.target.value,
                }))
              }
            />
          </div>
          {selectedWorkOrder ? (
            <div className="store-work-order-bill-strip">
              <strong>Workorder No: {selectedWorkOrder.label}</strong>
              <strong>Department: {selectedWorkOrder.departmentName ?? "-"}</strong>
              <strong>
                Start: {formatRequisitionDate(selectedWorkOrder.workOrderDate)}
              </strong>
              <strong>
                End: {formatRequisitionDate(selectedWorkOrder.expectedDate)}
              </strong>
            </div>
          ) : null}
          <div className="table-scroll">
            <table className="store-charge-table store-work-order-bill-lines">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Unit</th>
                  <th>Amount</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {lines.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="store-empty-row">
                      Select a work order to load items.
                    </td>
                  </tr>
                ) : (
                  lines.map((line, index) => (
                    <tr key={`${line.type}-${index}`}>
                      <td>{index + 1}</td>
                      <td>{line.type}</td>
                      <td>{line.description || "-"}</td>
                      <td>{line.unit || "-"}</td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          aria-label={`Amount ${index + 1}`}
                          value={line.amount}
                          onChange={(event) =>
                            setLines((current) =>
                              current.map((row, rowIndex) =>
                                rowIndex === index
                                  ? { ...row, amount: event.target.value }
                                  : row,
                              ),
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          value={line.remarks}
                          onChange={(event) =>
                            setLines((current) =>
                              current.map((row, rowIndex) =>
                                rowIndex === index
                                  ? { ...row, remarks: event.target.value }
                                  : row,
                              ),
                            )
                          }
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="store-work-order-bill-body">
            <Field
              label="Remarks"
              value={form.remarks}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  remarks: event.target.value,
                }))
              }
              textarea
              required={false}
            />
            <div className="store-work-order-bill-deductions">
              <h3>Deductions</h3>
              <table className="store-charge-table">
                <thead>
                  <tr>
                    <th>Deduction</th>
                    <th>Amount</th>
                    <th className="store-charge-narrow">#</th>
                  </tr>
                </thead>
                <tbody>
                  {deductions.map((row, index) => (
                    <tr key={row.key}>
                      <td>
                        <input
                          value={row.deduction}
                          onChange={(event) =>
                            updateDeduction(row.key, {
                              deduction: event.target.value,
                            })
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={row.amount}
                          onChange={(event) =>
                            updateDeduction(row.key, {
                              amount: event.target.value,
                            })
                          }
                        />
                      </td>
                      <RowActions
                        removable={deductions.length > 1}
                        isLast={index === deductions.length - 1}
                        onRemove={() =>
                          setDeductions((current) =>
                            current.filter((entry) => entry.key !== row.key),
                          )
                        }
                        onAdd={() =>
                          setDeductions((current) => [
                            ...current,
                            blankWorkOrderBillDeduction(),
                          ])
                        }
                      />
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="store-work-order-bill-summary">
              <h3>Bill Summary</h3>
              <SummaryRow label="Gross Amount" value={grossAmount} />
              <SummaryInput
                label="Miscellaneous Charges"
                mode={form.miscellaneousChargeType}
                value={form.miscellaneousCharge}
                onMode={(miscellaneousChargeType) =>
                  setForm((current) => ({
                    ...current,
                    miscellaneousChargeType,
                  }))
                }
                onValue={(miscellaneousCharge) =>
                  setForm((current) => ({ ...current, miscellaneousCharge }))
                }
              />
              <SummaryInput
                label="Discount"
                mode={form.discountType}
                value={form.discount}
                onMode={(discountType) =>
                  setForm((current) => ({ ...current, discountType }))
                }
                onValue={(discount) =>
                  setForm((current) => ({ ...current, discount }))
                }
              />
              <div className="store-summary-line store-summary-line-tds">
                <span>TDS</span>
                <input
                  placeholder="Select TDS Section Code"
                  value={form.tdsSectionCode}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      tdsSectionCode: event.target.value,
                    }))
                  }
                />
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={form.tdsPercent}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      tdsPercent: event.target.value,
                    }))
                  }
                />
                <input disabled value={tdsAmount.toFixed(2)} />
              </div>
              <div className="store-summary-line store-summary-line-gst">
                <span>GST (%)</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={form.gstPercent}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      gstPercent: event.target.value,
                    }))
                  }
                />
                <input disabled value={gstAmount.toFixed(2)} />
              </div>
              <SummaryRow label="Total Deduction" value={totalDeduction} />
              <SummaryRow label="Net Amount" value={netAmount} />
            </div>
          </div>
          <div className="form-actions">
            <button
              className="button button-primary"
              disabled={mutation.isPending}
              type="submit"
            >
              {mutation.isPending ? "Saving..." : "Create Bill"}
            </button>
            <button
              className="button button-secondary"
              type="button"
              onClick={resetForm}
            >
              Cancel
            </button>
          </div>
          {selectedWorkOrder && lines.length === 0 ? (
            <p className="field-error">
              The selected work order has no billable lines.
            </p>
          ) : null}
        </form>
      )}
    </div>
  );
};

const SummaryRow = ({ label, value }: { label: string; value: number }) => (
  <div className="store-summary-line store-summary-line-total">
    <span>{label}</span>
    <input disabled value={Number(value || 0).toFixed(2)} />
  </div>
);

const SummaryInput = ({
  label,
  mode,
  value,
  onMode,
  onValue,
}: {
  label: string;
  mode: string;
  value: string;
  onMode: (value: string) => void;
  onValue: (value: string) => void;
}) => (
  <div className="store-summary-line store-summary-line-pair">
    <span>{label}</span>
    <select value={mode} onChange={(event) => onMode(event.target.value)}>
      <option value="amount">Rs</option>
      <option value="percentage">%</option>
    </select>
    <input
      type="number"
      min="0"
      step="0.01"
      value={value}
      onChange={(event) => onValue(event.target.value)}
    />
  </div>
);

type Form = ReturnType<typeof initialForm>;
type Update = (
  key: keyof Form,
) => (
  event: React.ChangeEvent<
    HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  >,
) => void;

const MasterFields = ({
  form,
  masterKind,
  categories,
  expenseLedgerHeads,
  update,
  setForm,
  errors = {},
}: {
  form: Form;
  masterKind: StoreMasterKind | undefined;
  categories: StoreLookup[] | undefined;
  expenseLedgerHeads: StoreLookup[] | undefined;
  update: Update;
  setForm: React.Dispatch<React.SetStateAction<Form>>;
  errors?: Record<string, string | undefined> | undefined;
}) =>
  form.kind === "expense-ledger-item" ? (
    <>
      <section className="store-ledger-master-section">
        <h3>Basic Information</h3>
        <div className="store-ledger-master-grid store-ledger-basic-grid">
          <Field
            label="Ledger Item"
            value={form.name}
            onChange={update("name")}
            error={errors.name}
          />
          <Lookup
            label="Ledger Head"
            value={form.parent}
            options={expenseLedgerHeads}
            onChange={(parent) =>
              setForm((current) => ({ ...current, parent }))
            }
            error={errors.parent}
          />
        </div>
      </section>
      <section className="store-ledger-master-section">
        <h3>TDS Configuration</h3>
        <div className="store-ledger-master-grid">
          <Select
            label="TDS Applicable"
            value={form.tdsApplicable}
            options={[
              ["N", "N"],
              ["Y", "Y"],
            ]}
            onChange={(tdsApplicable) =>
              setForm((current) => ({ ...current, tdsApplicable }))
            }
          />
          <Field
            label="TDS Nature of Payment"
            value={form.tdsNatureOfPayment}
            onChange={update("tdsNatureOfPayment")}
            required={false}
            disabled={form.tdsApplicable !== "Y"}
          />
          <Select
            label="TDS Section Code"
            value={form.tdsSectionCode}
            options={[
              ["194C-individual", "194C - Contractor (Individual/HUF)"],
              ["194C-other", "194C - Contractor (Other than Individual/HUF)"],
              ["194I", "194I - Rent"],
              ["194J", "194J - Professional services"],
            ]}
            onChange={(tdsSectionCode) =>
              setForm((current) => ({ ...current, tdsSectionCode }))
            }
            required={false}
            disabled={form.tdsApplicable !== "Y"}
          />
          <Field
            label="TDS Rate (%)"
            type="number"
            min="0"
            max="100"
            step="0.01"
            value={form.tdsRate}
            onChange={update("tdsRate")}
            disabled={form.tdsApplicable !== "Y"}
          />
          <Field
            label="TDS Threshold (Optional)"
            type="number"
            min="0"
            step="0.01"
            value={form.tdsThreshold}
            onChange={update("tdsThreshold")}
            required={false}
            disabled={form.tdsApplicable !== "Y"}
          />
        </div>
      </section>
      <section className="store-ledger-master-section">
        <h3>GST Configuration</h3>
        <div className="store-ledger-master-grid">
          <Select
            label="GST Applicable"
            value={form.gstApplicable}
            options={[
              ["N", "N"],
              ["Y", "Y"],
            ]}
            onChange={(gstApplicable) =>
              setForm((current) => ({ ...current, gstApplicable }))
            }
          />
          <Select
            label="GST Charge Type"
            value={form.gstChargeType}
            options={[
              ["regular", "Regular"],
              ["reverse-charge", "Reverse Charge"],
            ]}
            onChange={(gstChargeType) =>
              setForm((current) => ({ ...current, gstChargeType }))
            }
            required={false}
            disabled={form.gstApplicable !== "Y"}
          />
          <Field
            label="SAC / HSN Code"
            value={form.sacHsnCode}
            onChange={update("sacHsnCode")}
            required={false}
            disabled={form.gstApplicable !== "Y"}
          />
          <Select
            label="GST Rate (%)"
            value={form.gstRate}
            options={[
              ["0", "0%"],
              ["5", "5%"],
              ["12", "12%"],
              ["18", "18%"],
              ["28", "28%"],
            ]}
            onChange={(gstRate) =>
              setForm((current) => ({ ...current, gstRate }))
            }
            disabled={form.gstApplicable !== "Y"}
          />
          <Field
            label="IGST Ledger Name"
            value={form.igstLedgerName}
            onChange={update("igstLedgerName")}
            required={false}
            disabled={form.gstApplicable !== "Y"}
          />
          <Field
            label="CGST Ledger Name"
            value={form.cgstLedgerName}
            onChange={update("cgstLedgerName")}
            required={false}
            disabled={form.gstApplicable !== "Y"}
          />
          <Field
            label="SGST Ledger Name"
            value={form.sgstLedgerName}
            onChange={update("sgstLedgerName")}
            required={false}
            disabled={form.gstApplicable !== "Y"}
          />
          <Field
            label="RCM Output GST Ledger"
            value={form.rcmOutputGstLedger}
            onChange={update("rcmOutputGstLedger")}
            required={false}
            disabled={form.gstApplicable !== "Y"}
          />
        </div>
      </section>
      <section className="store-ledger-master-section">
        <h3>Cost Centre &amp; Misc</h3>
        <div className="store-ledger-master-grid">
          <Select
            label="Cost Centre Applicable (Optional)"
            value={form.costCentreApplicable}
            options={[
              ["N", "N"],
              ["Y", "Y"],
            ]}
            onChange={(costCentreApplicable) =>
              setForm((current) => ({ ...current, costCentreApplicable }))
            }
          />
          <Field
            label="Default Cost Centre (Optional)"
            value={form.defaultCostCentre}
            onChange={update("defaultCostCentre")}
            required={false}
            disabled={form.costCentreApplicable !== "Y"}
          />
          <Field
            label="Opening Balance (Optional)"
            type="number"
            step="0.01"
            value={form.openingBalance}
            onChange={update("openingBalance")}
            required={false}
          />
          <label className="field field-full">
            Description / Notes (Optional)
            <textarea
              value={form.descriptionNotes}
              onChange={update("descriptionNotes")}
              rows={3}
            />
          </label>
        </div>
      </section>
    </>
  ) : (
    <>
      {masterKind && !isCompactWorkOrderMaster(form.kind) ? (
        <label className="field">
          Master type
          <input value={masterTitles[masterKind]} disabled />
        </label>
      ) : !masterKind ? (
        <Select
          label="Master type"
          value={form.kind}
          options={Object.entries(masterTitles)}
          onChange={(kind) =>
            setForm((current) => ({
              ...current,
              kind: kind as StoreMasterKind,
            }))
          }
        />
      ) : null}
      {form.kind === "category" ? (
        <Lookup
          label="Item Category"
          value={form.parent}
          options={categories}
          required={false}
          onChange={(parent) => setForm((current) => ({ ...current, parent }))}
        />
      ) : null}
      <Field
        label={
          isCompactWorkOrderMaster(form.kind)
            ? `${masterTitles[form.kind]} Name`
            : masterTitles[form.kind]
        }
        size="lg"
        value={form.name}
        onChange={update("name")}
        error={errors.name}
      />
      {!isCompactWorkOrderMaster(form.kind) ? (
        <>
          <Field
            label="Code"
            size="sm"
            value={form.code}
            onChange={update("code")}
            required={false}
          />
          <Select
            label="Status"
            size="xs"
            value={form.masterStatus}
            options={[
              ["active", "Active"],
              ["inactive", "Inactive"],
            ]}
            onChange={(masterStatus) =>
              setForm((current) => ({ ...current, masterStatus }))
            }
          />
        </>
      ) : null}
    </>
  );

const VendorFields = ({
  form,
  update,
  uppercaseName = false,
}: {
  form: Form;
  update: Update;
  uppercaseName?: boolean;
}) => (
  <>
    <Field
      label="Vendor Name"
      size="lg"
      value={form.vendorName}
      onChange={(event) => {
        if (uppercaseName)
          event.target.value = event.target.value.toUpperCase();
        update("vendorName")(event);
      }}
    />
    <Field
      label="GSTIN"
      size="sm"
      value={form.gstin}
      onChange={update("gstin")}
      required={false}
    />
    <Field
      label="Enter Vendor Phone no."
      size="sm"
      value={form.phone}
      onChange={update("phone")}
      required={false}
    />
    <Field
      label="Vendor Email"
      type="email"
      size="lg"
      value={form.email}
      onChange={update("email")}
      required={false}
    />
    <Field
      label="Contact Person name"
      value={form.contactPersonName}
      onChange={update("contactPersonName")}
      required={false}
    />
    <label className="field field-full">
      Vendor Address
      <textarea
        value={form.address}
        onChange={update("address")}
        rows={3}
        maxLength={500}
      />
    </label>
    <Field
      label="Pincode"
      size="xs"
      value={form.pinCode}
      onChange={update("pinCode")}
      required={false}
    />
  </>
);

const WorkOrderVendorFields = ({
  form,
  update,
  setForm,
}: {
  form: Form;
  update: Update;
  setForm: React.Dispatch<React.SetStateAction<Form>>;
}) => (
  <>
    <Field
      label="Vendor Name"
      value={form.vendorName}
      onChange={update("vendorName")}
    />
    <Field
      label="Contact Person"
      value={form.contactPersonName}
      onChange={update("contactPersonName")}
      required={false}
    />
    <Field
      label="Contact Mobile"
      value={form.phone}
      onChange={update("phone")}
      required={false}
    />
    <Field
      label="Contact Email"
      type="email"
      value={form.email}
      onChange={update("email")}
      required={false}
    />
    <Field
      label="GSTIN"
      value={form.gstin}
      onChange={update("gstin")}
      required={false}
    />
    <Select
      label="Status"
      value={form.masterStatus}
      options={[
        ["active", "Active"],
        ["inactive", "Inactive"],
      ]}
      onChange={(masterStatus) =>
        setForm((current) => ({ ...current, masterStatus }))
      }
    />
    <label className="field field-full">
      Address
      <textarea
        value={form.address}
        onChange={update("address")}
        rows={3}
        maxLength={500}
      />
    </label>
  </>
);

const ItemFields = ({
  isPharmacy,
  form,
  itemTypes,
  categories,
  brands,
  stores,
  units,
  update,
  setForm,
  errors = {},
}: {
  isPharmacy: boolean;
  form: Form;
  itemTypes: StoreLookup[] | undefined;
  categories: StoreLookup[] | undefined;
  brands: StoreLookup[] | undefined;
  stores: StoreLookup[] | undefined;
  units: StoreLookup[] | undefined;
  update: Update;
  setForm: React.Dispatch<React.SetStateAction<Form>>;
  errors?: Record<string, string | undefined> | undefined;
}) =>
  isPharmacy ? (
    <>
      <Field
        label="Medicine Name *"
        size="lg"
        value={form.name}
        error={errors.name}
        onChange={update("name")}
      />
      <Lookup
        label="Medicine Category *"
        value={form.category}
        options={categories}
        error={errors.category}
        onChange={(category) =>
          setForm((current) => ({ ...current, category }))
        }
      />
      <Field
        label="Medicine Company"
        value={form.company}
        required={false}
        onChange={update("company")}
      />
      <Field
        label="Medicine Composition"
        required={false}
        value={form.composition}
        onChange={update("composition")}
      />
      <Field
        label="Medicine Group"
        value={form.group}
        required={false}
        onChange={update("group")}
      />
      <Lookup
        label="Unit *"
        size="sm"
        value={form.unit}
        options={units}
        error={errors.unit}
        onChange={(unit) => setForm((current) => ({ ...current, unit }))}
      />
      <Lookup
        label="Sub Unit *"
        size="sm"
        value={form.subUnit}
        options={units}
        error={errors.subUnit}
        onChange={(subUnit) => setForm((current) => ({ ...current, subUnit }))}
      />
      <Field
        label="Enter Min Level *"
        type="number"
        min="0"
        size="xs"
        value={form.reorderLevel}
        error={errors.reorderLevel}
        onChange={update("reorderLevel")}
      />
      <Field
        label="Price / Unit"
        type="number"
        min="0"
        required={false}
        size="sm"
        value={form.unitRate}
        onChange={update("unitRate")}
      />
      <Field
        label="1 Unit = How many Sub Unit?"
        type="number"
        min="1"
        required={false}
        size="xs"
        value={form.subUnitsPerUnit}
        onChange={update("subUnitsPerUnit")}
      />
    </>
  ) : (
    <>
      <Lookup
        label="Item Type"
        value={form.type}
        options={itemTypes}
        error={errors.type}
        onChange={(type) => setForm((current) => ({ ...current, type }))}
      />
      <Lookup
        label="Item Category"
        value={form.category}
        options={categories}
        error={errors.category}
        onChange={(category) =>
          setForm((current) => ({ ...current, category }))
        }
      />
      <Lookup
        label="Item Sub Category"
        value={form.subCategory}
        options={categories}
        required={false}
        onChange={(subCategory) =>
          setForm((current) => ({ ...current, subCategory }))
        }
      />
      <Field
        label="Item Name"
        size="lg"
        value={form.name}
        onChange={update("name")}
      />
      <Field
        label="Low Level"
        type="number"
        min="0"
        size="xs"
        value={form.reorderLevel}
        onChange={update("reorderLevel")}
      />
      <Lookup
        label="Company"
        value={form.brand}
        options={brands}
        required={false}
        onChange={(brand) => setForm((current) => ({ ...current, brand }))}
      />
      <Lookup
        label="Stored"
        value={form.store}
        options={stores}
        required={false}
        onChange={(store) => setForm((current) => ({ ...current, store }))}
      />
      <Field
        label="HSN or SAC No"
        size="sm"
        value={form.hsnSacNumber}
        onChange={update("hsnSacNumber")}
        required={false}
      />
      <Lookup
        label="Item Unit"
        size="sm"
        value={form.unit}
        options={units}
        error={errors.unit}
        onChange={(unit) => setForm((current) => ({ ...current, unit }))}
      />
      <Field
        label="How many Sub Unit in 1 Unit?"
        type="number"
        min="1"
        size="xs"
        value={form.subUnitsPerUnit}
        onChange={update("subUnitsPerUnit")}
      />
      <Lookup
        label="Item Sub Unit"
        size="sm"
        value={form.subUnit}
        options={units}
        error={errors.subUnit}
        onChange={(subUnit) => setForm((current) => ({ ...current, subUnit }))}
      />
    </>
  );

const PurchaseHeaderFields = ({
  form,
  vendors,
  stores,
  showStore = true,
  purchaseOrders,
  update,
  setForm,
  updateInspectionRequired,
  errors = {},
}: {
  form: Form;
  vendors: StoreLookup[] | undefined;
  stores: StoreLookup[] | undefined;
  showStore?: boolean;
  purchaseOrders: StoreLookup[] | undefined;
  update: Update;
  setForm: React.Dispatch<React.SetStateAction<Form>>;
  updateInspectionRequired: (value: string) => void;
  errors?: Record<string, string | undefined> | undefined;
}) => (
  <>
    <Field
      label="Date"
      type="date"
      size="sm"
      value={form.purchaseDate}
      onChange={update("purchaseDate")}
    />
    <Select
      label="Purchase Type"
      size="sm"
      value={form.purchaseType}
      options={[
        ["direct", "Direct"],
        ["indirect", "Against Purchase Order"],
      ]}
      onChange={(purchaseType) =>
        setForm((current) => ({ ...current, purchaseType }))
      }
    />
    {form.purchaseType === "indirect" ? (
      <Lookup
        label="Purchase Order Id"
        value={form.purchaseOrder}
        options={purchaseOrders}
        error={errors.purchaseOrder}
        onChange={(purchaseOrder) => {
          const selected = purchaseOrders?.find(
            (entry) => entry.id === purchaseOrder,
          );
          setForm((current) => ({
            ...current,
            purchaseOrder,
            vendor: selected?.vendor ?? current.vendor,
            store: selected?.store ?? current.store,
          }));
        }}
      />
    ) : null}
    <Lookup
      label="Vendor"
      size="lg"
      value={form.vendor}
      options={vendors}
      error={errors.vendor}
      required={false}
      onChange={(vendor) =>
        setForm((current) => ({ ...current, vendor, purchaseOrder: "" }))
      }
    />
    {showStore ? (
      <Lookup
        label="Store"
        value={form.store}
        options={stores}
        required={false}
        onChange={(store) => setForm((current) => ({ ...current, store }))}
      />
    ) : null}
    <Field
      label="Invoice No"
      size="sm"
      value={form.invoiceNumber}
      onChange={update("invoiceNumber")}
      error={errors.invoiceNumber}
    />
    <Field
      label="Invoice Date"
      type="datetime-local"
      value={form.invoiceDateTime}
      onChange={update("invoiceDateTime")}
      required={false}
    />
    <Select
      label="Inspection Required"
      size="sm"
      value={form.inspectionRequired}
      options={[
        ["false", "No"],
        ["true", "Yes"],
      ]}
      onChange={updateInspectionRequired}
    />
    {form.purchaseType === "indirect" ? (
      <Select
        label="GRN Status"
        size="xs"
        value={form.grnStatus}
        options={[
          ["incomplete", "Incomplete"],
          ["complete", "Completed"],
        ]}
        onChange={(grnStatus) =>
          setForm((current) => ({ ...current, grnStatus }))
        }
      />
    ) : null}
  </>
);

const WorkOrderHeaderFields = ({
  form,
  workOrderNumber,
  departments,
  vendors,
  update,
  setForm,
  errors = {},
}: {
  form: Form;
  workOrderNumber: string;
  departments: StoreLookup[] | undefined;
  vendors: StoreLookup[] | undefined;
  update: Update;
  setForm: React.Dispatch<React.SetStateAction<Form>>;
  errors?: Record<string, string | undefined> | undefined;
}) => (
  <>
    <Field
      label="Workorder No"
      value={workOrderNumber}
      disabled
      required={false}
    />
    <Lookup
      label="Department"
      value={form.department}
      options={departments}
      error={errors.department}
      onChange={(department) =>
        setForm((current) => ({ ...current, department }))
      }
    />
    <Lookup
      label="Vendor"
      value={form.vendor}
      options={vendors}
      error={errors.vendor}
      onChange={(vendor) => setForm((current) => ({ ...current, vendor }))}
    />
    <Field
      label="Start Date"
      type="date"
      size="sm"
      value={form.workOrderDate}
      onChange={update("workOrderDate")}
    />
    <Field
      label="End Date"
      type="date"
      size="sm"
      value={form.expectedDate}
      onChange={update("expectedDate")}
      required={false}
    />
  </>
);

const ViewText = ({
  value,
  note = false,
}: {
  value: unknown;
  note?: boolean;
}) => {
  const label = scalarText(value);
  return label === "-" ? (
    label
  ) : (
    <TruncatedText
      text={label}
      className={note ? "store-view-note-text" : "store-view-cell-text"}
    />
  );
};

const ExpenseLedgerItemViewModal = ({
  row,
  onClose,
}: {
  row: Record<string, unknown>;
  onClose: () => void;
}) => (
  <Modal
    open
    onClose={onClose}
    labelledBy="expense-ledger-item-view-title"
    className="store-ledger-view-modal"
  >
    <header>
      <div>
        <p>Expense Ledger Item</p>
        <h2 id="expense-ledger-item-view-title">{text(row, "name")}</h2>
      </div>
      <button type="button" onClick={onClose} aria-label="Close">
        ×
      </button>
    </header>
    <div className="store-view-modal-body">
      <table>
        <tbody>
          <tr>
            <td>Ledger Head</td>
            <td>
              <ViewText value={text(row, "parent")} />
            </td>
            <td>Opening Balance</td>
            <td>{moneyNumber(row.openingBalance).toFixed(2)}</td>
          </tr>
          <tr>
            <td>TDS Applicable</td>
            <td>{scalarText(row.tdsApplicable)}</td>
            <td>TDS Nature of Payment</td>
            <td>
              <ViewText value={row.tdsNatureOfPayment} />
            </td>
          </tr>
          <tr>
            <td>TDS Section Code</td>
            <td>
              <ViewText value={row.tdsSectionCode} />
            </td>
            <td>TDS Rate (%)</td>
            <td>{scalarText(row.tdsRate)}</td>
          </tr>
          <tr>
            <td>TDS Threshold</td>
            <td>{scalarText(row.tdsThreshold)}</td>
            <td>GST Applicable</td>
            <td>{scalarText(row.gstApplicable)}</td>
          </tr>
          <tr>
            <td>GST Charge Type</td>
            <td>
              <ViewText value={row.gstChargeType} />
            </td>
            <td>SAC / HSN Code</td>
            <td>
              <ViewText value={row.sacHsnCode} />
            </td>
          </tr>
          <tr>
            <td>GST Rate (%)</td>
            <td>{scalarText(row.gstRate)}</td>
            <td>IGST Ledger Name</td>
            <td>
              <ViewText value={row.igstLedgerName} />
            </td>
          </tr>
          <tr>
            <td>CGST Ledger Name</td>
            <td>
              <ViewText value={row.cgstLedgerName} />
            </td>
            <td>SGST Ledger Name</td>
            <td>
              <ViewText value={row.sgstLedgerName} />
            </td>
          </tr>
          <tr>
            <td>RCM Output GST Ledger</td>
            <td>
              <ViewText value={row.rcmOutputGstLedger} />
            </td>
            <td>Cost Centre Applicable</td>
            <td>{scalarText(row.costCentreApplicable)}</td>
          </tr>
          <tr>
            <td>Default Cost Centre</td>
            <td>
              <ViewText value={row.defaultCostCentre} />
            </td>
            <td>Description / Notes</td>
            <td>
              <ViewText value={row.descriptionNotes} note />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <footer className="bed-modal-actions">
      <button
        className="button button-secondary"
        type="button"
        onClick={onClose}
      >
        Close
      </button>
    </footer>
  </Modal>
);

const RequisitionViewModal = ({
  row,
  onClose,
}: {
  row: Record<string, unknown>;
  onClose: () => void;
}) => {
  const lines = Array.isArray(row.lines)
    ? (row.lines as Record<string, unknown>[])
    : [];
  const grandTotal = lines.reduce((sum, line) => {
    const item =
      typeof line.item === "object" && line.item
        ? (line.item as Record<string, unknown>)
        : {};
    const subUnitsPerUnit = Math.max(1, Number(item.subUnitsPerUnit ?? 1));
    return (
      sum +
      (Number(line.requestedQuantity ?? 0) / subUnitsPerUnit) *
        Number(line.rate ?? 0)
    );
  }, 0);
  return (
    <Modal
      open
      onClose={onClose}
      labelledBy="requisition-view-title"
      className="store-requisition-view-modal"
    >
      <header>
        <div>
          <p>Store</p>
          <h2 id="requisition-view-title">
            Requisition {text(row, "requisitionNumber")}
          </h2>
        </div>
        <button type="button" onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>
      <div className="store-view-modal-body">
        <table>
          <tbody>
            <tr>
              <td>Date</td>
              <td>{formatRequisitionDate(row.requisitionDate)}</td>
              <td>
                {row.requisitionFor === "patient" ? "Patient" : "Department"}
              </td>
              <td>
                <ViewText value={requisitionDestination(row)} />
              </td>
            </tr>
            <tr>
              <td>Status</td>
              <td>
                <RequisitionStatusBadge status={row.status} />
              </td>
              <td>Total Items</td>
              <td>{lines.length}</td>
            </tr>
            {row.approvedAt ? (
              <tr>
                <td>Approved At</td>
                <td colSpan={3}>{formatRequisitionDate(row.approvedAt)}</td>
              </tr>
            ) : null}
            {row.rejectedAt ? (
              <tr>
                <td>Rejected By</td>
                <td>
                  <ViewText value={text(row, "rejectedBy")} />
                </td>
                <td>Rejected At</td>
                <td>{formatRequisitionDate(row.rejectedAt)}</td>
              </tr>
            ) : null}
            {row.rejectionNote ? (
              <tr>
                <td>Rejection Note</td>
                <td colSpan={3}>
                  <ViewText value={row.rejectionNote} note />
                </td>
              </tr>
            ) : null}
            {row.note ? (
              <tr>
                <td>Note</td>
                <td colSpan={3}>
                  <ViewText value={row.note} note />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Requested Qty</th>
                <th>Issued Qty</th>
                <th>Rejected Qty</th>
                <th>Rate (₹)</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, index) => (
                <tr key={String(line._id ?? index)}>
                  <td>
                    <ViewText value={line.itemName} />
                  </td>
                  <td>{scalarText(line.requestedQuantity)}</td>
                  <td>{scalarText(line.issuedQuantity)}</td>
                  <td>{scalarText(line.rejectedQuantity)}</td>
                  <td>{Number(line.rate ?? 0).toFixed(2)}</td>
                  <td>
                    <ViewText value={line.remarks} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={5}>Grand Total</td>
                <td>{formatMoney(grandTotal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </Modal>
  );
};

const PurchaseOrderViewModal = ({
  row: listRow,
  onClose,
}: {
  row: Record<string, unknown>;
  onClose: () => void;
}) => {
  // List rows do not carry unit/sub-unit names; the detail endpoint enriches them.
  const detail = useStorePurchaseOrder(String(listRow.id ?? listRow._id ?? ""));
  const row = detail.data ?? listRow;
  const lines = Array.isArray(row.lines)
    ? (row.lines as Record<string, unknown>[])
    : [];
  const requisitions = Array.isArray(row.requisitions)
    ? row.requisitions
        .map((entry) => text({ entry }, "entry"))
        .filter((label) => label !== "-")
    : [];
  const grandTotal = moneyNumber(
    row.total ?? lines.reduce((sum, line) => sum + moneyNumber(line.amount), 0),
  );
  return (
    <Modal
      open
      onClose={onClose}
      labelledBy="purchase-order-view-title"
      className="store-requisition-view-modal"
    >
      <header>
        <div>
          <p>Store</p>
          <h2 id="purchase-order-view-title">
            Purchase Order {text(row, "purchaseOrderNumber")}
          </h2>
        </div>
        <button type="button" onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>
      <div className="store-view-modal-body">
        <table>
          <tbody>
            <tr>
              <td>Date</td>
              <td>{formatRequisitionDate(row.orderDate)}</td>
              <td>Vendor</td>
              <td>
                <ViewText value={text(row, "vendor")} />
              </td>
            </tr>
            <tr>
              <td>Status</td>
              <td>
                <PurchaseOrderStatusBadge status={row.status} />
              </td>
              <td>Requisition</td>
              <td>
                <ViewText value={requisitions.join(", ") || "-"} />
              </td>
            </tr>
            {row.approvedAt ? (
              <tr>
                <td>Approved At</td>
                <td colSpan={3}>{formatRequisitionDate(row.approvedAt)}</td>
              </tr>
            ) : null}
            {row.rejectedAt ? (
              <tr>
                <td>Rejected By</td>
                <td>
                  <ViewText value={text(row, "rejectedBy")} />
                </td>
                <td>Rejected At</td>
                <td>{formatRequisitionDate(row.rejectedAt)}</td>
              </tr>
            ) : null}
            {row.rejectionNote ? (
              <tr>
                <td>Rejection Note</td>
                <td colSpan={3}>
                  <ViewText value={row.rejectionNote} note />
                </td>
              </tr>
            ) : null}
            {row.note ? (
              <tr>
                <td>Note</td>
                <td colSpan={3}>
                  <ViewText value={row.note} note />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Quantity</th>
                <th>Unit Qty</th>
                <th>Sub Unit Qty</th>
                <th>Rate</th>
                <th>Discount (₹)</th>
                <th>Tax (₹)</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, index) => {
                const item =
                  line.item && typeof line.item === "object"
                    ? (line.item as Record<string, unknown>)
                    : {};
                const gross =
                  (moneyNumber(line.quantity) /
                    (Number(
                      item.subUnitsPerUnit ?? line.subUnitsPerUnit ?? 1,
                    ) || 1)) *
                  moneyNumber(line.rate);
                const discountAmount =
                  line.discountType === "amount"
                    ? Math.min(gross, moneyNumber(line.discountAmount))
                    : (gross * moneyNumber(line.discountPercent)) / 100;
                const taxAmount = Number(
                  (
                    ((gross - discountAmount) * moneyNumber(line.taxPercent)) /
                    100
                  ).toFixed(2),
                );
                const unit =
                  line.unit && typeof line.unit === "object"
                    ? (line.unit as Record<string, unknown>)
                    : item.unit && typeof item.unit === "object"
                      ? (item.unit as Record<string, unknown>)
                      : {};
                const subUnit =
                  line.subUnit && typeof line.subUnit === "object"
                    ? (line.subUnit as Record<string, unknown>)
                    : item.subUnit && typeof item.subUnit === "object"
                      ? (item.subUnit as Record<string, unknown>)
                      : {};
                const unitName = String(
                  line.unitName ?? unit.name ?? item.unitName ?? "",
                ).trim();
                const subUnitName = String(
                  line.subUnitName ?? subUnit.name ?? item.subUnitName ?? "",
                ).trim();
                return (
                  <tr key={String(line._id ?? index)}>
                    <td>
                      <ViewText value={line.itemName} />
                    </td>
                    <td>
                      {scalarText(line.quantity)}
                      {subUnitName || unitName
                        ? ` ${subUnitName || unitName}`
                        : ""}
                    </td>
                    <td>
                      {scalarText(line.unitQuantity)}
                      {unitName ? ` ${unitName}` : ""}
                    </td>
                    <td>
                      {scalarText(line.subUnitQuantity)}
                      {subUnitName ? ` ${subUnitName}` : ""}
                    </td>
                    <td>{Number(line.rate ?? 0).toFixed(2)}</td>
                    <td>{formatMoney(discountAmount)}</td>
                    <td>{formatMoney(taxAmount)}</td>
                    <td>{formatMoney(moneyNumber(line.amount))}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={7}>Grand Total</td>
                <td>{formatMoney(grandTotal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </Modal>
  );
};

const WorkOrderViewModal = ({
  row,
  onClose,
}: {
  row: Record<string, unknown>;
  onClose: () => void;
}) => {
  const lines = Array.isArray(row.lines)
    ? (row.lines as Record<string, unknown>[])
    : [];
  const grandTotal = moneyNumber(
    row.total ?? lines.reduce((sum, line) => sum + moneyNumber(line.amount), 0),
  );
  return (
    <Modal
      open
      onClose={onClose}
      labelledBy="work-order-view-title"
      className="store-requisition-view-modal"
    >
      <header>
        <div>
          <p>Store</p>
          <h2 id="work-order-view-title">
            Work Order {text(row, "workOrderNumber")}
          </h2>
        </div>
        <button type="button" onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>
      <div className="store-view-modal-body">
        <table>
          <tbody>
            <tr>
              <td>Start Date</td>
              <td>{formatRequisitionDate(row.workOrderDate)}</td>
              <td>End Date</td>
              <td>{formatRequisitionDate(row.expectedDate)}</td>
            </tr>
            <tr>
              <td>Department</td>
              <td><ViewText value={text(row, "department")} /></td>
              <td>Vendor</td>
              <td><ViewText value={text(row, "vendor")} /></td>
            </tr>
            <tr>
              <td>Priority</td>
              <td><ViewText value={row.priority} /></td>
              <td>Status</td>
              <td><WorkOrderStatusBadge status={row.status} /></td>
            </tr>
            <tr>
              <td>Subject</td>
              <td colSpan={3}><ViewText value={row.subject} note /></td>
            </tr>
            {row.approvedAt ? (
              <tr>
                <td>Approved By</td>
                <td><ViewText value={text(row, "approvedBy")} /></td>
                <td>Approved At</td>
                <td>{formatRequisitionDate(row.approvedAt)}</td>
              </tr>
            ) : null}
            {row.rejectionNote ? (
              <tr>
                <td>Rejection Note</td>
                <td colSpan={3}><ViewText value={row.rejectionNote} note /></td>
              </tr>
            ) : null}
            {row.note ? (
              <tr>
                <td>Note</td>
                <td colSpan={3}><ViewText value={row.note} note /></td>
              </tr>
            ) : null}
          </tbody>
        </table>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Type</th>
                <th>Description</th>
                <th>Unit</th>
                <th>Quantity</th>
                <th>Rate</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, index) => (
                <tr key={String(line._id ?? index)}>
                  <td>{index + 1}</td>
                  <td><ViewText value={line.itemName || text(line, "item")} /></td>
                  <td><ViewText value={line.remarks} /></td>
                  <td><ViewText value={text(line, "unit")} /></td>
                  <td>{scalarText(line.quantity)}</td>
                  <td>{formatMoney(moneyNumber(line.rate))}</td>
                  <td>{formatMoney(moneyNumber(line.amount))}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={6}>Grand Total</td>
                <td>{formatMoney(grandTotal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </Modal>
  );
};

const IssueViewModal = ({
  row,
  hideBatch = false,
  onClose,
}: {
  row: Record<string, unknown>;
  hideBatch?: boolean;
  onClose: () => void;
}) => {
  const lines = Array.isArray(row.lines)
    ? (row.lines as Record<string, unknown>[])
    : [];
  return (
    <Modal
      open
      onClose={onClose}
      labelledBy="issue-view-title"
      className="store-requisition-view-modal"
    >
      <header>
        <div>
          <p>Store</p>
          <h2 id="issue-view-title">Issue {text(row, "issueNumber")}</h2>
        </div>
        <button type="button" onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>
      <div className="store-view-modal-body">
        <table>
          <tbody>
            <tr>
              <td>Date</td>
              <td>{formatRequisitionDate(row.issueDate)}</td>
              <td>Department</td>
              <td>{text(row, "department")}</td>
            </tr>
            <tr>
              <td>Requisition</td>
              <td>{text(row, "requisition")}</td>
              <td>Status</td>
              <td>
                <PurchaseBadge
                  field="completionStatus"
                  value={row.completionStatus}
                />
              </td>
            </tr>
            <tr>
              <td>Total Items</td>
              <td>{lines.length}</td>
              <td>Total</td>
              <td>{formatMoneyCompact(row.total)}</td>
            </tr>
            {row.note ? (
              <tr>
                <td>Note</td>
                <td colSpan={3}>{scalarText(row.note)}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Item</th>
                {hideBatch ? null : <th>Batch</th>}
                <th>Expiry</th>
                <th>Qty</th>
                <th>Unit Qty</th>
                <th>Sub Unit Qty</th>
                <th>Rate (₹)</th>
                <th>Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, index) => (
                <tr key={String(line._id ?? index)}>
                  <td>{scalarText(line.itemName)}</td>
                  {hideBatch ? null : (
                    <td>
                      {line.batchNumber ? scalarText(line.batchNumber) : "-"}
                    </td>
                  )}
                  <td>
                    {line.expiryDate
                      ? formatRequisitionDate(line.expiryDate)
                      : "-"}
                  </td>
                  <td>{scalarText(line.quantity)}</td>
                  <td>{scalarText(line.unitQuantity)}</td>
                  <td>{scalarText(line.subUnitQuantity)}</td>
                  <td>{Number(line.rate ?? 0).toFixed(2)}</td>
                  <td>{Number(line.amount ?? 0).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Modal>
  );
};

const PurchaseDetailPage = ({
  row,
  onClose,
  onMakePayment,
}: {
  row: Record<string, unknown>;
  onClose: () => void;
  onMakePayment: () => void;
}) => {
  const lines = Array.isArray(row.lines)
    ? (row.lines as Record<string, unknown>[])
    : [];
  const payments = Array.isArray(row.payments)
    ? (row.payments as Record<string, unknown>[])
    : [];
  const payable = moneyNumber(row.total);
  const paid = moneyNumber(
    row.totalPayment ??
      payments.reduce((sum, payment) => sum + moneyNumber(payment.amount), 0),
  );
  const due = moneyNumber(row.dueAmount ?? Math.max(0, payable - paid));
  const cgst = moneyNumber(row.totalCgstAmount);
  const sgst = moneyNumber(row.totalSgstAmount);
  const igst = moneyNumber(row.totalIgstAmount);
  return (
    <section className="form-card store-complete-form compact-form">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Store</p>
          <h2>Purchase {text(row, "purchaseNumber")}</h2>
        </div>
        <button
          className="button button-secondary"
          type="button"
          onClick={onClose}
        >
          Back
        </button>
      </div>
      <div className="store-view-modal-body">
        <table>
          <tbody>
            <tr>
              <td>Purchase Date</td>
              <td>{formatRequisitionDateTime(row.purchaseDate)}</td>
              <td>Vendor</td>
              <td>
                <ViewText value={text(row, "vendor")} />
              </td>
            </tr>
            <tr>
              <td>Generated By</td>
              <td>
                <ViewText value={text(row, "generatedBy")} />
              </td>
              <td>Purchase Order</td>
              <td>
                <ViewText value={text(row, "purchaseOrder")} />
              </td>
            </tr>
            <tr>
              <td>Invoice No</td>
              <td>
                <ViewText value={text(row, "invoiceNumber")} />
              </td>
              <td>Invoice Date</td>
              <td>{formatRequisitionDateTime(row.invoiceDateTime)}</td>
            </tr>
            <tr>
              <td>Purchase Type</td>
              <td>
                <PurchaseBadge field="purchaseType" value={row.purchaseType} />
              </td>
              <td>GRN Status</td>
              <td>
                <PurchaseBadge field="grnStatus" value={row.grnStatus} />
              </td>
            </tr>
            <tr>
              <td>Inspection Required</td>
              <td>{boolText(row.inspectionRequired)}</td>
              <td>Store</td>
              <td>
                <ViewText value={text(row, "store")} />
              </td>
            </tr>
          </tbody>
        </table>
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Item Name</th>
                <th>Exp. Date</th>
                <th>Quantity</th>
                <th>Test Qty</th>
                <th>Rate</th>
                <th>Dis Amt</th>
                <th>Dis %</th>
                <th>MRP</th>
                <th>CGST %</th>
                <th>SGST %</th>
                <th>IGST %</th>
                <th>Total GST</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line, index) => {
                const totalGst =
                  moneyNumber(line.cgstAmount) +
                  moneyNumber(line.sgstAmount) +
                  moneyNumber(line.igstAmount);
                return (
                  <tr key={String(line._id ?? index)}>
                    <td>{index + 1}</td>
                    <td>
                      <ViewText value={line.itemName} />
                    </td>
                    <td>
                      {line.expiryDate
                        ? formatRequisitionDate(line.expiryDate)
                        : "-"}
                    </td>
                    <td>{scalarText(line.quantity)}</td>
                    <td>{scalarText(line.testQuantity ?? 0)}</td>
                    <td>{formatMoney(moneyNumber(line.rate))}</td>
                    <td>{formatMoney(moneyNumber(line.discountAmount))}</td>
                    <td>{moneyNumber(line.discountPercent).toFixed(2)}</td>
                    <td>{formatMoney(moneyNumber(line.mrp))}</td>
                    <td>{moneyNumber(line.cgstPercent).toFixed(2)}</td>
                    <td>{moneyNumber(line.sgstPercent).toFixed(2)}</td>
                    <td>{moneyNumber(line.igstPercent).toFixed(2)}</td>
                    <td>{formatMoney(totalGst)}</td>
                    <td>{formatMoney(moneyNumber(line.amount))}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={13}>Grand Total</td>
                <td>{formatMoney(payable)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
        <div className="store-summary-row">
          <div className="store-summary-notes">
            <p>
              <strong>Note:</strong> <ViewText value={row.note} note />
            </p>
            <div className="store-totals-list">
              <div>
                <span>Payable Amount</span>
                <strong>{formatMoney(payable)}</strong>
              </div>
              <div>
                <span>Payment</span>
                <strong>{formatMoney(paid)}</strong>
              </div>
              <div>
                <span>Due</span>
                <strong>{formatMoney(due)}</strong>
              </div>
            </div>
            <button
              className="button button-primary store-payment-button"
              type="button"
              disabled={due <= 0}
              onClick={onMakePayment}
            >
              Make Payment
            </button>
            <div className="store-payment-history">
              <h3>Payment</h3>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Date</th>
                      <th>Mode</th>
                      <th>Bank</th>
                      <th>Received By</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.length > 0 ? (
                      payments.map((payment, index) => (
                        <tr key={String(payment._id ?? index)}>
                          <td>{index + 1}</td>
                          <td>{formatRequisitionDateTime(payment.date)}</td>
                          <td>{scalarText(payment.mode)}</td>
                          <td>
                            {payment.bankName
                              ? scalarText(payment.bankName)
                              : "-"}
                          </td>
                          <td>{text(payment, "receivedBy")}</td>
                          <td>{formatMoney(moneyNumber(payment.amount))}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6}>No payment recorded.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <div className="store-totals-list">
            <div>
              <span>Sub Total</span>
              <strong>{formatMoney(moneyNumber(row.subTotal))}</strong>
            </div>
            <div>
              <span>CGST</span>
              <strong>{formatMoney(cgst)}</strong>
            </div>
            <div>
              <span>SGST</span>
              <strong>{formatMoney(sgst)}</strong>
            </div>
            <div>
              <span>IGST</span>
              <strong>{formatMoney(igst)}</strong>
            </div>
            <div>
              <span>Discount</span>
              <strong>
                {formatMoney(
                  moneyNumber(row.appliedDiscountAmount) +
                    moneyNumber(row.lineDiscountAmount),
                )}
              </strong>
            </div>
            <div>
              <span>Total</span>
              <strong>{formatMoney(payable)}</strong>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const PurchasePaymentModal = ({
  row,
  submitting,
  onClose,
  onConfirm,
}: {
  row: Record<string, unknown>;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (amount: number, mode: string, bankName: string) => void;
}) => {
  const due = moneyNumber(
    row.dueAmount ??
      Math.max(0, moneyNumber(row.total) - moneyNumber(row.totalPayment)),
  );
  const [amount, setAmount] = useState(due > 0 ? due.toFixed(2) : "0");
  const [mode, setMode] = useState("Cash");
  const [bankName, setBankName] = useState("");
  const [touched, setTouched] = useState(false);
  const paymentAmount = moneyNumber(amount);
  const error =
    touched && (paymentAmount <= 0 || paymentAmount > due)
      ? `Enter an amount up to ${formatMoney(due)}.`
      : undefined;
  const submit = () => {
    setTouched(true);
    if (paymentAmount <= 0 || paymentAmount > due) return;
    onConfirm(paymentAmount, mode, bankName);
  };
  return (
    <Modal
      open
      onClose={onClose}
      labelledBy="purchase-payment-title"
      className="store-requisition-reject-modal"
    >
      <header>
        <div>
          <p>Store</p>
          <h2 id="purchase-payment-title">Make Payment</h2>
        </div>
        <button type="button" onClick={onClose} aria-label="Close">
          x
        </button>
      </header>
      <div className="store-view-modal-body">
        <table>
          <tbody>
            <tr>
              <td>{row.billNumber ? "Bill" : "Purchase"}</td>
              <td>
                {row.billNumber
                  ? text(row, "billNumber")
                  : text(row, "purchaseNumber")}
              </td>
              <td>Due</td>
              <td>{formatMoney(due)}</td>
            </tr>
          </tbody>
        </table>
        <div className="form-grid">
          <Field
            label="Amount"
            type="number"
            size="sm"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            error={error}
          />
          <Select
            label="Payment Mode"
            size="sm"
            value={mode}
            options={[
              "Cash",
              "UPI",
              "Transfer to Bank Account",
              "Cheque",
              "Online",
              "Other",
            ].map((entry) => [entry, entry])}
            onChange={setMode}
          />
          <Field
            label="Bank Name"
            size="lg"
            value={bankName}
            onChange={(event) => setBankName(event.target.value)}
            required={false}
          />
        </div>
        <div className="form-actions">
          <button
            className="button button-secondary"
            type="button"
            disabled={submitting}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="button button-primary"
            type="button"
            disabled={submitting}
            onClick={submit}
          >
            {submitting ? "Saving..." : "Save Payment"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

const RejectModal = ({
  title,
  submitting,
  onClose,
  onConfirm,
}: {
  title: string;
  submitting: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}) => {
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const trimmed = reason.trim();
  const submit = () => {
    setTouched(true);
    if (!trimmed) return;
    onConfirm(trimmed);
  };
  return (
    <Modal
      open
      onClose={onClose}
      labelledBy="store-reject-title"
      className="store-requisition-reject-modal"
    >
      <header>
        <div>
          <p>Store</p>
          <h2 id="store-reject-title">{title}</h2>
        </div>
        <button type="button" onClick={onClose} aria-label="Close">
          ×
        </button>
      </header>
      <div className="store-view-modal-body">
        <Field
          label="Reason for rejection"
          textarea
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          error={
            touched && !trimmed ? "A rejection reason is required." : undefined
          }
        />
        <div className="form-actions">
          <button
            className="button button-secondary"
            type="button"
            disabled={submitting}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="button button-danger"
            type="button"
            disabled={submitting}
            onClick={submit}
          >
            {submitting ? "Rejecting…" : "Reject"}
          </button>
        </div>
      </div>
    </Modal>
  );
};

type LineTableProps = {
  lines: LineDraft[];
  items: StoreLookup[] | undefined;
  updateLine: (key: string, values: Partial<LineDraft>) => void;
  removeLine: (key: string) => void;
  addLine: () => void;
  errors?: Record<string, string | undefined> | undefined;
  inspectionRequired?: boolean;
  hideTestQuantity?: boolean;
  hideBatch?: boolean;
  readOnlyRate?: boolean;
  allowAddLine?: boolean;
  itemSelectionDisabled?: boolean;
  units?: StoreLookup[] | undefined;
};
const lineFieldSet =
  (
    line: LineDraft,
    updateLine: (key: string, values: Partial<LineDraft>) => void,
    key: keyof LineDraft,
  ) =>
  (event: React.ChangeEvent<HTMLInputElement>) =>
    updateLine(line.key, { [key]: event.target.value });
const lookupOptions = (items: StoreLookup[] | undefined) =>
  (items ?? []).map((option) => ({ value: option.id, label: option.label }));
const activeLineItemValue = (
  items: StoreLookup[] | undefined,
  itemId: string,
) => (items?.some((item) => item.id === itemId) ? itemId : "");
const lineItemOptions = (items: StoreLookup[] | undefined, line: LineDraft) => {
  const options = lookupOptions(items);
  if (line.item && !options.some((option) => option.value === line.item))
    return [
      ...options,
      { value: line.item, label: line.itemName || "Item name unavailable" },
    ];
  return options;
};
const lineUnitOptions = (units: StoreLookup[] | undefined, line: LineDraft) => {
  const options = lookupOptions(units);
  if (line.unit && !options.some((option) => option.value === line.unit)) {
    return [
      ...options,
      { value: line.unit, label: line.unitName || "Saved unit" },
    ];
  }
  return options;
};
const RowActions = ({
  removable,
  isLast,
  onRemove,
  onAdd,
  allowAdd = true,
}: {
  removable: boolean;
  isLast: boolean;
  onRemove: () => void;
  onAdd: () => void;
  allowAdd?: boolean;
}) => (
  <td className="store-charge-narrow">
    <div className="store-row-actions">
      {removable ? (
        <button
          className="store-remove"
          type="button"
          onClick={onRemove}
          aria-label="Remove item"
        >
          ×
        </button>
      ) : null}
      {isLast && allowAdd ? (
        <button className="store-add" type="button" onClick={onAdd}>
          +
        </button>
      ) : null}
    </div>
  </td>
);

const RequisitionLineTable = ({
  lines,
  items,
  updateLine,
  removeLine,
  addLine,
  errors = {},
  readOnlyRate = false,
  allowAddLine = true,
}: LineTableProps) => (
  <div className="table-scroll">
    <table className="store-charge-table">
      <thead>
        <tr>
          <th>
            Item<span className="store-required">*</span>
          </th>
          <th>
            Unit Qty<span className="store-required">*</span>
          </th>
          <th>Unit</th>
          <th>Sub Unit Qty</th>
          <th>Sub Unit</th>
          <th>Rate (₹)</th>
          <th>Amount (₹)</th>
          <th>Remarks</th>
          <th className="store-charge-narrow" />
        </tr>
      </thead>
      <tbody>
        {lines.map((line, index) => {
          const item = itemFor(items, line.item);
          const set = (key: keyof LineDraft) =>
            lineFieldSet(line, updateLine, key);
          return (
            <tr key={line.key}>
              <td>
                <SearchableSelect
                  value={activeLineItemValue(items, line.item)}
                  options={lookupOptions(items)}
                  placeholder="Select Item"
                  clearable={false}
                  ariaInvalid={Boolean(errors[`item-${line.key}`])}
                  onChange={(itemId) => {
                    const selected = itemFor(items, itemId);
                    updateLine(line.key, {
                      item: itemId,
                      itemName: selected?.label ?? "",
                      rate: String(selected?.unitRate ?? 0),
                    });
                  }}
                />
                <FieldError message={errors[`item-${line.key}`]} />
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  required
                  value={line.unitQuantity}
                  onChange={set("unitQuantity")}
                />
                <FieldError message={errors[`quantity-${line.key}`]} />
              </td>
              <td>
                <input disabled value={item?.unitName ?? ""} />
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  max={
                    item?.subUnitsPerUnit
                      ? String(item.subUnitsPerUnit - 1)
                      : undefined
                  }
                  value={line.subUnitQuantity}
                  onChange={set("subUnitQuantity")}
                />
              </td>
              <td>
                <input disabled value={item?.subUnitName ?? ""} />
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={line.rate}
                  disabled={readOnlyRate}
                  onChange={set("rate")}
                />
              </td>
              <td>
                <input
                  disabled
                  value={requisitionLineAmount(line, item).toFixed(2)}
                />
              </td>
              <td>
                <input value={line.remarks} onChange={set("remarks")} />
              </td>
              <RowActions
                removable={lines.length > 1}
                isLast={index === lines.length - 1}
                onRemove={() => removeLine(line.key)}
                onAdd={addLine}
                allowAdd={allowAddLine}
              />
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const WorkOrderLineTable = ({
  lines,
  items,
  units,
  updateLine,
  removeLine,
  addLine,
  errors = {},
}: LineTableProps) => (
  <div className="table-scroll store-work-order-line-wrap">
    <table className="store-charge-table store-work-order-line-table">
      <thead>
        <tr>
          <th>
            Type<span className="store-required">*</span>
          </th>
          <th>
            Description<span className="store-required">*</span>
          </th>
          <th>
            Unit<span className="store-required">*</span>
          </th>
          <th className="store-charge-narrow">#</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line, index) => {
          const selectedType = itemFor(items, line.item);
          const typeUnitId = idOf(selectedType?.unit);
          const typeUnitName =
            selectedType?.unitName || lookupLabel(selectedType?.unit);
          const savedUnitId = idOf(line.unit);
          const savedUnitName =
            line.unitName || lookupLabel(line.unit) || typeUnitName;
          const selectedUnit = units?.find(
            (unit) =>
              unit.id === savedUnitId ||
              unit.label.trim().toLowerCase() ===
                savedUnitName.trim().toLowerCase(),
          );
          const unitValue =
            selectedUnit?.id || savedUnitId || typeUnitId || savedUnitName;
          const unitOptions = lineUnitOptions(units, {
            ...line,
            unit: unitValue,
            unitName: savedUnitName,
          });
          return (
          <tr key={line.key}>
            <td>
              <SearchableSelect
                value={line.item}
                options={lineItemOptions(items, line)}
                placeholder="Select Type"
                clearable={false}
                ariaInvalid={Boolean(errors[`item-${line.key}`])}
                onChange={(itemId) => {
                  const selected = itemFor(items, itemId);
                  updateLine(line.key, {
                    item: itemId,
                    itemName: selected?.label ?? "",
                    unitQuantity:
                      number(line.unitQuantity) > 0 ? line.unitQuantity : "1",
                    rate: String(selected?.unitRate ?? 0),
                  });
                }}
              />
              <FieldError message={errors[`item-${line.key}`]} />
            </td>
            <td>
              <input
                required
                value={line.remarks}
                onChange={(event) =>
                  updateLine(line.key, { remarks: event.target.value })
                }
                aria-invalid={Boolean(errors[`description-${line.key}`])}
              />
              <FieldError message={errors[`description-${line.key}`]} />
            </td>
            <td>
              <SearchableSelect
                value={unitValue}
                options={unitOptions}
                placeholder="Select Unit"
                clearable={false}
                ariaInvalid={Boolean(errors[`unit-${line.key}`])}
                onChange={(unit) => updateLine(line.key, {
                  unit,
                  unitName: units?.find((entry) => entry.id === unit)?.label ?? "",
                })}
              />
              <FieldError message={errors[`unit-${line.key}`]} />
            </td>
            <RowActions
              removable={lines.length > 1}
              isLast={index === lines.length - 1}
              onRemove={() => removeLine(line.key)}
              onAdd={addLine}
            />
          </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const PurchaseOrderLineTable = ({
  lines,
  items,
  updateLine,
  removeLine,
  addLine,
  errors = {},
  itemSelectionDisabled = false,
  allowAddLine = true,
}: LineTableProps) => (
  <div className="table-scroll">
    <table className="store-charge-table">
      <thead>
        <tr>
          <th>
            Item<span className="store-required">*</span>
          </th>
          <th>
            Unit Qty<span className="store-required">*</span>
          </th>
          <th>Unit</th>
          <th>Sub Unit Qty</th>
          <th>Sub Unit</th>
          <th>
            Rate/Qty (₹)<span className="store-required">*</span>
          </th>
          <th>Discount (₹)</th>
          <th>Tax (%)</th>
          <th>Amount (₹)</th>
          <th className="store-charge-narrow" />
        </tr>
      </thead>
      <tbody>
        {lines.map((line, index) => {
          const item = itemFor(items, line.item);
          const amount = orderAmounts(line, item);
          const set = (key: keyof LineDraft) =>
            lineFieldSet(line, updateLine, key);
          return (
            <tr key={line.key}>
              <td>
                <SearchableSelect
                  value={activeLineItemValue(items, line.item)}
                  options={lookupOptions(items)}
                  placeholder="Select Item"
                  clearable={false}
                  disabled={itemSelectionDisabled}
                  ariaInvalid={Boolean(errors[`item-${line.key}`])}
                  onChange={(itemId) => {
                    const selected = itemFor(items, itemId);
                    updateLine(line.key, {
                      item: itemId,
                      rate: String(selected?.unitRate ?? 0),
                      taxPercent: String(selected?.taxPercent ?? 0),
                    });
                  }}
                />
                <FieldError message={errors[`item-${line.key}`]} />
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  required
                  value={line.unitQuantity}
                  onChange={set("unitQuantity")}
                />
              </td>
              <td>
                <input disabled value={item?.unitName ?? ""} />
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  max={
                    item?.subUnitsPerUnit
                      ? String(item.subUnitsPerUnit - 1)
                      : undefined
                  }
                  value={line.subUnitQuantity}
                  onChange={set("subUnitQuantity")}
                />
              </td>
              <td>
                <input disabled value={item?.subUnitName ?? ""} />
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  required
                  value={line.rate}
                  onChange={set("rate")}
                />
              </td>
              <td>
                <input
                  aria-label="Discount Amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={line.discountAmount}
                  onChange={(event) =>
                    updateLine(line.key, {
                      discountType: "amount",
                      discountAmount: event.target.value,
                      discountPercent: "0",
                    })
                  }
                />
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={line.taxPercent}
                  onChange={set("taxPercent")}
                />
              </td>
              <td>
                <input disabled value={amount.total.toFixed(2)} />
              </td>
              <RowActions
                removable={lines.length > 1}
                isLast={index === lines.length - 1}
                onRemove={() => removeLine(line.key)}
                onAdd={addLine}
                allowAdd={allowAddLine}
              />
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const PurchaseLineTable = ({
  lines,
  items,
  updateLine,
  removeLine,
  addLine,
  errors = {},
  inspectionRequired = false,
  hideTestQuantity = false,
  allowAddLine = true,
}: LineTableProps) => (
  <div className="table-scroll">
    <table className="store-charge-table">
      <thead>
        <tr>
          <th>
            Item<span className="store-required">*</span>
          </th>
          <th>Exp. Date</th>
          <th>
            Unit Qty<span className="store-required">*</span>
          </th>
          <th>Unit</th>
          <th>Sub Unit Qty</th>
          <th>Sub Unit</th>
          {hideTestQuantity ? null : <th>Test QTY</th>}
          <th>
            MRP/QTY<span className="store-required">*</span>
          </th>
          <th>
            Rate/QTY<span className="store-required">*</span>
          </th>
          <th>Net Amt.</th>
          <th>Dis (%)</th>
          <th>Dis (₹)</th>
          <th>CGST (%)</th>
          <th>SGST (%)</th>
          <th>IGST (%)</th>
          <th>
            Amount<span className="store-required">*</span>
          </th>
          <th className="store-charge-narrow" />
        </tr>
      </thead>
      <tbody>
        {lines.map((line, index) => {
          const item = itemFor(items, line.item);
          const amount = purchaseAmounts(line, item);
          const gross =
            (quantityFor(line, item) / Number(item?.subUnitsPerUnit ?? 1)) *
            number(line.rate);
          const set = (key: keyof LineDraft) =>
            lineFieldSet(line, updateLine, key);
          return (
            <tr key={line.key}>
              <td>
                <SearchableSelect
                  value={line.item}
                  options={lookupOptions(items)}
                  placeholder="Select Item"
                  clearable={false}
                  ariaInvalid={Boolean(errors[`item-${line.key}`])}
                  onChange={(itemId) => {
                    const selected = itemFor(items, itemId);
                    updateLine(line.key, {
                      item: itemId,
                      batch: "",
                      rate: String(selected?.unitRate ?? 0),
                      mrp: String(selected?.unitMrp ?? 0),
                    });
                  }}
                />
                <FieldError message={errors[`item-${line.key}`]} />
              </td>
              <td>
                <input
                  type="date"
                  value={line.expiryDate}
                  aria-invalid={Boolean(errors[`expiryDate-${line.key}`])}
                  onChange={set("expiryDate")}
                />
                <FieldError message={errors[`expiryDate-${line.key}`]} />
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  required
                  value={line.unitQuantity}
                  aria-invalid={Boolean(errors[`quantity-${line.key}`])}
                  onChange={set("unitQuantity")}
                />
                <FieldError message={errors[`quantity-${line.key}`]} />
              </td>
              <td>
                <input disabled value={item?.unitName ?? ""} />
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  max={
                    item?.subUnitsPerUnit
                      ? String(item.subUnitsPerUnit - 1)
                      : undefined
                  }
                  value={line.subUnitQuantity}
                  onChange={set("subUnitQuantity")}
                />
              </td>
              <td>
                <input disabled value={item?.subUnitName ?? ""} />
              </td>
              {hideTestQuantity ? null : (
                <td>
                  <input
                    type="number"
                    min="0"
                    value={inspectionRequired ? line.testQuantity : "0"}
                    disabled={!inspectionRequired}
                    onChange={set("testQuantity")}
                  />
                </td>
              )}
              <td>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  required
                  value={line.mrp}
                  onChange={set("mrp")}
                />
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  required
                  value={line.rate}
                  onChange={set("rate")}
                />
              </td>
              <td>
                <input disabled value={gross.toFixed(2)} />
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={line.discountPercent}
                  onChange={(event) =>
                    updateLine(line.key, {
                      discountPercent: event.target.value,
                      discountAmount: "0",
                    })
                  }
                />
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={line.discountAmount}
                  onChange={(event) =>
                    updateLine(line.key, {
                      discountAmount: event.target.value,
                      discountPercent: "0",
                    })
                  }
                />
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={line.cgstPercent}
                  onChange={set("cgstPercent")}
                />
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={line.sgstPercent}
                  onChange={set("sgstPercent")}
                />
              </td>
              <td>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={line.igstPercent}
                  onChange={set("igstPercent")}
                />
              </td>
              <td>
                <input disabled value={amount.total.toFixed(2)} />
              </td>
              <RowActions
                removable={lines.length > 1}
                isLast={index === lines.length - 1}
                onRemove={() => removeLine(line.key)}
                onAdd={addLine}
                allowAdd={allowAddLine}
              />
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

const IssueLineTable = ({
  lines,
  items,
  updateLine,
  removeLine,
  addLine,
  errors = {},
  allowAddLine = true,
  hideBatch = false,
}: LineTableProps) => (
  <div className="table-scroll store-issue-line-table">
    <table className="store-charge-table">
      <thead>
        <tr>
          <th>
            Item<span className="store-required">*</span>
          </th>
          {hideBatch ? null : <th>Batch No</th>}
          <th>Exp. Date</th>
          <th>Avl. Qty</th>
          <th>
            Unit Qty<span className="store-required">*</span>
          </th>
          <th>Unit</th>
          <th>Sub Unit Qty</th>
          <th>Sub Unit</th>
          <th>Rate (₹)</th>
          <th>Amount (₹)</th>
          <th className="store-charge-narrow" />
        </tr>
      </thead>
      <tbody>
        {lines.map((line, index) => (
          <IssueLineRow
            key={line.key}
            line={line}
            items={items}
            updateLine={updateLine}
            removable={lines.length > 1}
            isLast={index === lines.length - 1}
            onRemove={() => removeLine(line.key)}
            onAdd={addLine}
            allowAddLine={allowAddLine}
            hideBatch={hideBatch}
            errors={errors}
          />
        ))}
      </tbody>
    </table>
  </div>
);

const IssueLineRow = ({
  line,
  items,
  updateLine,
  removable,
  isLast,
  onRemove,
  onAdd,
  allowAddLine = true,
  hideBatch = false,
  errors = {},
}: {
  line: LineDraft;
  items: StoreLookup[] | undefined;
  updateLine: (key: string, values: Partial<LineDraft>) => void;
  removable: boolean;
  isLast: boolean;
  onRemove: () => void;
  onAdd: () => void;
  allowAddLine?: boolean;
  hideBatch?: boolean;
  errors?: Record<string, string | undefined> | undefined;
}) => {
  const item = itemFor(items, line.item);
  const batches = useStoreLookup(
    "batches",
    { item: line.item },
    Boolean(line.item),
  );
  const batch = batches.data?.find((entry) => entry.id === line.batch);
  const set = (key: keyof LineDraft) => lineFieldSet(line, updateLine, key);
  useEffect(() => {
    if (!hideBatch || !line.item || line.batch || !batches.data?.length) return;
    const selected = batches.data[0];
    if (!selected) return;
    updateLine(line.key, {
      batch: selected.id,
      rate: String(selected.rate ?? item?.unitRate ?? 0),
    });
  }, [
    batches.data,
    hideBatch,
    item?.unitRate,
    line.batch,
    line.item,
    line.key,
    updateLine,
  ]);
  return (
    <tr>
      <td>
        <SearchableSelect
          value={line.item}
          options={lookupOptions(items)}
          placeholder="Select Item"
          clearable={false}
          ariaInvalid={Boolean(errors[`item-${line.key}`])}
          onChange={(itemId) =>
            updateLine(line.key, { item: itemId, batch: "", rate: "0" })
          }
        />
        <FieldError message={errors[`item-${line.key}`]} />
      </td>
      {hideBatch ? null : (
        <td>
          <SearchableSelect
            value={line.batch}
            options={lookupOptions(batches.data)}
            placeholder="Select Batch"
            clearable
            ariaInvalid={Boolean(errors[`batch-${line.key}`])}
            onChange={(batchId) => {
              const selected = batches.data?.find(
                (entry) => entry.id === batchId,
              );
              updateLine(line.key, {
                batch: batchId,
                rate: String(selected?.rate ?? item?.unitRate ?? 0),
              });
            }}
          />
          <FieldError message={errors[`batch-${line.key}`]} />
        </td>
      )}
      <td>
        <input
          disabled
          value={batch?.expiryDate ? formatDate(batch.expiryDate) : ""}
        />
      </td>
      <td>
        <input disabled value={String(batch?.availableQuantity ?? "")} />
      </td>
      <td>
        <input
          type="number"
          min="0"
          required
          value={line.unitQuantity}
          onChange={set("unitQuantity")}
        />
      </td>
      <td>
        <input disabled value={item?.unitName ?? ""} />
      </td>
      <td>
        <input
          type="number"
          min="0"
          max={
            item?.subUnitsPerUnit ? String(item.subUnitsPerUnit - 1) : undefined
          }
          value={line.subUnitQuantity}
          onChange={set("subUnitQuantity")}
        />
      </td>
      <td>
        <input disabled value={item?.subUnitName ?? ""} />
      </td>
      <td>
        <input disabled value={line.rate} />
      </td>
      <td>
        <input disabled value={issueLineAmount(line, item).toFixed(2)} />
      </td>
      <RowActions
        removable={removable}
        isLast={isLast}
        onRemove={onRemove}
        onAdd={onAdd}
        allowAdd={allowAddLine}
      />
    </tr>
  );
};

const PurchaseTotals = ({
  form,
  totals,
  update,
  setForm,
}: {
  form: Form;
  totals: {
    subTotal: number;
    lineDiscount: number;
    cgst: number;
    sgst: number;
    igst: number;
    beforeOverallDiscount: number;
    overallDiscount: number;
    total: number;
  };
  update: Update;
  setForm: React.Dispatch<React.SetStateAction<Form>>;
}) => (
  <div className="store-totals-list">
    <div>
      <span>Sub Total</span>
      <strong>{formatMoney(totals.subTotal)}</strong>
    </div>
    <div>
      <span>CGST Amount</span>
      <strong>{formatMoney(totals.cgst)}</strong>
    </div>
    <div>
      <span>SGST Amount</span>
      <strong>{formatMoney(totals.sgst)}</strong>
    </div>
    <div>
      <span>IGST Amount</span>
      <strong>{formatMoney(totals.igst)}</strong>
    </div>
    <div className="store-discount-row">
      <span>Discount</span>
      <span className="store-discount-input">
        <SearchableSelect
          clearable={false}
          value={form.discountType}
          options={[
            { value: "amount", label: "Rs." },
            { value: "percentage", label: "%" },
          ]}
          onChange={(discountType) =>
            setForm((current) => ({ ...current, discountType }))
          }
        />
        <input
          type="number"
          min="0"
          max={form.discountType === "percentage" ? "100" : undefined}
          step="0.01"
          value={form.totalDiscountAmount}
          onChange={update("totalDiscountAmount")}
        />
      </span>
    </div>
    <div>
      <span>Total</span>
      <strong>{formatMoney(totals.total)}</strong>
    </div>
  </div>
);

type FieldSize = "xs" | "sm" | "lg" | "half" | "full";
const sizeClass = (size?: FieldSize) => (size ? ` field-${size}` : "");
const errorClass = (error?: string) => (error ? " has-error" : "");
const Field = ({
  label,
  value,
  onChange,
  type = "text",
  required = true,
  disabled = false,
  textarea = false,
  min,
  max,
  step,
  size,
  error,
}: {
  label: string;
  value: string;
  onChange?: (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => void;
  type?: string;
  required?: boolean;
  disabled?: boolean;
  textarea?: boolean;
  min?: string;
  max?: string | undefined;
  step?: string;
  size?: FieldSize;
  error?: string | undefined;
}) => {
  const phoneField = isPhoneFieldName(label);
  return (
    <label className={`field${sizeClass(size)}${errorClass(error)}`}>
      {textarea ? null : label}
      {textarea ? (
        <CkEditorField
          label={label}
          value={value}
          disabled={disabled}
          onChange={(next) =>
            onChange?.({
              target: { value: next },
            } as React.ChangeEvent<HTMLTextAreaElement>)
          }
        />
      ) : (
        <input
          required={required}
          disabled={disabled}
          type={phoneField ? "tel" : type}
          min={min}
          max={max}
          step={step}
          value={value}
          maxLength={phoneField ? PHONE_INPUT_MAX_LENGTH : undefined}
          inputMode={phoneField ? "numeric" : undefined}
          pattern={phoneField ? PHONE_INPUT_PATTERN : undefined}
          title={phoneField ? PHONE_INPUT_TITLE : undefined}
          onChange={onChange}
          aria-invalid={Boolean(error)}
        />
      )}
      <FieldError message={error} />
    </label>
  );
};
const Select = ({
  label,
  value,
  options,
  onChange,
  required = true,
  disabled = false,
  size,
  error,
}: {
  label: string;
  value: string;
  options: (readonly [string, string])[];
  onChange: (value: string) => void;
  required?: boolean;
  disabled?: boolean;
  size?: FieldSize;
  error?: string | undefined;
}) => (
  <label className={`field${sizeClass(size)}${errorClass(error)}`}>
    {label}
    <SearchableSelect
      value={value}
      options={options.map(([optionValue, optionLabel]) => ({
        value: optionValue,
        label: optionLabel,
      }))}
      placeholder="Select"
      clearable={!required}
      disabled={disabled}
      ariaInvalid={Boolean(error)}
      onChange={onChange}
    />
    <FieldError message={error} />
  </label>
);
const Lookup = ({
  label,
  value,
  options,
  onChange,
  required = true,
  showRequiredMarker = false,
  matchDropdownWidth = false,
  clearable = !required,
  size,
  error,
}: {
  label: string;
  value: string;
  options: StoreLookup[] | undefined;
  onChange: (value: string) => void;
  required?: boolean;
  showRequiredMarker?: boolean;
  matchDropdownWidth?: boolean;
  clearable?: boolean;
  size?: FieldSize;
  error?: string | undefined;
}) => (
  <label className={`field${sizeClass(size)}${errorClass(error)}`}>
    <span className="store-field-label">
      {label}
      {showRequiredMarker ? <span className="store-required">*</span> : null}
    </span>
    <SearchableSelect
      value={value}
      options={(options ?? []).map((option) => ({
        value: option.id,
        label: option.label,
      }))}
      placeholder={`Select ${label}`}
      clearable={clearable}
      matchControlWidth={matchDropdownWidth}
      ariaInvalid={Boolean(error)}
      onChange={onChange}
    />
    <FieldError message={error} />
  </label>
);

interface StoreListControls {
  search: string;
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  onSearchChange: (value: string) => void;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
}

const RecordList = ({
  title,
  resource,
  params,
  rows,
  columns,
  columnLabels,
  sortableKeys,
  tableSort,
  listControls,
  onAdd,
  addActions,
  addLabel,
  actionLabel = "Actions",
  actionClassName,
  rowAction,
  renderCell,
}: {
  title: string;
  resource: string;
  params: Record<string, unknown>;
  rows: ReturnType<typeof useStoreRows>;
  columns: string[];
  columnLabels?: Record<string, string> | undefined;
  sortableKeys: readonly string[];
  tableSort: TableSortControl;
  listControls?: StoreListControls | undefined;
  onAdd?: (() => void) | undefined;
  addActions?: { label: string; onClick: () => void }[] | undefined;
  addLabel?: string | undefined;
  actionLabel?: string | undefined;
  actionClassName?: string | undefined;
  rowAction?: ((row: Record<string, unknown>) => ReactNode) | undefined;
  renderCell?:
    | ((
        row: Record<string, unknown>,
        key: string,
        index: number,
      ) => ReactNode | undefined)
    | undefined;
}) => {
  const inventoryModule = useInventoryModule();
  const firstVisibleRecord = listControls
    ? (listControls.page - 1) * listControls.limit + 1
    : 0;
  const lastVisibleRecord = listControls
    ? Math.min(
        firstVisibleRecord + (rows.data?.data.length ?? 0) - 1,
        listControls.total,
      )
    : 0;
  const exportToExcel = async () => {
    const exportRows = await fetchAllPages<Record<string, unknown>>(
      (page, limit) =>
        fetchStoreRows(resource, { ...params, page, limit }, inventoryModule),
      500,
    );
    const excluded = [
      "_id",
      "createdAt",
      "updatedAt",
      "lines",
      "generatedBy",
      "createdBy",
      "updatedBy",
      "approvedBy",
      "normalizedName",
    ];
    if (resource === "requisitions")
      excluded.push(
        "note",
        "approvedAt",
        "rejectionNote",
        "rejectedBy",
        "rejectedAt",
      );
    const exportColumns = Object.keys(exportRows[0] ?? {}).filter(
      (key) => !excluded.includes(key),
    );
    if (resource === "requisitions") {
      const statusIndex = exportColumns.indexOf("status");
      exportColumns.splice(
        statusIndex === -1 ? exportColumns.length : statusIndex,
        0,
        "totalItems",
      );
    }
    await exportRowsToExcel({
      fileName: `${title.replace(/\s+/g, "-")}-${today()}`,
      title: `${title} List`,
      columns: exportColumns.map((key) => ({
        header: humanize(key),
        width: 16,
        map: (row: Record<string, unknown>) => text(row, key),
      })),
      rows: exportRows,
    });
  };
  return (
    <section className="content-card">
      <div className="section-heading">
        <h2>{title} list</h2>
        <div className="table-actions">
          <ExcelExportButton
            onExport={exportToExcel}
            disabled={!rows.data || rows.data.data.length === 0}
          />
          {onAdd ? (
            <button
              className="button button-primary"
              type="button"
              onClick={onAdd}
            >
              {addLabel ?? "Add"}
            </button>
          ) : null}
          {addActions?.map((action) => (
            <button
              key={action.label}
              className="button button-primary"
              type="button"
              onClick={action.onClick}
            >
              {action.label}
            </button>
          ))}
        </div>
      </div>
      {listControls ? (
        <div className="store-list-toolbar">
          <label className="store-list-search">
            <input
              type="search"
              value={listControls.search}
              maxLength={160}
              placeholder={`Search ${title}`}
              aria-label={`Search ${title}`}
              onChange={(event) =>
                listControls.onSearchChange(event.target.value)
              }
            />
          </label>
          <span className="result-count">
            {listControls.total.toLocaleString("en-IN")} records
          </span>
        </div>
      ) : null}
      {rows.isLoading ? <LoadingState /> : null}
      {rows.isError ? (
        <ErrorState
          title="Unable to load records"
          message="Please try again."
          action={{ label: "Try again", onClick: () => void rows.refetch() }}
        />
      ) : null}
      {rows.data && rows.data.data.length === 0 ? (
        <EmptyState
          title="No records found"
          message={
            listControls?.search
              ? "Try a different search term."
              : onAdd || addActions?.length
              ? "Use the Add button to create the first record."
              : "No records are currently available."
          }
        />
      ) : null}
      {rows.data && rows.data.data.length > 0 ? (
        <div className="table-scroll">
          <table className={resource === "vendors" ? "store-vendors-table" : undefined}>
            <thead>
              <tr>
                {columns.map((key) =>
                  sortableKeys.includes(key) ? (
                    <SortableTh key={key} control={tableSort} column={key}>
                      {columnLabels?.[key] ?? humanize(key)}
                    </SortableTh>
                  ) : (
                    <th key={key}>{columnLabels?.[key] ?? humanize(key)}</th>
                  ),
                )}
                {rowAction ? (
                  <th className={actionClassName}>{actionLabel}</th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {rows.data.data.map((row, index) => (
                <tr key={String(row.id)}>
                  {columns.map((key) => (
                    <td key={key}>
                      {renderCell?.(row, key, index) ?? cellText(row, key)}
                    </td>
                  ))}
                  {rowAction ? (
                    <td className={actionClassName}>
                      <div className="table-actions">{rowAction(row)}</div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {listControls && rows.data ? (
        <div className="pagination store-list-pagination">
          <label>
            Rows
            <select
              value={listControls.limit}
              disabled={rows.isLoading}
              aria-label="Rows per page"
              onChange={(event) =>
                listControls.onLimitChange(Number(event.target.value))
              }
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
            </select>
          </label>
          <span className="pagination-summary">
            {listControls.total > 0
              ? `Showing ${firstVisibleRecord.toLocaleString("en-IN")} to ${lastVisibleRecord.toLocaleString("en-IN")} of ${listControls.total.toLocaleString("en-IN")} entries`
              : "Showing 0 entries"}
          </span>
          <button
            className="button button-secondary"
            type="button"
            disabled={rows.isLoading || listControls.page <= 1}
            onClick={() => listControls.onPageChange(listControls.page - 1)}
          >
            Previous
          </button>
          <span>
            Page {listControls.page} of {listControls.totalPages}
          </span>
          <button
            className="button button-secondary"
            type="button"
            disabled={
              rows.isLoading || listControls.page >= listControls.totalPages
            }
            onClick={() => listControls.onPageChange(listControls.page + 1)}
          >
            Next
          </button>
        </div>
      ) : null}
    </section>
  );
};

type StoreStatAccent = "teal" | "cyan" | "indigo" | "amber" | "violet" | "rose";
interface StoreStatDatum {
  key: string;
  label: string;
  value: number;
  displayValue?: string;
  breakdown?: { label: string; value: number }[];
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  accent: StoreStatAccent;
}
const dashboardMoneyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  notation: "compact",
  maximumFractionDigits: 1,
});
const StoreStatCard = ({ stat }: { stat: StoreStatDatum }) => (
  <article
    className={`stat-card accent-${stat.accent}${stat.breakdown ? " stat-card-summary" : ""}`}
  >
    <span className="stat-card-top" aria-hidden="true" />
    {stat.breakdown ? (
      <>
        <span className="stat-card-icon" aria-hidden="true">
          <stat.icon />
        </span>
        <div className="stat-card-text">
          <small>{stat.label}</small>
        </div>
        <div className="stat-card-breakdown">
          {stat.breakdown.map((entry) => (
            <span key={entry.label}>
              <strong>{entry.value.toLocaleString("en-IN")}</strong>
              <small>{entry.label}</small>
            </span>
          ))}
        </div>
      </>
    ) : (
      <>
        <span className="stat-card-icon" aria-hidden="true">
          <stat.icon />
        </span>
        <div className="stat-card-text">
          <strong>
            {stat.displayValue ?? stat.value.toLocaleString("en-IN")}
          </strong>
          <small>{stat.label}</small>
        </div>
      </>
    )}
  </article>
);

const StoreChartTooltip = ({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: { name: string; value: number; color: string }[];
  label?: string;
}) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="chart-tooltip">
      <strong>{label}</strong>
      {payload.map((entry) => (
        <div key={entry.name} className="chart-tooltip-row">
          <span
            className="chart-tooltip-dot"
            style={{ background: entry.color }}
          />
          <span>{entry.name}</span>
          <b>{entry.value.toLocaleString("en-IN")}</b>
        </div>
      ))}
    </div>
  );
};

const STORE_HEALTH_COLORS = ["#0d6b66", "#c23b42"];

const StoreDashboard = ({
  data,
  loading,
  error,
}: {
  data: Record<string, unknown> | undefined;
  loading: boolean;
  error: string | null;
}) => {
  const inventoryModule = useInventoryModule();
  const isPharmacy = inventoryModule === "pharmacy";
  const moduleBasePath = `/${inventoryModule}`;
  const [showAllLowStock, setShowAllLowStock] = useState(false);
  const lowStock = Array.isArray(data?.lowStock)
    ? (data.lowStock as Record<string, unknown>[])
    : [];
  const recentRequisitions = Array.isArray(data?.recentRequisitions)
    ? (data.recentRequisitions as Record<string, unknown>[])
    : [];
  const lowStockAccessors = useMemo<SortAccessors<Record<string, unknown>>>(
    () => ({
      itemCode: (row) => text(row, "itemCode"),
      name: (row) => text(row, "name"),
      available: (row) => Number(row.availableQuantity ?? 0),
      reorderLevel: (row) => Number(row.reorderLevel ?? 0),
      status: (row) => Number(row.availableQuantity ?? 0),
    }),
    [],
  );
  const lowStockSort = useTableSort(lowStock, lowStockAccessors);
  if (loading) return <LoadingState />;
  if (error)
    return (
      <ErrorState
        title={`Unable to load ${isPharmacy ? "Pharmacy" : "Store"} dashboard`}
        message={error}
      />
    );
  const activeItems = Number(data?.activeItems ?? 0);
  const stockValue = Number(data?.stockValue ?? 0);
  const issueValue = Number(data?.issueValue ?? 0);
  const stats: StoreStatDatum[] = [
    {
      key: "requisitions",
      label: "Requisitions",
      value: Number(data?.requisitions ?? 0),
      breakdown: [
        { label: "Total", value: Number(data?.requisitions ?? 0) },
        { label: "Verify", value: Number(data?.verifyRequisitions ?? 0) },
        { label: "Approved", value: Number(data?.approvedRequisitions ?? 0) },
      ],
      icon: IconClock,
      accent: "teal",
    },
    {
      key: "purchaseOrders",
      label: "Purchase Orders",
      value: Number(data?.purchaseOrders ?? 0),
      breakdown: [
        { label: "Total", value: Number(data?.purchaseOrders ?? 0) },
        { label: "Verify", value: Number(data?.pendingPurchaseOrders ?? 0) },
        { label: "Approved", value: Number(data?.approvedPurchaseOrders ?? 0) },
      ],
      icon: IconBillCheck,
      accent: "cyan",
    },
    {
      key: "purchases",
      label: "Purchases",
      value: Number(data?.purchases ?? 0),
      breakdown: [
        { label: "Direct", value: Number(data?.directPurchases ?? 0) },
        {
          label: "Against PO",
          value: Number(data?.againstPurchaseOrderPurchases ?? 0),
        },
      ],
      icon: IconReports,
      accent: "indigo",
    },
    {
      key: "issues",
      label: "Item Issues",
      value: Number(data?.issues ?? 0),
      breakdown: [
        { label: "Total", value: Number(data?.issues ?? 0) },
        { label: "Completed", value: Number(data?.completedIssues ?? 0) },
        { label: "Pending", value: Number(data?.pendingIssues ?? 0) },
      ],
      icon: IconBillCheck,
      accent: "teal",
    },
    {
      key: "stockValue",
      label: "Stock Amount",
      value: stockValue,
      displayValue: dashboardMoneyFormatter.format(stockValue),
      icon: IconChart,
      accent: "amber",
    },
    {
      key: "issueValue",
      label: "Issue Amount",
      value: issueValue,
      displayValue: dashboardMoneyFormatter.format(issueValue),
      icon: IconChart,
      accent: "rose",
    },
  ];
  const healthy = Math.max(0, activeItems - lowStock.length);
  const healthData = [
    { name: "Healthy stock", value: healthy },
    { name: "Needs attention", value: lowStock.length },
  ];

  return (
    <>
      <section
        className="stat-grid"
        aria-label={`${isPharmacy ? "Pharmacy" : "Store"} summary`}
      >
        {stats.map((stat) => (
          <StoreStatCard key={stat.key} stat={stat} />
        ))}
      </section>
      <div className="chart-grid">
        <section className="chart-card">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Recent requests</p>
              <h2>Latest requisitions</h2>
            </div>
            <Link
              to={`${moduleBasePath}/requisitions`}
              className="button button-secondary"
            >
              View all
            </Link>
          </div>
          <div className="chart-body">
            {recentRequisitions.length === 0 ? (
              <EmptyState
                title="No requisitions yet"
                message={`New ${isPharmacy ? "pharmacy" : "store"} requisitions will appear here.`}
              />
            ) : (
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Requisition</th>
                      <th>
                        {isPharmacy ? "Department / Patient" : "Department"}
                      </th>
                      <th>Date</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentRequisitions.map((row) => (
                      <tr key={String(row.id)}>
                        <td>{text(row, "requisitionNumber")}</td>
                        <td>
                          {isPharmacy
                            ? requisitionDestination(row)
                            : text(row, "department")}
                        </td>
                        <td>{formatRequisitionDate(row.requisitionDate)}</td>
                        <td>
                          <RequisitionStatusBadge status={row.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
        <section className="chart-card">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Inventory health</p>
              <h2>Active items status</h2>
            </div>
          </div>
          <div className="chart-body">
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie
                  data={healthData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={55}
                  outerRadius={82}
                  paddingAngle={healthy > 0 && lowStock.length > 0 ? 3 : 0}
                  strokeWidth={0}
                >
                  {healthData.map((entry, index) => (
                    <Cell
                      key={entry.name}
                      fill={STORE_HEALTH_COLORS[index] ?? "#8ba09e"}
                    />
                  ))}
                </Pie>
                <Tooltip content={<StoreChartTooltip />} />
                <Legend
                  wrapperStyle={{ fontSize: 12, color: "#5e7774" }}
                  iconType="circle"
                  iconSize={8}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <p className="chart-footnote">
            {activeItems.toLocaleString("en-IN")} active items tracked ·{" "}
            {lowStock.length.toLocaleString("en-IN")} at or below reorder level.
          </p>
        </section>
      </div>
      <div className="dashboard-grid">
        <section className="content-card">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Needs attention</p>
              <h2>Low-stock items</h2>
            </div>
            {lowStock.length > 5 ? (
              <div className="table-actions">
                <button
                  type="button"
                  className="button button-secondary"
                  aria-expanded={showAllLowStock}
                  onClick={() => setShowAllLowStock((current) => !current)}
                >
                  {showAllLowStock ? "Show less" : "View all"}
                </button>
              </div>
            ) : null}
          </div>
          {lowStock.length === 0 ? (
            <EmptyState
              title="Stock levels look healthy"
              message="No active item is at or below its reorder level."
            />
          ) : (
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <SortableTh control={lowStockSort} column="itemCode">
                      Item code
                    </SortableTh>
                    <SortableTh control={lowStockSort} column="name">
                      Item
                    </SortableTh>
                    <SortableTh control={lowStockSort} column="available">
                      Available
                    </SortableTh>
                    <SortableTh control={lowStockSort} column="reorderLevel">
                      Reorder level
                    </SortableTh>
                    <SortableTh control={lowStockSort} column="status">
                      Status
                    </SortableTh>
                  </tr>
                </thead>
                <tbody>
                  {lowStockSort.rows
                    .slice(0, showAllLowStock ? undefined : 5)
                    .map((row) => {
                      const available = Number(row.availableQuantity ?? 0);
                      return (
                        <tr key={String(row.id)}>
                          <td>{text(row, "itemCode")}</td>
                          <td>{text(row, "name")}</td>
                          <td>{available}</td>
                          <td>{text(row, "reorderLevel")}</td>
                          <td>
                            <span
                              className={`status-badge ${available <= 0 ? "critical" : "warning"}`}
                            >
                              {available <= 0 ? "Out of stock" : "Low stock"}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <aside className="content-card quick-panel">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Shortcuts</p>
              <h2>Quick actions</h2>
            </div>
          </div>
          <Link to={`${moduleBasePath}/requisitions`}>
            <span>
              <IconReports />
            </span>
            <div>
              <strong>New requisition</strong>
              <small>Request items for a department</small>
            </div>
            <b>›</b>
          </Link>
          <Link to={`${moduleBasePath}/purchase-orders`}>
            <span>
              <IconBillCheck />
            </span>
            <div>
              <strong>Purchase order</strong>
              <small>Order stock from a vendor</small>
            </div>
            <b>›</b>
          </Link>
          <Link to={isPharmacy ? "/pharmacy/medicines" : "/store/stock"}>
            <span>
              <IconStore />
            </span>
            <div>
              <strong>{isPharmacy ? "Medicine list" : "Present stock"}</strong>
              <small>
                {isPharmacy
                  ? "Manage pharmacy medicines"
                  : "View live batch-level stock"}
              </small>
            </div>
            <b>›</b>
          </Link>
          <Link
            to={
              isPharmacy
                ? "/pharmacy/vendors"
                : "/store/reports/upcoming-expiry"
            }
          >
            <span>
              <IconAlertTriangle />
            </span>
            <div>
              <strong>{isPharmacy ? "Vendors" : "Expiry report"}</strong>
              <small>
                {isPharmacy
                  ? "Manage pharmacy suppliers"
                  : "Batches nearing expiry"}
              </small>
            </div>
            <b>›</b>
          </Link>
        </aside>
      </div>
    </>
  );
};
